import { Injectable } from '@angular/core';
import { isNil } from 'lodash';

import { EColorPalette } from '@shared/enums/color-palette.enum';
import { EIcon } from '@shared/enums/icon.enum';
import { ISvgCircle, ISvgPath, ISvgRectangle } from '@shared/interfaces/svg-params.interface';

@Injectable({
  providedIn: 'root',
})
export class IconService {
  private static uniqueSvgIconId = -1;
  uniqueId = '';

  getIconTemplate(icon: EIcon, width = 15, height = 15, color: EColorPalette, secondColor: EColorPalette): string {
    this.uniqueId = String(++IconService.uniqueSvgIconId);
    switch (icon) {
      case EIcon.MATERIAL_SORT_ARROW: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 -960 960 960')}
        ${this.getPath({
          d: 'M440-240v-368L296-464l-56-56 240-240 240 240-56 56-144-144v368h-80Z',
          fill: color || EColorPalette.cGray,
        })}
      ${this.getSvgEndTemplate()}
      `;
      }

      case EIcon.ANGLE_DOWN: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 14 8')}
        ${this.getPath({
          d: 'M12.2929 0.292892C12.6834 -0.0976324 13.3166 -0.0976324 13.7071 0.292892C14.0976 0.683416 14.0976 1.31658 13.7071 1.70711L7.70711 7.70711C7.32853 8.08568 6.71894 8.09893 6.32428 7.73715L0.324275 2.23715C-0.0828438 1.86396 -0.110347 1.23139 0.262846 0.824274C0.636039 0.417155 1.26861 0.389652 1.67572 0.762845L6.96992 5.61586L12.2929 0.292892Z',
          fill: color || EColorPalette.cGray,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.ANGLE_UP: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 14 8')}
        ${this.getPath({
          d: 'M1.70711 7.70711C1.31658 8.09763 0.683417 8.09763 0.292893 7.70711C-0.0976317 7.31658 -0.0976317 6.68342 0.292893 6.29289L6.29289 0.292895C6.67147 -0.0856838 7.28106 -0.0989294 7.67572 0.262848L13.6757 5.76285C14.0828 6.13604 14.1103 6.76861 13.7372 7.17573C13.364 7.58285 12.7314 7.61035 12.3243 7.23716L7.03008 2.38414L1.70711 7.70711Z',
          fill: color || EColorPalette.cGray,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.ANGLE_LEFT: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 9 14')}
        ${this.getPath({
          d: 'M8.20711 1.70711C8.59763 1.31658 8.59763 0.683418 8.20711 0.292893C7.81658 -0.0976311 7.18342 -0.0976311 6.79289 0.292893L0.792895 6.29289C0.414316 6.67147 0.40107 7.28106 0.762847 7.67572L6.26285 13.6757C6.63604 14.0828 7.26861 14.1103 7.67573 13.7372C8.08284 13.364 8.11035 12.7314 7.73716 12.3243L2.88414 7.03008L8.20711 1.70711Z',
          fill: color || EColorPalette.cGray,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.ANGLE_RIGHT: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 9 14')}
        ${this.getPath({
          d: 'M0.792892 1.70711C0.402368 1.31658 0.402368 0.683418 0.792892 0.292893C1.18342 -0.0976311 1.81658 -0.0976311 2.20711 0.292893L8.20711 6.29289C8.58568 6.67147 8.59893 7.28106 8.23715 7.67572L2.73715 13.6757C2.36396 14.0828 1.73139 14.1103 1.32427 13.7372C0.917155 13.364 0.889652 12.7314 1.26284 12.3243L6.11586 7.03008L0.792892 1.70711Z',
          fill: color || EColorPalette.cGray,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.BOARD: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M2 2.94737C2 2.42415 2.42415 2 2.94737 2V2H21.0526V2C21.5758 2 22 2.42417 22 2.9474V3C22 3.55228 21.5523 4 21 4V4V17C21 17.5523 20.5523 18 20 18H16L18 22L16 21.9975L14 18H10L8 22H6L8 18H3.99625C3.44543 18 2.99833 17.5546 2.99625 17.0038L2.94737 4V4C2.42415 4 2 3.57585 2 3.05263V2.94737ZM5 4H19V16H5V4Z',
          fill: color || EColorPalette.cGray,
          opacity: 0.3,
        })}
        ${this.getRectangle({ x: 7, y: 6, width: 4, height: 3, fill: color || EColorPalette.cGray })},
        ${this.getRectangle({ x: 7, y: 11, width: 4, height: 3, fill: color || EColorPalette.cGray })},
        ${this.getRectangle({ x: 13, y: 6, width: 4, height: 3, fill: color || EColorPalette.cGray })},
        ${this.getRectangle({ x: 13, y: 11, width: 4, height: 3, fill: color || EColorPalette.cGray })},
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.WARNING: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z',
          fill: color || EColorPalette.cRed,
          opacity: 0.3,
        })}
        ${this.getRectangle({ x: 11, y: 7, width: 2, height: 8, rx: 1, fill: color || EColorPalette.cRed })},
        ${this.getRectangle({ x: 11, y: 16, width: 2, height: 2, rx: 1, fill: color || EColorPalette.cRed })},
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.WARNING2: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 25 25')}
        ${this.getPath({
          d: 'M13.3944 4.96361C12.995 4.30995 12.039 4.32977 11.667 4.99942L3.32536 20.0144C2.95506 20.6809 3.43703 21.5 4.19951 21.5H21.7169C22.4979 21.5 22.9774 20.6449 22.5702 19.9785L13.3944 4.96361Z',
          fill: color || EColorPalette.cRed,
          opacity: 0.3,
        })}
        ${this.getRectangle({ x: 11.5, y: 9.5, width: 2, height: 7, rx: 1, fill: color || EColorPalette.cRed })},
        ${this.getRectangle({ x: 11.5, y: 17.5, width: 2, height: 2, rx: 1, fill: color || EColorPalette.cRed })},
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.HOME_HEART: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 37 36')}
        ${this.getPath({
          d: 'M6.10038 12.6225L17.6952 5.72247C17.9373 5.57852 18.2155 5.50235 18.4992 5.50235C18.7829 5.50235 19.0611 5.57852 19.3032 5.72247L30.8981 12.6225C31.3491 12.8904 31.7219 13.2665 31.9805 13.7149C32.2391 14.1633 32.375 14.6688 32.375 15.183V28.5C32.375 29.2956 32.0501 30.0587 31.4719 30.6213C30.8937 31.1839 30.1094 31.5 29.2917 31.5H7.70833C6.89058 31.5 6.10632 31.1839 5.52809 30.6213C4.94985 30.0587 4.625 29.2956 4.625 28.5V15.183C4.62487 14.669 4.76048 14.1635 5.01883 13.7152C5.27718 13.2668 5.6496 12.8905 6.10038 12.6225Z',
          fill: color || EColorPalette.cRed,
          opacity: 0.3,
        })}
        ${this.getPath({
          d: 'M18.5 28C22.5068 28 25.7549 24.7439 25.7549 20.7273C25.7549 16.7107 22.5068 13.4545 18.5 13.4545C14.4932 13.4545 11.2451 16.7107 11.2451 20.7273C11.2451 24.7439 14.4932 28 18.5 28Z',
          fill: color || EColorPalette.cRed,
          opacity: 0.3,
        })}
        ${this.getPath({
          d: 'M19.2257 17.0891C19.2257 16.6885 18.9008 16.3636 18.5002 16.3636C18.0995 16.3636 17.7747 16.6885 17.7747 17.0891V20H14.8745C14.4728 20 14.1472 20.3256 14.1472 20.7273C14.1472 21.1289 14.4728 21.4546 14.8745 21.4546H17.7747V24.3654C17.7747 24.7661 18.0995 25.0909 18.5002 25.0909C18.9008 25.0909 19.2257 24.7661 19.2257 24.3654V21.4546H22.1258C22.5275 21.4546 22.8531 21.1289 22.8531 20.7273C22.8531 20.3256 22.5275 20 22.1258 20H19.2257V17.0891Z',
          fill: color || EColorPalette.cRed,
          opacity: 1,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.ARROW_DOWN: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 22 22')}
        ${this.getRectangle({
          x: 10.0833,
          y: 4.58334,
          width: 1.83333,
          height: 12.8333,
          rx: 0.916667,
          fill: color || EColorPalette.cRed,
          opacity: 0.3,
        })},
        ${this.getPath({
          d: 'M6.1481 10.3518C5.79012 9.99384 5.20972 9.99384 4.85174 10.3518C4.49376 10.7098 4.49376 11.2902 4.85174 11.6482L10.3517 17.1482C10.6988 17.4952 11.2576 17.5074 11.6193 17.1757L17.1193 12.1341C17.4925 11.792 17.5177 11.2121 17.1756 10.8389C16.8336 10.4657 16.2537 10.4405 15.8805 10.7826L11.0275 15.2312L6.1481 10.3518Z',
          fill: color || EColorPalette.cRed,
          opacity: 1,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.ARROW_UP: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 22 22')}
        ${this.getRectangle({
          x: 10.0833,
          y: 9.16666,
          width: 1.83333,
          height: 9.16667,
          rx: 0.916667,
          fill: color || EColorPalette.cGreen,
          opacity: 0.3,
        })},
        ${this.getPath({
          d: 'M6.1481 11.6482C5.79012 12.0062 5.20972 12.0062 4.85174 11.6482C4.49376 11.2902 4.49376 10.7098 4.85174 10.3518L10.3517 4.85181C10.6988 4.50478 11.2576 4.49264 11.6193 4.82427L17.1193 9.86594C17.4925 10.208 17.5177 10.7879 17.1756 11.1611C16.8336 11.5343 16.2537 11.5595 15.8805 11.2174L11.0275 6.76879L6.1481 11.6482Z',
          fill: color || EColorPalette.cGreen,
          opacity: 1,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.ARROW_LEFT: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getRectangle({
          x: 0,
          y: 0,
          width: 2,
          height: 14,
          rx: 1,
          transform: 'matrix(0 -1 -1 0 19 13)',
          fill: color || EColorPalette.cGray4,
          opacity: 0.3,
        })},
        ${this.getPath({
          d: 'M12.7071 17.2929C13.0976 17.6834 13.0976 18.3166 12.7071 18.7071C12.3166 19.0976 11.6834 19.0976 11.2929 18.7071L5.29289 12.7071C4.91432 12.3285 4.90107 11.7189 5.26285 11.3243L10.7628 5.32428C11.136 4.91716 11.7686 4.88965 12.1757 5.26285C12.5828 5.63604 12.6103 6.26861 12.2372 6.67572L7.38414 11.9699L12.7071 17.2929Z',
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.WAITING: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 37 36')}
        ${this.getPath({
          d: 'M16.1875 3.75C16.1875 2.92157 16.8591 2.25 17.6875 2.25H19.3125C20.1409 2.25 20.8125 2.92157 20.8125 3.75V7.5C20.8125 8.32843 20.1409 9 19.3125 9H17.6875C16.8591 9 16.1875 8.32843 16.1875 7.5V3.75ZM24.2001 9.35195C23.6063 9.92966 23.6063 10.8663 24.2001 11.444L25.3203 12.5339C25.914 13.1116 26.8767 13.1116 27.4705 12.5339L30.2258 9.85303C30.8196 9.27532 30.8196 8.33867 30.2258 7.76096L29.1056 6.67105C28.5119 6.09334 27.5492 6.09334 26.9555 6.67105L24.2001 9.35195ZM29.25 15.75C28.4216 15.75 27.75 16.4216 27.75 17.25V18.75C27.75 19.5784 28.4216 20.25 29.25 20.25H33.1875C34.0159 20.25 34.6875 19.5784 34.6875 18.75V17.25C34.6875 16.4216 34.0159 15.75 33.1875 15.75H29.25ZM24.2001 24.636C23.6063 25.2137 23.6063 26.1503 24.2001 26.728L26.9555 29.4089C27.5492 29.9866 28.5119 29.9866 29.1056 29.4089L30.2258 28.319C30.8196 27.7413 30.8196 26.8047 30.2258 26.2269L27.4705 23.546C26.8767 22.9683 25.914 22.9683 25.3203 23.546L24.2001 24.636ZM3.8125 15.75C2.98407 15.75 2.3125 16.4216 2.3125 17.25V18.75C2.3125 19.5784 2.98407 20.25 3.8125 20.25H7.75C8.57843 20.25 9.25 19.5784 9.25 18.75V17.25C9.25 16.4216 8.57843 15.75 7.75 15.75H3.8125ZM17.6875 27C16.8591 27 16.1875 27.6716 16.1875 28.5V32.25C16.1875 33.0784 16.8591 33.75 17.6875 33.75H19.3125C20.1409 33.75 20.8125 33.0784 20.8125 32.25V28.5C20.8125 27.6716 20.1409 27 19.3125 27H17.6875ZM6.85634 9.85302C6.26258 9.27531 6.26258 8.33866 6.85634 7.76096L7.97653 6.67104C8.57029 6.09333 9.53295 6.09333 10.1267 6.67104L12.8821 9.35195C13.4758 9.92965 13.4758 10.8663 12.8821 11.444L11.7619 12.5339C11.1681 13.1116 10.2055 13.1116 9.61171 12.5339L6.85634 9.85302ZM6.85634 28.319C6.26258 27.7413 6.26258 26.8047 6.85634 26.2269L9.61171 23.546C10.2055 22.9683 11.1681 22.9683 11.7619 23.546L12.8821 24.636C13.4758 25.2137 13.4758 26.1503 12.8821 26.728L10.1267 29.4089C9.53295 29.9866 8.57028 29.9866 7.97653 29.4089L6.85634 28.319Z',
          fill: color || EColorPalette.cRed,
          opacity: 0.3,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.FILE_PLUS: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 37 36')}
        ${this.getPath({
          d: 'M9.02984 3H21.486C21.8338 3 22.1707 3.12082 22.4392 3.34179L30.2866 9.79998C30.6328 10.0849 30.8334 10.5098 30.8334 10.9582V30.125C30.8334 32.8108 30.8019 33 27.9703 33H9.02984C6.19827 33 6.16675 32.8108 6.16675 30.125V5.875C6.16675 3.18919 6.19827 3 9.02984 3Z',
          fill: color || EColorPalette.cGreen,
          opacity: 0.3,
        })}
        ${this.getPath({
          d: 'M16.9584 21L13.8751 21C13.0236 21 12.3334 20.3284 12.3334 19.5C12.3334 18.6716 13.0236 18 13.8751 18L16.9584 18L16.9584 15C16.9584 14.1716 17.6486 13.5 18.5001 13.5C19.3515 13.5 20.0417 14.1716 20.0417 15L20.0417 18L23.1251 18C23.9765 18 24.6667 18.6716 24.6667 19.5C24.6667 20.3284 23.9765 21 23.1251 21L20.0417 21L20.0417 24C20.0417 24.8284 19.3515 25.5 18.5001 25.5C17.6486 25.5 16.9584 24.8284 16.9584 24L16.9584 21Z',
          fill: color || EColorPalette.cGreen,
          opacity: 1,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.HOME: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 37 36')}
        ${this.getPath({
          d: 'M6.10038 12.6225L17.6952 5.72247C17.9373 5.57852 18.2155 5.50235 18.4992 5.50235C18.7829 5.50235 19.0611 5.57852 19.3032 5.72247L30.8981 12.6225C31.3491 12.8904 31.7219 13.2665 31.9805 13.7149C32.2391 14.1633 32.375 14.6688 32.375 15.183V28.5C32.375 29.2956 32.0501 30.0587 31.4719 30.6213C30.8937 31.1839 30.1094 31.5 29.2917 31.5H7.70833C6.89058 31.5 6.10632 31.1839 5.52809 30.6213C4.94985 30.0587 4.625 29.2956 4.625 28.5V15.183C4.62487 14.669 4.76048 14.1635 5.01883 13.7152C5.27718 13.2668 5.6496 12.8905 6.10038 12.6225ZM15.4167 19.5C15.0078 19.5 14.6157 19.658 14.3265 19.9393C14.0374 20.2206 13.875 20.6021 13.875 21V25.5C13.875 25.8978 14.0374 26.2793 14.3265 26.5606C14.6157 26.8419 15.0078 27 15.4167 27H21.5833C21.9922 27 22.3843 26.8419 22.6735 26.5606C22.9626 26.2793 23.125 25.8978 23.125 25.5V21C23.125 20.6021 22.9626 20.2206 22.6735 19.9393C22.3843 19.658 21.9922 19.5 21.5833 19.5H15.4167Z',
          fill: color || EColorPalette.cGreen,
          opacity: 1,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.SAD: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 130 130')}
        ${this.getRectangle({
          x: 10.8335,
          y: 10.8333,
          width: 108.333,
          height: 108.333,
          rx: 54.1667,
          fill: color || EColorPalette.cGreen,
          opacity: 0.3,
        })},
        ${this.getPath({
          d: 'M33.4103 89.0781C31.7509 91.5672 32.4235 94.9303 34.9126 96.5897C37.4017 98.2491 40.7648 97.5765 42.4242 95.0874C48.6915 85.6865 56.0865 81.2494 65.0006 81.2494C73.9146 81.2494 81.3097 85.6865 87.577 95.0874C89.2364 97.5765 92.5994 98.2491 95.0885 96.5897C97.5776 94.9303 98.2503 91.5672 96.5908 89.0781C88.4137 76.8124 77.7532 70.4161 65.0006 70.4161C52.2479 70.4161 41.5875 76.8124 33.4103 89.0781Z',
          fill: color || EColorPalette.cGreen,
          opacity: 1,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.EXPIRATIONS: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 37 36')}
        ${this.getUrlClip()}
        ${this.getPath({
          d: 'M31.2188 15.75H5.78118C5.14307 15.75 4.625 16.1922 4.625 16.7368V28.5395C4.625 30.1717 6.18132 31.5 8.09375 31.5H28.9062C30.8187 31.5 32.375 30.1717 32.375 28.5395V16.7368C32.375 16.1922 31.8569 15.75 31.2188 15.75Z',
          fill: color || EColorPalette.cGreen,
          opacity: 0.3,
        })}
        ${this.getPath({
          d: 'M28.9062 7.875H8.09375C6.17793 7.875 4.625 9.38596 4.625 11.25V14.6699H32.375V11.25C32.375 9.38596 30.8221 7.875 28.9062 7.875Z',
          fill: color || EColorPalette.cGreen,
          opacity: 0.3,
        })}
        ${this.getPath({
          d: 'M11.5626 4.5H10.4064C9.76807 4.5 9.25 5.00407 9.25 5.62493V10.1251C9.25 10.7459 9.76807 11.25 10.4064 11.25H11.5626C12.2009 11.25 12.7188 10.7459 12.7188 10.1251V5.62493C12.7188 5.00407 12.2009 4.5 11.5626 4.5Z',
          fill: color || EColorPalette.cGreen,
          opacity: 1,
        })}
        ${this.getPath({
          d: 'M26.5936 4.5H25.4374C24.7991 4.5 24.2812 5.00407 24.2812 5.62493V10.1251C24.2812 10.7459 24.7991 11.25 25.4374 11.25H26.5936C27.2319 11.25 27.75 10.7459 27.75 10.1251V5.62493C27.75 5.00407 27.2319 4.5 26.5936 4.5Z',
          fill: color || EColorPalette.cGreen,
          opacity: 1,
        })}
        ${this.getEndUrlClip()}
        ${this.getSvgEndTemplate()}
        ${this.getDefsPart()}
        ${this.getClipPath()}
        ${this.getClipPathRect()}
        ${this.endClipPath()}
        ${this.endDefsPart()}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.SIGN_OUT: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 37 36')}
        ${this.getPath({
          d: 'M21.5846 10.5C21.5846 11.3284 22.2745 12 23.1255 12C23.9766 12 24.6665 11.3284 24.6665 10.5V9C24.6665 5.68629 21.9068 3 18.5026 3H9.25791C5.85363 3 3.09391 5.68629 3.09391 9V27C3.09391 30.3137 5.85363 33 9.25791 33H18.5133C21.9175 33 24.6772 30.3137 24.6772 27V25.5C24.6772 24.6716 23.9873 24 23.1362 24C22.2852 24 21.5953 24.6716 21.5953 25.5V27C21.5953 28.6569 20.2154 30 18.5133 30H9.25791C7.55577 30 6.17591 28.6569 6.17591 27V9C6.17591 7.34315 7.55577 6 9.25791 6H18.5026C20.2047 6 21.5846 7.34315 21.5846 9V10.5Z',
          fill: color || EColorPalette.cGreen,
          opacity: 0.3,
        })}
        ${this.getRectangle({
          x: 30.8335,
          y: 16.5,
          width: 3,
          height: 18.5,
          rx: 1,
          fill: color || EColorPalette.cGreen,
          opacity: 0.3,
          transform: 'rotate(90 30.8335 16.5)',
        })},
        ${this.getPath({
          d: 'M26.66 14.5607C26.058 13.9749 26.058 13.0251 26.66 12.4393C27.2621 11.8536 28.2382 11.8536 28.8403 12.4393L33.4653 16.9393C34.0673 17.5251 34.0673 18.4749 33.4653 19.0607L28.8403 23.5607C28.2382 24.1464 27.2621 24.1464 26.66 23.5607C26.058 22.9749 26.058 22.0251 26.66 21.4393L30.1949 18L26.66 14.5607Z',
          fill: color || EColorPalette.cGreen,
          opacity: 1,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.FORWARD: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M13 9.99997V5.6701C13 5.02297 13.7646 4.67961 14.2483 5.10955L21.4055 11.4715C21.728 11.7582 21.7427 12.2573 21.4375 12.5624L14.2803 19.7196C13.8078 20.1921 13 19.8575 13 19.1893V15C11.2455 14.8943 6.13692 14.9653 4.50643 14.9914C4.22674 14.9959 3.99999 14.7705 3.99999 14.4907V10.505C3.99999 10.2269 4.2243 10.0022 4.5024 10.0047C6.29384 10.021 12.2993 10.0698 13 9.99997Z',
          fill: color || EColorPalette.cGray3,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.GREEN_FLAG: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 14 14')}
        ${this.getPath({
          d: 'M2.04175 1.75H2.91675V11.375C2.91675 11.6071 2.82456 11.8296 2.66047 11.9937C2.49637 12.1578 2.27381 12.25 2.04175 12.25C1.80968 12.25 1.58712 12.1578 1.42303 11.9937C1.25894 11.8296 1.16675 11.6071 1.16675 11.375V2.625C1.16675 2.39294 1.25894 2.17038 1.42303 2.00628C1.58712 1.84219 1.80968 1.75 2.04175 1.75Z',
          fill: color || EColorPalette.cGreen2,
        })}
        ${this.getPath({
          d: 'M4.08325 1.75H11.5237C11.6365 1.7498 11.747 1.78235 11.8417 1.84368C11.9365 1.90502 12.0114 1.99251 12.0574 2.09556C12.1034 2.19861 12.1185 2.31279 12.1009 2.42426C12.0833 2.53573 12.0337 2.63971 11.9583 2.72358L10.2083 4.66667L11.9583 6.60975C12.0337 6.69363 12.0833 6.7976 12.1009 6.90907C12.1185 7.02054 12.1034 7.13472 12.0574 7.23777C12.0114 7.34082 11.9365 7.42832 11.8417 7.48965C11.747 7.55099 11.6365 7.58353 11.5237 7.58333H4.08325V1.75Z',
          fill: color || EColorPalette.cGreen2,
          opacity: 0.3,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.DESIGN_PIN: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 48 48')}
        ${this.getPath({
          d: 'M23.3471 16.6617L29.9987 12.1998L28.2427 10.4438C27.4617 9.66276 27.4617 8.39643 28.2427 7.61539L31.0712 4.78696C31.8522 4.00591 33.1185 4.00591 33.8996 4.78696L45.2133 16.1007C45.9944 16.8817 45.9944 18.148 45.2133 18.9291L42.3849 21.7575C41.6038 22.5386 40.3375 22.5386 39.5564 21.7575L37.8006 20.0016L33.3386 26.6533C33.8529 28.5048 33.9971 30.4642 33.733 32.4187L32.869 38.8118C32.7211 39.9064 31.7138 40.6739 30.6192 40.5259C30.1852 40.4673 29.7825 40.2678 29.4728 39.9582L10.0421 20.5274C9.26104 19.7464 9.26104 18.4801 10.0421 17.699C10.3517 17.3893 10.7545 17.1899 11.1885 17.1312L17.5816 16.2673C19.5361 16.0032 21.4955 16.1473 23.3471 16.6617V16.6617Z',
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getPath({
          d: 'M17.636 28.1211L9.55477 36.2023L8.4436 41.5561L13.7974 40.445L21.8786 32.3637L17.636 28.1211Z',
          fill: EColorPalette.cGray4,
          opacity: 0.3,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.YELLOW_FLAG: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 14 14')}
        ${this.getPath({
          d: 'M2.04175 1.75H2.91675V11.375C2.91675 11.6071 2.82456 11.8296 2.66047 11.9937C2.49637 12.1578 2.27381 12.25 2.04175 12.25C1.80968 12.25 1.58712 12.1578 1.42303 11.9937C1.25894 11.8296 1.16675 11.6071 1.16675 11.375V2.625C1.16675 2.39294 1.25894 2.17038 1.42303 2.00628C1.58712 1.84219 1.80968 1.75 2.04175 1.75Z',
          fill: color || EColorPalette.cYellow,
        })}
        ${this.getPath({
          d: 'M4.08325 1.75H11.5237C11.6365 1.7498 11.747 1.78235 11.8417 1.84368C11.9365 1.90502 12.0114 1.99251 12.0574 2.09556C12.1034 2.19861 12.1185 2.31279 12.1009 2.42426C12.0833 2.53573 12.0337 2.63971 11.9583 2.72358L10.2083 4.66667L11.9583 6.60975C12.0337 6.69363 12.0833 6.7976 12.1009 6.90907C12.1185 7.02054 12.1034 7.13472 12.0574 7.23777C12.0114 7.34082 11.9365 7.42832 11.8417 7.48965C11.747 7.55099 11.6365 7.58353 11.5237 7.58333H4.08325V1.75Z',
          fill: color || EColorPalette.cYellow,
          opacity: 0.3,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.ARROW_DROP_DOWN_CIRCLE: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 -960 960 960')}
        ${this.getPath({
          d: 'm480-360 160-160H320l160 160Z',
          fill: color || EColorPalette.cGreen,
        })}
        ${this.getPath({
          d: 'M480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Z',
          fill: color || EColorPalette.cGreen,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.CALENDAR_MONTH: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 -960 960 960')}
        ${this.getPath({
          d: 'M200-80q-33 0-56.5-23.5T120-160v-560q0-33 23.5-56.5T200-800h40v-80h80v80h320v-80h80v80h40q33 0 56.5 23.5T840-720v560q0 33-23.5 56.5T760-80H200Zm0-80h560v-400H200v400Zm0-480h560v-80H200v80Zm0 0v-80 80Zm280 240q-17 0-28.5-11.5T440-440q0-17 11.5-28.5T480-480q17 0 28.5 11.5T520-440q0 17-11.5 28.5T480-400Zm-160 0q-17 0-28.5-11.5T280-440q0-17 11.5-28.5T320-480q17 0 28.5 11.5T360-440q0 17-11.5 28.5T320-400Zm320 0q-17 0-28.5-11.5T600-440q0-17 11.5-28.5T640-480q17 0 28.5 11.5T680-440q0 17-11.5 28.5T640-400ZM480-240q-17 0-28.5-11.5T440-280q0-17 11.5-28.5T480-320q17 0 28.5 11.5T520-280q0 17-11.5 28.5T480-240Zm-160 0q-17 0-28.5-11.5T280-280q0-17 11.5-28.5T320-320q17 0 28.5 11.5T360-280q0 17-11.5 28.5T320-240Zm320 0q-17 0-28.5-11.5T600-280q0-17 11.5-28.5T640-320q17 0 28.5 11.5T680-280q0 17-11.5 28.5T640-240Z',
          fill: color || EColorPalette.cGray,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.CALENDAR_CLOCK: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 -960 960 960')}
        ${this.getPath({
          d: 'M200-640h560v-80H200v80Zm0 0v-80 80Zm0 560q-33 0-56.5-23.5T120-160v-560q0-33 23.5-56.5T200-800h40v-80h80v80h320v-80h80v80h40q33 0 56.5 23.5T840-720v227q-19-9-39-15t-41-9v-43H200v400h252q7 22 16.5 42T491-80H200Zm520 40q-83 0-141.5-58.5T520-240q0-83 58.5-141.5T720-440q83 0 141.5 58.5T920-240q0 83-58.5 141.5T720-40Zm67-105 28-28-75-75v-112h-40v128l87 87Z',
          fill: color || EColorPalette.cGray,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.PAGE_INFO: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 -960 960 960')}
        ${this.getPath({
          d: 'M710-150q-63 0-106.5-43.5T560-300q0-63 43.5-106.5T710-450q63 0 106.5 43.5T860-300q0 63-43.5 106.5T710-150Zm0-80q29 0 49.5-20.5T780-300q0-29-20.5-49.5T710-370q-29 0-49.5 20.5T640-300q0 29 20.5 49.5T710-230Zm-550-30v-80h320v80H160Zm90-250q-63 0-106.5-43.5T100-660q0-63 43.5-106.5T250-810q63 0 106.5 43.5T400-660q0 63-43.5 106.5T250-510Zm0-80q29 0 49.5-20.5T320-660q0-29-20.5-49.5T250-730q-29 0-49.5 20.5T180-660q0 29 20.5 49.5T250-590Zm230-30v-80h320v80H480Zm230 320ZM250-660Z',
          fill: color || EColorPalette.cGray,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.RED_FLAG: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 14 14')}
        ${this.getPath({
          d: 'M2.04175 1.75H2.91675V11.375C2.91675 11.6071 2.82456 11.8296 2.66047 11.9937C2.49637 12.1578 2.27381 12.25 2.04175 12.25C1.80968 12.25 1.58712 12.1578 1.42303 11.9937C1.25894 11.8296 1.16675 11.6071 1.16675 11.375V2.625C1.16675 2.39294 1.25894 2.17038 1.42303 2.00628C1.58712 1.84219 1.80968 1.75 2.04175 1.75Z',
          fill: color || EColorPalette.cRed,
        })}
        ${this.getPath({
          d: 'M4.08325 1.75H11.5237C11.6365 1.7498 11.747 1.78235 11.8417 1.84368C11.9365 1.90502 12.0114 1.99251 12.0574 2.09556C12.1034 2.19861 12.1185 2.31279 12.1009 2.42426C12.0833 2.53573 12.0337 2.63971 11.9583 2.72358L10.2083 4.66667L11.9583 6.60975C12.0337 6.69363 12.0833 6.7976 12.1009 6.90907C12.1185 7.02054 12.1034 7.13472 12.0574 7.23777C12.0114 7.34082 11.9365 7.42832 11.8417 7.48965C11.747 7.55099 11.6365 7.58353 11.5237 7.58333H4.08325V1.75Z',
          fill: color || EColorPalette.cRed,
          opacity: 0.3,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.REPEAT: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 25 24')}
        ${this.getPath({
          d: 'M12.8395 9.38404C12.5096 9.62892 12.0415 9.39342 12.0415 8.98255V8H8.0415C5.83236 8 4.0415 9.79086 4.0415 12V13C4.0415 14.6569 5.38465 16 7.0415 16V18C4.28008 18 2.0415 15.7614 2.0415 13V12C2.0415 8.68629 4.7278 6 8.0415 6H12.0415V4.728C12.0415 4.31643 12.511 4.08112 12.8407 4.3274L15.6977 6.46137C15.966 6.66171 15.9654 7.06392 15.6965 7.26345L12.8395 9.38404Z',
          fill: color || EColorPalette.cGreen3,
        })}
        ${this.getPath({
          d: 'M17.0414 6V8C18.6982 8 20.0414 9.34315 20.0414 11V12C20.0414 14.2091 18.2505 16 16.0414 16H12.0997V14.728C12.0997 14.3164 11.6302 14.0811 11.3005 14.3274L8.44347 16.4614C8.17524 16.6617 8.17585 17.0639 8.44468 17.2635L11.3017 19.384C11.6316 19.6289 12.0997 19.3934 12.0997 18.9825V18H16.0414C19.3551 18 22.0414 15.3137 22.0414 12V11C22.0414 8.23858 19.8028 6 17.0414 6Z',
          fill: color || EColorPalette.cGray4,
          opacity: 0.3,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.DOLLAR: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 25')}
        ${this.getRectangle({
          x: 11.5,
          y: 2.44792,
          width: 2,
          height: 4,
          rx: 1,
          fill: EColorPalette.cGray4,
          opacity: 0.3,
        })},
        ${this.getRectangle({
          x: 11.5,
          y: 16.4479,
          width: 2,
          height: 4,
          rx: 1,
          fill: EColorPalette.cGray4,
          opacity: 0.3,
        })},
        ${this.getPath({
          d: 'M15.4929 8.49192C15.2142 8.13725 14.8501 7.85542 14.4004 7.64642C13.9507 7.43741 13.5169 7.33292 13.0989 7.33292C12.8836 7.33292 12.6651 7.35192 12.4434 7.38992C12.2217 7.42792 12.0222 7.50075 11.8449 7.60842C11.6676 7.71608 11.5187 7.85541 11.3984 8.02642C11.2781 8.19742 11.2179 8.41591 11.2179 8.68192C11.2179 8.90992 11.2654 9.09991 11.3604 9.25192C11.4554 9.40392 11.5947 9.53691 11.7784 9.65092C11.9621 9.76492 12.1806 9.86942 12.4339 9.96442C12.6872 10.0594 12.9722 10.1576 13.2889 10.2589C13.7449 10.4109 14.2199 10.5787 14.7139 10.7624C15.2079 10.9461 15.6576 11.1899 16.0629 11.4939C16.4682 11.7979 16.8039 12.1747 17.0699 12.6244C17.3359 13.0741 17.4689 13.6346 17.4689 14.3059C17.4689 15.0786 17.3264 15.7467 17.0414 16.3104C16.7564 16.8741 16.3732 17.3396 15.8919 17.7069C15.4106 18.0743 14.8596 18.3466 14.2389 18.5239C13.6182 18.7012 12.9786 18.7899 12.3199 18.7899C11.3572 18.7899 10.4262 18.6221 9.52691 18.2864C8.62757 17.9507 7.88025 17.4726 7.28491 16.8519L9.41291 14.6859C9.74225 15.0913 10.1761 15.4301 10.7144 15.7024C11.2527 15.9748 11.7879 16.1109 12.3199 16.1109C12.5606 16.1109 12.7949 16.0856 13.0229 16.0349C13.2509 15.9842 13.4504 15.9019 13.6214 15.7879C13.7924 15.6739 13.9286 15.5219 14.0299 15.3319C14.1312 15.1419 14.1819 14.9139 14.1819 14.6479C14.1819 14.3946 14.1186 14.1792 13.9919 14.0019C13.8652 13.8246 13.6847 13.6631 13.4504 13.5174C13.2161 13.3717 12.9247 13.2387 12.5764 13.1184C12.2281 12.9981 11.8322 12.8682 11.3889 12.7289C10.9582 12.5896 10.5371 12.4249 10.1254 12.2349C9.71374 12.0449 9.34641 11.8011 9.02341 11.5034C8.70041 11.2057 8.44075 10.8448 8.24441 10.4204C8.04808 9.99608 7.94991 9.47992 7.94991 8.87192C7.94991 8.12458 8.10191 7.48492 8.40591 6.95292C8.70991 6.42091 9.10891 5.98392 9.60291 5.64192C10.0969 5.29991 10.6542 5.04975 11.2749 4.89142C11.8956 4.73308 12.5226 4.65392 13.1559 4.65392C13.9159 4.65392 14.6917 4.79325 15.4834 5.07192C16.2751 5.35058 16.9686 5.76225 17.5639 6.30692L15.4929 8.49192Z',
          fill: color || EColorPalette.cGreen3,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }
      case EIcon.DONE: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z',
          fill: color || EColorPalette.cGreen2,
          opacity: 0.3,
        })}
        ${this.getPath({
          d: 'M16.769 7.81769C17.1458 7.41393 17.7786 7.39211 18.1823 7.76895C18.5861 8.14578 18.6079 8.77857 18.2311 9.18232L11.2311 16.6823C10.8655 17.074 10.2561 17.108 9.84923 16.7593L6.34923 13.7593C5.9299 13.3998 5.88134 12.7685 6.24076 12.3492C6.60018 11.9299 7.23148 11.8813 7.65081 12.2407L10.423 14.6169L16.769 7.81769Z',
          fill: color || EColorPalette.cGreen2,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.DONE_WB: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M16.769 7.81769C17.1458 7.41393 17.7786 7.39211 18.1823 7.76895C18.5861 8.14578 18.6079 8.77857 18.2311 9.18232L11.2311 16.6823C10.8655 17.074 10.2561 17.108 9.84923 16.7593L6.34923 13.7593C5.9299 13.3998 5.88134 12.7685 6.24076 12.3492C6.60018 11.9299 7.23148 11.8813 7.65081 12.2407L10.423 14.6169L16.769 7.81769Z',
          fill: color || EColorPalette.cGreen2,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }
      case EIcon.QUESTION: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z',
          fill: color || EColorPalette.cYellow,
          opacity: 0.3,
        })}
        ${this.getPath({
          d: 'M10.5909 13.209V14.868H11.8509C14.3919 14.868 16.3869 12.915 16.3869 10.395C16.3869 7.875 14.3919 5.922 11.8509 5.922C9.30994 5.922 7.33594 7.875 7.33594 10.395H9.12094C9.12094 8.799 10.2339 7.581 11.8509 7.581C13.4469 7.581 14.6019 8.799 14.6019 10.395C14.6019 11.991 13.4469 13.209 11.8509 13.209H10.5909ZM11.9999 16C11.4477 16 10.9999 16.4477 10.9999 17C10.9999 17.5523 11.4477 18 11.9999 18C12.5522 18 12.9999 17.5523 12.9999 17C12.9999 16.4477 12.5522 16 11.9999 16Z',
          fill: color || EColorPalette.cYellow,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.ERROR: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z',
          fill: color || EColorPalette.cRed,
          opacity: 0.3,
        })}
        ${this.getPath({
          d: 'M16.2782 7.79289C15.8877 7.40237 15.2545 7.40237 14.864 7.79289L12.0355 10.6213L9.20711 7.79289C8.81658 7.40237 8.18342 7.40237 7.79289 7.79289C7.40237 8.18342 7.40237 8.81658 7.79289 9.20711L10.6213 12.0355L7.79289 14.864C7.40237 15.2545 7.40237 15.8877 7.79289 16.2782C8.18342 16.6687 8.81658 16.6687 9.20711 16.2782L12.0355 13.4497L14.864 16.2782C15.2545 16.6687 15.8877 16.6687 16.2782 16.2782C16.6687 15.8877 16.6687 15.2545 16.2782 14.864L13.4497 12.0355L16.2782 9.20711C16.6687 8.81658 16.6687 8.18342 16.2782 7.79289Z',
          fill: color || EColorPalette.cRed,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.PERMISSION_ERROR: {
        return `
        <svg width="50" height="50" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clip-path="url(#clip0_2609_370934)">
        <circle cx="25" cy="25" r="25" fill="#FBE2E5"/>
        <path d="M5 60L5 42.336L24.7912 36.448L24.7912 60M13.7961 39.7191L13.7961 14L35.0533 14L35.0533 50.8" stroke="#D72A40" stroke-width="3"/>
        <rect x="24" y="20" width="2.18182" height="8.72727" rx="1.09091" fill="#D72A40"/>
        <rect x="24" y="29.8184" width="2.18182" height="2.18182" rx="1.09091" fill="#D72A40"/>
        </g>
        <defs>
        <clipPath id="clip0_2609_370934">
        <rect width="50" height="50" rx="25" fill="white"/>
        </clipPath>
        </defs>
        </svg>
        `;
      }

      case EIcon.SEARCH: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M14.2929 16.7071C13.9024 16.3166 13.9024 15.6834 14.2929 15.2929C14.6834 14.9024 15.3166 14.9024 15.7071 15.2929L19.7071 19.2929C20.0976 19.6834 20.0976 20.3166 19.7071 20.7071C19.3166 21.0976 18.6834 21.0976 18.2929 20.7071L14.2929 16.7071Z',
          fill: color || EColorPalette.cGray,
          opacity: 0.3,
        })}
        ${this.getPath({
          d: 'M4 11C4 14.866 7.13401 18 11 18C14.866 18 18 14.866 18 11C18 7.13401 14.866 4 11 4C7.13401 4 4 7.13401 4 11ZM16 11C16 13.7614 13.7614 16 11 16C8.23858 16 6 13.7614 6 11C6 8.23858 8.23858 6 11 6C13.7614 6 16 8.23858 16 11Z',
          fill: color || EColorPalette.cGray,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.TICK: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 11 8')}
        ${this.getPath({
          d: 'M10.1424 0.306021C10.5504 0.71405 10.5504 1.37559 10.1424 1.78362L4.23202 7.69398C3.82399 8.10201 3.16245 8.10201 2.75442 7.69398L2.01562 6.95518L8.6648 0.306021C9.07283 -0.102007 9.73437 -0.102007 10.1424 0.306021Z',
          fill: color || EColorPalette.cGreen3,
        })}
        ${this.getPath({
          d: 'M0.306024 3.76769C0.714052 3.35966 1.37559 3.35966 1.78362 3.76769L4.97077 6.95484L4.23197 7.69364C3.82395 8.10167 3.16241 8.10167 2.75438 7.69364L0.306003 5.24527C-0.102005 4.83726 -0.102005 4.17572 0.306024 3.76769Z',
          fill: color || EColorPalette.cGreen3,
        })}
        ${this.getSvgEndTemplate()}`;
      }

      case EIcon.TIME_SCHEDULE: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M10.4646 7.96165C10.4846 7.70115 10.7018 7.5 10.9631 7.5H11.0476C11.3044 7.5 11.5195 7.69465 11.5451 7.95025L12.0001 12.5L15.2481 14.356C15.4039 14.4451 15.5001 14.6107 15.5001 14.7902V14.8454C15.5001 15.1751 15.1866 15.4145 14.8685 15.3278L10.3987 14.1087C10.1673 14.0456 10.0134 13.8271 10.0318 13.588L10.4646 7.96165Z',
          fill: color || EColorPalette.cGray4,
          opacity: 1,
        })}
        ${this.getPath({
          d: 'M6.20102 1.41818C5.93691 1.10343 5.42697 1.2239 5.33164 1.62357L4.20314 6.3547C4.12546 6.68035 4.38353 6.98885 4.71778 6.96991L9.58475 6.6941C9.99566 6.67082 10.204 6.18879 9.93948 5.87351L8.74872 4.45442C9.62236 4.15588 10.5488 3.99995 11.5 3.99995C16.1944 3.99995 20 7.80553 20 12.5C20 17.1944 16.1944 21 11.5 21C6.80558 21 3 17.1944 3 12.5C3 11.7119 3.10672 10.94 3.31481 10.1982L1.38913 9.65801C1.13559 10.5619 1 11.5151 1 12.5C1 18.2989 5.70101 23 11.5 23C17.299 23 22 18.2989 22 12.5C22 6.70096 17.299 1.99995 11.5 1.99995C10.0413 1.99995 8.65217 2.29739 7.3898 2.83491L6.20102 1.41818Z',
          fill: color || EColorPalette.cGray4,
          opacity: 0.3,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.THUNDER_CIRCLE: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z',
          fill: color || EColorPalette.cGray4,
          opacity: 0.3,
        })}
        ${this.getPath({
          d: 'M11 16.8229C11 17.6144 12.0669 17.8662 12.4208 17.1584L15.4573 11.0854C15.7066 10.5867 15.344 10 14.7865 10H12V7.17705C12 6.38563 10.9331 6.13377 10.5792 6.84164L7.54271 12.9146C7.29337 13.4133 7.65599 14 8.21353 14H11V16.8229Z',
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.SHIELD_USER: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M4 4.00006L11.6314 2.56917C11.875 2.52349 12.125 2.52349 12.3686 2.56917L20 4.00006V11.9033C20 15.696 18.0462 19.2212 14.83 21.2313L12.53 22.6688C12.2057 22.8715 11.7943 22.8715 11.47 22.6688L9.17001 21.2313C5.95382 19.2212 4 15.696 4 11.9033L4 4.00006Z',
          fill: color || EColorPalette.cGray4,
          opacity: 0.3,
        })}
        ${this.getPath({
          d: 'M12 11C10.8954 11 10 10.1046 10 9C10 7.89543 10.8954 7 12 7C13.1046 7 14 7.89543 14 9C14 10.1046 13.1046 11 12 11Z',
          fill: color || EColorPalette.cGray4,
          opacity: 0.3,
        })}
        ${this.getPath({
          d: 'M7.00036 16.4995C7.2157 13.5166 9.36773 12 11.9907 12C14.6507 12 16.836 13.4332 16.9988 16.5C17.0053 16.6222 16.9988 17 16.5815 17C14.5228 17 11.4637 17 7.40417 17C7.26484 17 6.98863 16.662 7.00036 16.4995Z',
          fill: color || EColorPalette.cGray4,
          opacity: 0.3,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }
      case EIcon.PORTFOLIOS: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M2 19.5C2 20.3284 2.67157 21 3.5 21H20.5C21.3284 21 22 20.3284 22 19.5V8.5C22 7.67157 21.3284 7 20.5 7H10L7.43934 4.43934C7.15804 4.15804 6.7765 4 6.37868 4H3.5C2.67157 4 2 4.67157 2 5.5V19.5Z',
          fill: color || EColorPalette.cGray4,
          opacity: 0.3,
        })}
        ${this.getRectangle({
          opacity: 0.3,
          x: 6.76172,
          y: 14.127,
          width: 3.14286,
          height: 3.14286,
          rx: 0.75,
          transform: 'rotate(-45 6.76172 14.127)',
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getRectangle({
          opacity: 0.3,
          x: 13.0479,
          y: 14.127,
          width: 3.14286,
          height: 3.14286,
          rx: 0.75,
          transform: 'rotate(-45 13.0479 14.127)',
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getRectangle({
          x: 9.9043,
          y: 17.2695,
          width: 3.14286,
          height: 3.14286,
          rx: 0.75,
          transform: 'rotate(-45 9.9043 17.2695)',
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getRectangle({
          x: 9.9043,
          y: 10.9844,
          width: 3.14286,
          height: 3.14286,
          rx: 0.75,
          transform: 'rotate(-45 9.9043 10.9844)',
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.STAR: {
        return `
          ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 16 16')}
          ${this.getPath({
            d: 'M8 12.5003L5.27726 13.9318C4.95137 14.1031 4.54828 13.9779 4.37695 13.652C4.30872 13.5222 4.28518 13.3735 4.30996 13.229L4.83 10.197L2.62711 8.04973C2.36345 7.79273 2.35806 7.37065 2.61506 7.107C2.7174 7.00201 2.85149 6.93368 2.99658 6.9126L6.0409 6.47023L7.40236 3.71162C7.56531 3.38145 7.96506 3.24589 8.29523 3.40884C8.4267 3.47372 8.53312 3.58014 8.59801 3.71162L9.95947 6.47023L13.0038 6.9126C13.3681 6.96555 13.6206 7.30384 13.5677 7.6682C13.5466 7.8133 13.4783 7.94739 13.3733 8.04973L11.1704 10.197L11.6904 13.229C11.7526 13.5919 11.5089 13.9366 11.146 13.9988C11.0015 14.0236 10.8529 14 10.7231 13.9318L8 12.5003Z',
            fill: color || EColorPalette.cGray5,
          })}
          ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.HALF_STAR: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 25 24')}
        ${this.getPath({
          d: 'M12 17.9999L16.0844 20.1472C16.279 20.2496 16.502 20.2849 16.7188 20.2477C17.2631 20.1544 17.6287 19.6374 17.5353 19.0931L16.7553 14.545L20.0596 11.3241C20.2171 11.1706 20.3196 10.9695 20.3512 10.7518C20.4306 10.2053 20.0519 9.69783 19.5054 9.61841L14.9389 8.95486L12.8967 4.81693C12.7994 4.61972 12.6398 4.46009 12.4426 4.36276C12.3 4.29242 12.1489 4.25916 12 4.25928V17.9999Z',
          fill: color || EColorPalette.cGray4,
          opacity: 0.3,
        })}
        ${this.getPath({
          d: 'M12 4.25928C11.6316 4.25956 11.2774 4.46421 11.1033 4.81693L9.06111 8.95486L4.49463 9.61841C4.27699 9.65004 4.07585 9.75252 3.92234 9.91001C3.53684 10.3055 3.54493 10.9386 3.94042 11.3241L7.24475 14.545L6.4647 19.0931C6.42753 19.3098 6.46284 19.5328 6.56518 19.7274C6.82218 20.2163 7.42681 20.4042 7.91565 20.1472L12 17.9999V4.25928Z',
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.PLUS: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 25 24')}
        ${this.getRectangle({
          x: 4.5,
          y: 11,
          width: 16,
          height: 2,
          rx: 1,
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getRectangle({
          x: 13.5,
          y: 4,
          width: 16,
          height: 2,
          rx: 1,
          fill: color || EColorPalette.cGray4,
          transform: 'rotate(90 13.5 4)',
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.USER: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7C16 9.20914 14.2091 11 12 11Z',
          fill: color || EColorPalette.cGray4,
          opacity: 0.3,
        })}
        ${this.getPath({
          d: 'M3.00065 20.1992C3.38826 15.4265 7.26191 13 11.9833 13C16.7712 13 20.7049 15.2932 20.9979 20.2C21.0096 20.3955 20.9979 21 20.2467 21C16.5411 21 11.0347 21 3.7275 21C3.47671 21 2.97954 20.4592 3.00065 20.1992Z',
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.USER_GROUP: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M5 7C5 9.20914 6.79086 11 9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7ZM15 11C15 12.6569 16.3431 14 18 14C19.6569 14 21 12.6569 21 11C21 9.34315 19.6569 8 18 8C16.3431 8 15 9.34315 15 11Z',
          fill: color || EColorPalette.cWhite,
          opacity: 0.3,
        })}
        ${this.getPath({
          d: 'M8.98334 13C4.26191 13 0.388259 15.4265 0.000651684 20.1992C-0.0204618 20.4592 0.476712 21 0.727502 21H17.2467C17.9979 21 18.0096 20.3955 17.9979 20.2C17.7049 15.2932 13.7712 13 8.98334 13ZM23.4559 21H19.6C19.6 18.7491 18.8563 16.6718 17.6012 15.0006C21.0077 15.0379 23.7892 16.7601 23.9985 20.4C24.0069 20.5466 23.9985 21 23.4559 21Z',
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.USER_GROUP_FILL: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M5 7C5 9.20914 6.79086 11 9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7ZM15 11C15 12.6569 16.3431 14 18 14C19.6569 14 21 12.6569 21 11C21 9.34315 19.6569 8 18 8C16.3431 8 15 9.34315 15 11Z',
          fill: color || EColorPalette.cGreen,
        })}
        ${this.getPath({
          d: 'M8.98334 13C4.26191 13 0.388259 15.4265 0.000651684 20.1992C-0.0204618 20.4592 0.476712 21 0.727502 21H17.2467C17.9979 21 18.0096 20.3955 17.9979 20.2C17.7049 15.2932 13.7712 13 8.98334 13ZM23.4559 21H19.6C19.6 18.7491 18.8563 16.6718 17.6012 15.0006C21.0077 15.0379 23.7892 16.7601 23.9985 20.4C24.0069 20.5466 23.9985 21 23.4559 21Z',
          fill: color || EColorPalette.cGreen,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.DASHBOARD: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M3 4C2.44772 4 2 4.44772 2 5V7C2 7.55228 2.44771 8 3 8H20C20.5523 8 21 7.55228 21 7V5C21 4.44772 20.5523 4 20 4H3ZM10 10C9.44772 10 9 10.4477 9 11V19C9 19.5523 9.44771 20 10 20H20C20.5523 20 21 19.5523 21 19V11C21 10.4477 20.5523 10 20 10H10Z',
          fill: color || EColorPalette.cWhite,
        })}
        ${this.getRectangle({
          x: 2,
          y: 10,
          width: 5,
          height: 10,
          rx: 1,
          fill: color || EColorPalette.cWhite,
          opacity: 0.3,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.DASHBOARD2: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M9 5C9 4.44772 9.44772 4 10 4H21C21.5523 4 22 4.44772 22 5V7C22 7.55228 21.5523 8 21 8H10C9.44772 8 9 7.55228 9 7V5ZM9 11C9 10.4477 9.44772 10 10 10H21C21.5523 10 22 10.4477 22 11V13C22 13.5523 21.5523 14 21 14H10C9.44772 14 9 13.5523 9 13V11ZM10 16C9.44772 16 9 16.4477 9 17V19C9 19.5523 9.44772 20 10 20H21C21.5523 20 22 19.5523 22 19V17C22 16.4477 21.5523 16 21 16H10Z',
          fill: color || EColorPalette.cWhite,
        })}
        ${this.getRectangle({
          x: 2,
          y: 4,
          width: 5,
          height: 16,
          rx: 1,
          fill: color || EColorPalette.cWhite,
          opacity: 0.3,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.DASHBOARD3: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 25 24')}
        ${this.getPath({
          d: 'M3.5 4C2.94772 4 2.5 4.44772 2.5 5V7C2.5 7.55228 2.94771 8 3.5 8H20.5C21.0523 8 21.5 7.55228 21.5 7V5C21.5 4.44772 21.0523 4 20.5 4H3.5ZM3.5 10C2.94772 10 2.5 10.4477 2.5 11V19C2.5 19.5523 2.94772 20 3.5 20H13.5C14.0523 20 14.5 19.5523 14.5 19V11C14.5 10.4477 14.0523 10 13.5 10H3.5Z',
          fill: color || EColorPalette.cWhite,
        })}
        ${this.getRectangle({
          x: 16.5,
          y: 10,
          width: 5,
          height: 10,
          rx: 1,
          fill: color || EColorPalette.cWhite,
          opacity: 0.3,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }
      case EIcon.DASHBOARD4: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M2.5 5C2.5 4.44772 2.94772 4 3.5 4H20.5C21.0523 4 21.5 4.44772 21.5 5V7C21.5 7.55228 21.0523 8 20.5 8H3.5C2.94771 8 2.5 7.55228 2.5 7V5ZM2.5 11C2.5 10.4477 2.94772 10 3.5 10H13.5C14.0523 10 14.5 10.4477 14.5 11V13C14.5 13.5523 14.0523 14 13.5 14H3.5C2.94772 14 2.5 13.5523 2.5 13V11ZM3.5 16C2.94772 16 2.5 16.4477 2.5 17V19C2.5 19.5523 2.94772 20 3.5 20H13.5C14.0523 20 14.5 19.5523 14.5 19V17C14.5 16.4477 14.0523 16 13.5 16H3.5Z',
          fill: color || EColorPalette.cWhite,
        })}
        ${this.getRectangle({
          x: 16.5,
          y: 10,
          width: 5,
          height: 10,
          rx: 1,
          fill: color || EColorPalette.cWhite,
          opacity: 0.3,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.CHAIR: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M6.54213 21.5015C6.39919 22.035 6.71578 22.5833 7.24924 22.7262C7.78271 22.8692 8.33104 22.5526 8.47399 22.0191L10.0721 16.0547C10.2151 15.5213 9.8985 14.9729 9.36504 14.83C8.83157 14.687 8.28323 15.0036 8.14029 15.5371L6.54213 21.5015ZM17.4493 21.4711C17.5922 22.0046 17.2756 22.5529 16.7422 22.6959C16.2087 22.8388 15.6604 22.5222 15.5174 21.9888L14.2145 17.1263C14.0716 16.5929 14.3882 16.0445 14.9216 15.9016C15.4551 15.7586 16.0034 16.0752 16.1464 16.6087L17.4493 21.4711Z',
          fill: color || EColorPalette.cWhite,
          opacity: 0.3,
        })}
        ${this.getPath({
          d: 'M8 1C6.89543 1 6 1.89543 6 3V12.5C6 12.7761 6.22386 13 6.5 13H17.5C17.7761 13 18 12.7761 18 12.5V3C18 1.89543 17.1046 1 16 1H8ZM5.5 14C4.67157 14 4 14.6716 4 15.5C4 16.3284 4.67157 17 5.5 17H18.5C19.3284 17 20 16.3284 20 15.5C20 14.6716 19.3284 14 18.5 14H5.5Z',
          fill: color || EColorPalette.cWhite,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.LIBRARY: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M5 3C4.44772 3 4 3.44772 4 4V20C4 20.5523 4.44772 21 5 21H6C6.55228 21 7 20.5523 7 20V4C7 3.44772 6.55228 3 6 3H5ZM10 3C9.44772 3 9 3.44772 9 4V20C9 20.5523 9.44772 21 10 21H11C11.5523 21 12 20.5523 12 20V4C12 3.44772 11.5523 3 11 3H10Z',
          fill: color || EColorPalette.cWhite,
          opacity: 0.3,
        })}
        ${this.getRectangle({
          x: 13.4775,
          y: 3.92419,
          width: 3,
          height: 18,
          rx: 1,
          fill: color || EColorPalette.cWhite,
          opacity: 0.3,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.VISIBLE: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M3 12C3 12 5.45455 6 12 6C16.9091 6 21 12 21 12C21 12 16.9091 18 12 18C5.45455 18 3 12 3 12Z',
          fill: color || EColorPalette.cGreen3,
          opacity: 0.3,
        })}
        ${this.getPath({
          d: 'M12 15C10.3431 15 9 13.6569 9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12C15 13.6569 13.6569 15 12 15Z',
          fill: color || EColorPalette.cGreen3,
          opacity: 0.3,
        })}

        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.FILE_DONE: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M14.2364 2H6.35714C4.52045 2 4.5 2.12612 4.5 3.91667V20.0833C4.5 21.8739 4.52045 22 6.35714 22H18.6429C20.4796 22 20.5 21.8739 20.5 20.0833V7.5292C20.5 7.08788 20.3057 6.66895 19.9687 6.38394L15.2051 2.35474C14.9343 2.12568 14.5911 2 14.2364 2ZM10.7042 15.4625C10.8958 15.6542 11.1354 15.75 11.375 15.75C11.6146 15.75 11.8542 15.6542 12.0458 15.4625L15.8792 11.6292C16.2625 11.2458 16.2625 10.6708 15.8792 10.2875C15.4958 9.90417 14.9208 9.90417 14.5375 10.2875L11.375 13.45L10.1292 12.2042C9.79375 11.8208 9.17083 11.8208 8.7875 12.2042C8.40417 12.5875 8.40417 13.1625 8.7875 13.5458L10.7042 15.4625Z',
          fill: color || EColorPalette.cGray2,
          opacity: 0.3,
        })}
        ${this.getPath({
          d: 'M11.375 15.75C11.1354 15.75 10.8958 15.6542 10.7042 15.4625L8.7875 13.5458C8.40417 13.1625 8.40417 12.5875 8.7875 12.2042C9.17083 11.8208 9.79375 11.8208 10.1292 12.2042L11.375 13.45L14.5375 10.2875C14.9208 9.90417 15.4958 9.90417 15.8792 10.2875C16.2625 10.6708 16.2625 11.2458 15.8792 11.6292L12.0458 15.4625C11.8542 15.6542 11.6146 15.75 11.375 15.75Z',
          fill: secondColor || EColorPalette.cWhite,
        })}

        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.TIMER: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M12.5 21.5C8.08172 21.5 4.5 17.9183 4.5 13.5C4.5 9.08172 8.08172 5.5 12.5 5.5C16.9183 5.5 20.5 9.08172 20.5 13.5C20.5 17.9183 16.9183 21.5 12.5 21.5Z',
          fill: color || EColorPalette.cGray4,
          opacity: 0.3,
        })}
        ${this.getPath({
          d: 'M13.5 5.56189V4.5H14.5C15.0523 4.5 15.5 4.05228 15.5 3.5C15.5 2.94772 15.0523 2.5 14.5 2.5H10.5C9.94772 2.5 9.5 2.94772 9.5 3.5C9.5 4.05228 9.94772 4.5 10.5 4.5H11.5V5.56189C11.8276 5.52104 12.1613 5.5 12.5 5.5C12.8387 5.5 13.1724 5.52104 13.5 5.56189Z',
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getPath({
          d: 'M18.6672 8.40402L19.4498 7.62141C19.8403 7.23089 19.8403 6.59772 19.4498 6.2072C19.0593 5.81668 18.4261 5.81668 18.0356 6.2072L17.21 7.03282C17.7511 7.4276 18.2408 7.88864 18.6672 8.40402Z',
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getPath({
          d: 'M11.9646 8.46165C11.9846 8.20115 12.2018 8 12.4631 8H12.5371C12.7984 8 13.0156 8.20115 13.0356 8.46165L13.4587 13.9617C13.481 14.252 13.2514 14.5 12.9602 14.5H12.04C11.7488 14.5 11.5192 14.252 11.5415 13.9617L11.9646 8.46165Z',
          fill: color || EColorPalette.cGray4,
        })}

        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.TOOLS: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M15.9504 3.80746L13.0252 6.7326C12.2442 7.51365 12.2442 8.77998 13.0252 9.56103L14.4394 10.9752C15.2205 11.7563 16.4868 11.7563 17.2679 10.9752L20.193 8.0501C20.7347 10.0446 20.2302 12.2555 18.6752 13.8106C16.8459 15.6398 14.1092 16.0154 11.8846 14.9443L6.75796 20.0709C5.97692 20.852 4.71059 20.852 3.92954 20.0709C3.14849 19.2899 3.14849 18.0235 3.92954 17.2425L9.05617 12.1159C7.98507 9.89129 8.36066 7.15452 10.1899 5.32529C11.7449 3.77026 13.9558 3.26574 15.9504 3.80746Z',
          fill: color || EColorPalette.cGreen,
        })}
        ${this.getPath({
          d: 'M16.6571 5.92912L18.0713 7.34334C18.4618 7.73386 18.4618 8.36703 18.0713 8.75755L16.6916 10.1372C16.3011 10.5278 15.6679 10.5278 15.2774 10.1372L13.8632 8.72301C13.4727 8.33249 13.4727 7.69932 13.8632 7.3088L15.2429 5.92912C15.6334 5.5386 16.2666 5.5386 16.6571 5.92912Z',
          fill: color || EColorPalette.cGreen,
          opacity: 0.3,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.ARCHIVE: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 25 24')}
        ${this.getPath({
          d: 'M5 3C4.17157 3 3.5 3.67157 3.5 4.5V19.5C3.5 20.3284 4.17157 21 5 21H20C20.8284 21 21.5 20.3284 21.5 19.5V4.5C21.5 3.67157 20.8284 3 20 3H5ZM8.5 5C7.94772 5 7.5 5.44772 7.5 6C7.5 6.55228 7.94772 7 8.5 7H16.5C17.0523 7 17.5 6.55228 17.5 6C17.5 5.44772 17.0523 5 16.5 5H8.5Z',
          fill: color || EColorPalette.cGreen,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.BINOCULAR: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M12.8433 16H11.1563L10.985 16.6393C10.3351 19.0649 7.84182 20.5044 5.41617 19.8545C2.99052 19.2045 1.55103 16.7112 2.20098 14.2856L3.92068 7.86759C4.57064 5.44194 7.0639 4.00245 9.48955 4.6524C10.542 4.9344 11.4088 5.56341 11.9998 6.38334C12.5909 5.56341 13.4576 4.9344 14.5101 4.6524C16.9357 4.00245 19.429 5.44194 20.079 7.86759L21.7987 14.2856C22.4486 16.7112 21.0091 19.2045 18.5835 19.8545C16.1578 20.5044 13.6646 19.0649 13.0146 16.6393L12.8433 16ZM17.4562 18.1051C18.9629 18.1051 20.1843 16.8378 20.1843 15.2744C20.1843 13.7109 18.9629 12.4436 17.4562 12.4436C15.9494 12.4436 14.728 13.7109 14.728 15.2744C14.728 16.8378 15.9494 18.1051 17.4562 18.1051ZM6.54347 18.1051C8.0502 18.1051 9.27164 16.8378 9.27164 15.2744C9.27164 13.7109 8.0502 12.4436 6.54347 12.4436C5.03674 12.4436 3.81529 13.7109 3.81529 15.2744C3.81529 16.8378 5.03674 18.1051 6.54347 18.1051Z',
          fill: color || EColorPalette.cGreen,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.ARROW_FROM_LEFT: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 25 24')}
        ${this.getRectangle({
          x: 7.5,
          y: 13,
          width: 2,
          height: 14,
          rx: 1,
          transform: 'rotate(-90 7.5 13)',
          fill: color || EColorPalette.cWhite,
          opacity: 0.3,
        })}
        ${this.getRectangle({
          x: 3.5,
          y: 3,
          width: 2,
          height: 18,
          rx: 1,
          fill: color || EColorPalette.cWhite,
          opacity: 0.3,
        })}
        ${this.getPath({
          d: 'M13.7929 17.2929C13.4024 17.6834 13.4024 18.3166 13.7929 18.7071C14.1834 19.0976 14.8166 19.0976 15.2071 18.7071L21.2071 12.7071C21.5857 12.3285 21.5989 11.7189 21.2372 11.3243L15.7372 5.32428C15.364 4.91716 14.7314 4.88965 14.3243 5.26285C13.9172 5.63604 13.8897 6.26861 14.2628 6.67572L19.1159 11.9699L13.7929 17.2929Z',
          fill: color || EColorPalette.cWhite,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.ARROW_FROM_RIGHT: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 25 24')}
        ${this.getRectangle({
          opacity: 0.3,
          width: 2,
          height: 14,
          rx: 1,
          transform: 'matrix(0 -1 -1 0 17.5 13)',
          fill: color || EColorPalette.cWhite,
        })}
        ${this.getRectangle({
          opacity: 0.3,
          x: 19.5,
          y: 3,
          width: 2,
          height: 18,
          rx: 1,
          fill: color || EColorPalette.cWhite,
        })}
        ${this.getPath({
          d: 'M11.2071 17.2929C11.5976 17.6834 11.5976 18.3166 11.2071 18.7071C10.8166 19.0976 10.1834 19.0976 9.79289 18.7071L3.79289 12.7071C3.41432 12.3285 3.40107 11.7189 3.76285 11.3243L9.26285 5.32428C9.63604 4.91716 10.2686 4.88965 10.6757 5.26285C11.0828 5.63604 11.1103 6.26861 10.7372 6.67572L5.88414 11.9699L11.2071 17.2929Z',
          fill: color || EColorPalette.cWhite,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.PENCIL: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M20.4745 8.33237C21.0488 8.90667 21.0617 9.83374 20.5036 10.4238L10.9438 20.5307C10.6605 20.8303 10.2664 21 9.85405 21L5.49995 21C4.67153 21 3.99995 20.3284 3.99995 19.5L3.99995 15.1074C3.99995 14.7178 4.15155 14.3435 4.42266 14.0637L14.0817 4.09442C14.6646 3.49279 15.6273 3.48519 16.2196 4.07753L20.4745 8.33237Z',
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.CLOSE: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 25 24')}

        ${this.getRectangle({
          x: 6.13599,
          y: 16.9498,
          width: 16,
          height: 2,
          rx: 1,
          transform: 'rotate(-45 6.13599 16.9498)',
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getRectangle({
          x: 7.55029,
          y: 5.63605,
          width: 16,
          height: 2,
          rx: 1,
          transform: 'rotate(45 7.55029 5.63605)',
          fill: color || EColorPalette.cGray4,
          opacity: 1,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.RADIO_DEFAULT: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 17 17')}
        ${this.getCircle({
          cx: 8.1189,
          cy: 8.10181,
          r: 7,
          stroke: EColorPalette.cGray2,
          strokeWidth: 2,
        })}

        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.RADIO_SELECTED: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 17 17')}
        ${this.getCircle({
          cx: 8.1189,
          cy: 8.10181,
          r: 7,
          stroke: EColorPalette.cGreen,
          strokeWidth: 2,
        })}
        ${this.getCircle({
          cx: 8,
          cy: 8,
          r: 4,
          fill: EColorPalette.cGreen,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.BUILDING: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 37 26')}
        ${this.getPath({
          d: 'M5 4C5 2.89543 5.89543 2 7 2H17C18.1046 2 19 2.89543 19 4V21H13.5V18C13.5 17.4477 13.0523 17 12.5 17H11.5C10.9477 17 10.5 17.4477 10.5 18V21H5V4ZM8 5C8 4.44772 8.44772 4 9 4H10C10.5523 4 11 4.44772 11 5V6C11 6.55228 10.5523 7 10 7H9C8.44772 7 8 6.55228 8 6V5ZM14 4C13.4477 4 13 4.44772 13 5V6C13 6.55228 13.4477 7 14 7H15C15.5523 7 16 6.55228 16 6V5C16 4.44772 15.5523 4 15 4H14ZM8 9C8 8.44772 8.44772 8 9 8H10C10.5523 8 11 8.44772 11 9V10C11 10.5523 10.5523 11 10 11H9C8.44772 11 8 10.5523 8 10V9ZM9 12C8.44772 12 8 12.4477 8 13V14C8 14.5523 8.44772 15 9 15H10C10.5523 15 11 14.5523 11 14V13C11 12.4477 10.5523 12 10 12H9ZM13 13C13 12.4477 13.4477 12 14 12H15C15.5523 12 16 12.4477 16 13V14C16 14.5523 15.5523 15 15 15H14C13.4477 15 13 14.5523 13 14V13Z',
          fill: color || EColorPalette.cGreen,
        })}
        ${this.getRectangle({
          x: 13,
          y: 8,
          width: 3,
          height: 3,
          rx: 1,
          fill: EColorPalette.cGray6,
        })}
        ${this.getPath({
          d: 'M3 22.9881C3 21.8901 3.89009 21 4.98807 21H19.0119C20.1099 21 21 21.8901 21 22.9881C21 22.9947 20.9947 23 20.9881 23H3.01193C3.00534 23 3 22.9947 3 22.9881Z',
          fill: color || EColorPalette.cGreen,
          opacity: 0.3,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.MAIL: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M11.5746 21.1998C6.17461 21.1998 2.84961 17.3998 2.84961 12.5748C2.84961 6.8748 7.37461 3.0498 12.5246 3.0498C17.4496 3.0498 21.1246 6.0748 21.1246 10.8498C21.1246 15.1998 18.8246 16.9248 16.5246 16.9248C15.3996 16.9248 14.4746 16.3998 14.0746 15.6498C13.2996 16.3998 12.1246 16.8748 10.9996 16.8748C8.24961 16.8748 6.84961 14.9248 6.84961 12.5748C6.84961 9.5498 9.04961 7.0998 12.2746 7.0998C13.1996 7.0998 13.9496 7.3498 14.5246 7.7748L14.6246 7.3498H16.9996L15.8246 12.8498C15.5996 13.9498 15.8496 14.8248 16.9246 14.8248C18.2496 14.8248 19.0246 13.7248 19.0246 10.7998C19.0246 6.8998 15.9496 5.0748 12.4996 5.0748C8.62461 5.0748 5.04961 7.7498 5.04961 12.5748C5.04961 16.5248 7.57461 19.0998 11.5746 19.0998C13.0746 19.0998 14.6246 18.7748 15.9746 18.0748L16.7996 20.0998C15.2496 20.7998 13.1996 21.1998 11.5746 21.1998ZM11.3996 14.5248C12.0496 14.5248 12.6996 14.3498 13.2246 13.8248L14.0246 10.1248C13.5746 9.6498 12.9246 9.4248 12.2996 9.4248C10.6496 9.4248 9.44961 10.6998 9.44961 12.3748C9.44961 13.6748 10.0746 14.5248 11.3996 14.5248Z',
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.MAIL2: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M5 6C3.89543 6 3 6.89543 3 8V17C3 18.1046 3.89543 19 5 19H19C20.1046 19 21 18.1046 21 17V8C21 6.89543 20.1046 6 19 6H5ZM19.1604 8.14443C18.964 7.77972 18.5091 7.64327 18.1444 7.83965L12 11.1482L5.85557 7.83965C5.49087 7.64327 5.03603 7.77972 4.83965 8.14443C4.64327 8.50913 4.77972 8.96397 5.14443 9.16035L11.6444 12.6604C11.8664 12.7799 12.1336 12.7799 12.3556 12.6604L18.8556 9.16035C19.2203 8.96397 19.3567 8.50913 19.1604 8.14443Z',
          fill: color || EColorPalette.cBlue2,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.IPHONE: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M16.1667 1H7.83333C6.42705 1 5.75 1.98122 5.75 3.5V20.5C5.75 22.0188 6.42705 23 7.83333 23H16.1667C17.5729 23 18.25 22.0188 18.25 20.5V3.5C18.25 1.98122 17.5729 1 16.1667 1ZM7.13889 19V4H16.8611V19H7.13889Z',
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getPath({
          d: 'M7 4V19H17V4H7Z',
          fill: color || EColorPalette.cGray4,
          opacity: 0.3,
        })}
        ${this.getPath({
          d: 'M12 22C12.5523 22 13 21.5523 13 21C13 20.4477 12.5523 20 12 20C11.4477 20 11 20.4477 11 21C11 21.5523 11.4477 22 12 22Z',
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.IMPORT: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 25 24')}
        ${this.getRectangle({
          opacity: 0.3,
          x: 13.5,
          y: 13,
          width: 2,
          height: 12,
          rx: 1,
          transform: 'rotate(-180 13.5 13)',
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getPath({
          d: 'M17.5 7.99998C16.9477 7.99998 16.5 7.55227 16.5 6.99999C16.5 6.4477 16.9477 5.99999 17.5 5.99999H18.5C20.7091 5.99999 22.5 7.79085 22.5 9.99999V18C22.5 20.2091 20.7091 22 18.5 22H6.5C4.29086 22 2.5 20.2091 2.5 18L2.5 9.99304C2.5 7.7839 4.29086 5.99304 6.5 5.99304H7.5C8.05229 5.99304 8.5 6.44076 8.5 6.99304C8.5 7.54533 8.05229 7.99304 7.5 7.99304H6.5C5.39543 7.99304 4.5 8.88847 4.5 9.99304V18C4.5 19.1046 5.39543 20 6.5 20H18.5C19.6046 20 20.5 19.1046 20.5 18V9.99999C20.5 8.89542 19.6046 7.99998 18.5 7.99998H17.5Z',
          opacity: 0.3,
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getPath({
          d: 'M14.7929 10.2929C15.1834 9.90237 15.8166 9.90237 16.2071 10.2929C16.5976 10.6834 16.5976 11.3166 16.2071 11.7071L13.2071 14.7071C12.8166 15.0976 12.1834 15.0976 11.7929 14.7071L8.79289 11.7071C8.40237 11.3166 8.40237 10.6834 8.79289 10.2929C9.18342 9.90237 9.81658 9.90237 10.2071 10.2929L12.5 12.5858L14.7929 10.2929Z',
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.EXPORT: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 25 24')}
        ${this.getPath({
          d: 'M17.5 7.99998C16.9477 7.99998 16.5 7.55227 16.5 6.99999C16.5 6.4477 16.9477 5.99999 17.5 5.99999H18.5C20.7091 5.99999 22.5 7.79085 22.5 9.99999V18C22.5 20.2091 20.7091 22 18.5 22H6.5C4.29086 22 2.5 20.2091 2.5 18L2.5 9.99304C2.5 7.7839 4.29086 5.99304 6.5 5.99304H7.5C8.05229 5.99304 8.5 6.44076 8.5 6.99304C8.5 7.54533 8.05229 7.99304 7.5 7.99304H6.5C5.39543 7.99304 4.5 8.88847 4.5 9.99304V18C4.5 19.1046 5.39543 20 6.5 20H18.5C19.6046 20 20.5 19.1046 20.5 18V9.99999C20.5 8.89542 19.6046 7.99998 18.5 7.99998H17.5Z',
          opacity: 0.3,
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getRectangle({
          opacity: 0.3,
          width: 2,
          height: 12,
          rx: 1,
          transform: 'matrix(-1 0 0 1 13.5 2)',
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getPath({
          d: 'M12.5 2.41421L14.7929 4.70711C15.1834 5.09763 15.8166 5.09763 16.2071 4.70711C16.5976 4.31658 16.5976 3.68342 16.2071 3.29289L13.2071 0.292893C12.8166 -0.097631 12.1834 -0.097631 11.7929 0.292893L8.79289 3.29289C8.40237 3.68342 8.40237 4.31658 8.79289 4.70711C9.18342 5.09763 9.81658 5.09763 10.2071 4.70711L12.5 2.41421Z',
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.NOTIFICATION: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M11.5 3C9.01472 3 7 5.01472 7 7.5V12H5.5C4.67157 12 4 12.6716 4 13.5C4 14.3284 4.67157 15 5.5 15H18.5C19.3284 15 20 14.3284 20 13.5C20 12.6716 19.3284 12 18.5 12H17V7.5C17 5.01472 14.9853 3 12.5 3H11.5Z',
          fill: color || EColorPalette.cGreen,
        })}
        ${this.getRectangle({
          x: 10,
          y: 16,
          width: 4,
          height: 4,
          rx: 2,
          opacity: 0.3,
          fill: color || EColorPalette.cGreen,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.CHECK: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 14 14')}
        ${this.getPath({
          d: 'M10.3472 3.70553C10.5558 3.46006 10.924 3.43021 11.1694 3.63886C11.4149 3.84751 11.4448 4.21565 11.2361 4.46112L6.27779 10.2945C6.06586 10.5438 5.69035 10.57 5.44578 10.3526L2.82078 8.01931C2.57999 7.80528 2.5583 7.43657 2.77233 7.19578C2.98637 6.95499 3.35508 6.9333 3.59587 7.14734L5.77511 9.08444L10.3472 3.70553Z',
          fill: color || EColorPalette.cGreen,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.HYPERLINK: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M4.22085 14.1209C3.43981 14.902 3.43981 16.1683 4.22085 16.9494L7.04928 19.7778C7.83033 20.5588 9.09666 20.5588 9.87771 19.7778L12.7061 16.9494C13.4872 16.1683 13.4872 14.902 12.7061 14.1209L11.999 13.4138L9.87771 15.5352C9.48718 15.9257 8.85402 15.9257 8.46349 15.5352C8.07297 15.1446 8.07297 14.5115 8.46349 14.1209L10.5848 11.9996L9.87771 11.2925C9.09666 10.5115 7.83033 10.5115 7.04928 11.2925L4.22085 14.1209Z',
          fill: color || EColorPalette.cGreen,
          opacity: 0.3,
        })}
        ${this.getPath({
          d: 'M11.2931 7.05063C10.5121 7.83168 10.5121 9.09801 11.2931 9.87906L12.0002 10.5862L14.1215 8.46484C14.5121 8.07432 15.1452 8.07432 15.5358 8.46484C15.9263 8.85537 15.9263 9.48853 15.5358 9.87906L13.4144 12.0004L14.1215 12.7075C14.9026 13.4885 16.1689 13.4885 16.95 12.7075L19.7784 9.87906C20.5594 9.09801 20.5594 7.83168 19.7784 7.05063L16.95 4.2222C16.1689 3.44115 14.9026 3.44115 14.1215 4.2222L11.2931 7.05063Z',
          fill: color || EColorPalette.cGreen,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.ARROW_TO_BOTTOM: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getRectangle({
          x: 11,
          y: 3,
          width: 2,
          height: 14,
          rx: 1,
          opacity: 0.3,
          fill: color || EColorPalette.cGreen,
        })}
        ${this.getPath({
          d: 'M6.70711 9.29289C6.31658 8.90237 5.68342 8.90237 5.29289 9.29289C4.90237 9.68342 4.90237 10.3166 5.29289 10.7071L11.2929 16.7071C11.6715 17.0857 12.2811 17.0989 12.6757 16.7372L18.6757 11.2372C19.0828 10.864 19.1103 10.2314 18.7372 9.82427C18.364 9.41716 17.7314 9.38965 17.3243 9.76284L12.0301 14.6159L6.70711 9.29289Z',
          fill: color || EColorPalette.cGreen,
        })}
        ${this.getRectangle({
          x: 3,
          y: 19,
          width: 18,
          height: 2,
          rx: 1,
          opacity: 0.3,
          fill: color || EColorPalette.cGreen,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.COMMENT: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M5 3C3.34315 3 2 4.34315 2 6V15C2 16.6569 3.34315 18 5 18H18.0249L20.3178 20.2929C20.9478 20.9229 22.0249 20.4767 22.0249 19.5858V15L22 15.0098C22 15.0065 22 15.0033 22 15V6C22 4.34315 20.6569 3 19 3H5Z',
          fill: color || EColorPalette.cGreen,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.COMMENT2: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 25 24')}
        ${this.getPath({
          d: 'M6.5 3C4.84315 3 3.5 4.34315 3.5 6V15C3.5 16.6569 4.84315 18 6.5 18H18.5249L20.8178 20.2929C21.4478 20.9229 22.5249 20.4767 22.5249 19.5858V15L22.5 15.0098C22.5 15.0065 22.5 15.0033 22.5 15V6C22.5 4.34315 21.1569 3 19.5 3H6.5Z',
          fill: color || EColorPalette.cGray4,
          opacity: 0.3,
        })}
       ${this.getPath({
         d: 'M8 12C7.17157 12 6.5 11.3284 6.5 10.5C6.5 9.67157 7.17157 9 8 9C8.82843 9 9.5 9.67157 9.5 10.5C9.5 11.3284 8.82843 12 8 12ZM13 12C12.1716 12 11.5 11.3284 11.5 10.5C11.5 9.67157 12.1716 9 13 9C13.8284 9 14.5 9.67157 14.5 10.5C14.5 11.3284 13.8284 12 13 12ZM18 12C17.1716 12 16.5 11.3284 16.5 10.5C16.5 9.67157 17.1716 9 18 9C18.8284 9 19.5 9.67157 19.5 10.5C19.5 11.3284 18.8284 12 18 12Z',
         fill: color || EColorPalette.cGray4,
         opacity: 0.3,
       })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.CHECKBOX_SELECTED: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 16 16')}
        ${this.getRectangle({
          width: 16,
          height: 16,
          fill: color || EColorPalette.cWhite,
        })}
        ${this.getPath({
          d: 'M0 2C0 0.895431 0.895431 0 2 0H14C15.1046 0 16 0.895431 16 2V14C16 15.1046 15.1046 16 14 16H2C0.895431 16 0 15.1046 0 14V2Z',
          fill: color || EColorPalette.cGreen,
        })}
        ${this.getPath({
          d: 'M11.8254 4.23489C12.0638 3.95435 12.4845 3.92024 12.7651 4.1587C13.0456 4.39715 13.0797 4.81788 12.8413 5.09842L7.17461 11.7651C6.93241 12.05 6.50325 12.08 6.22374 11.8316L3.22374 9.16493C2.94856 8.92032 2.92377 8.49894 3.16838 8.22375C3.41299 7.94856 3.83437 7.92377 4.10956 8.16838L6.60013 10.3822L11.8254 4.23489Z',
          fill: color || EColorPalette.cWhite,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.CHECKBOX_DEFAULT: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 16 16')}
        ${this.getRectangle({
          width: 16,
          height: 16,
          fill: EColorPalette.cWhite,
        })}
        ${this.getPath({
          d: 'M1 2C1 1.44772 1.44772 1 2 1H14C14.5523 1 15 1.44772 15 2V14C15 14.5523 14.5523 15 14 15H2C1.44772 15 1 14.5523 1 14V2Z',
          fill: EColorPalette.cWhite,
          stroke: color || EColorPalette.cGray7,
          strokeWidth: 2,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.SAVE: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M6 4H7V11H17V4L19.8536 6.85355C19.9473 6.94732 20 7.0745 20 7.20711V18C20 19.3 19.2 20 18 20H6C4.79111 20 4 19.3 4 18V6C4 4.7 4.79111 4 6 4Z',
          fill: color || EColorPalette.cGreen3,
        })}
        ${this.getRectangle({
          opacity: 0.3,
          x: 12,
          y: 4,
          width: 3,
          height: 5,
          rx: 0.5,
          fill: color || EColorPalette.cGreen3,
        })}

        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.TICKET: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M6.63595 11.0857L3.10042 14.6213C2.70989 15.0118 2.70989 15.645 3.10042 16.0355L4.54999 17.4851C5.52068 16.8419 6.84138 16.948 7.69661 17.8032C8.55184 18.6585 8.65791 19.9792 8.0148 20.9499L9.46438 22.3994C9.8549 22.79 10.4881 22.79 10.8786 22.3994L14.4141 18.8639L12.9999 17.4497C12.6094 17.0592 12.6094 16.426 12.9999 16.0355C13.3904 15.645 14.0236 15.645 14.4141 16.0355L15.8283 17.4497L22.8994 10.3786C23.2899 9.9881 23.2899 9.35493 22.8994 8.96441L21.4498 7.51483C20.4791 8.15794 19.1584 8.05187 18.3032 7.19664C17.448 6.34141 17.3419 5.02071 17.985 4.05002L16.5354 2.60045C16.1449 2.20992 15.5118 2.20992 15.1212 2.60045L8.05016 9.67151L9.46438 11.0857C9.8549 11.4763 9.8549 12.1094 9.46438 12.4999C9.07385 12.8905 8.44069 12.8905 8.05016 12.4999L6.63595 11.0857ZM10.1715 14.6213C9.78096 14.2307 9.78096 13.5976 10.1715 13.207C10.562 12.8165 11.1952 12.8165 11.5857 13.207L12.2928 13.9142C12.6833 14.3047 12.6833 14.9378 12.2928 15.3284C11.9023 15.7189 11.2691 15.7189 10.8786 15.3284L10.1715 14.6213Z',
          fill: color || EColorPalette.cGray4,
          opacity: 0.3,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.TICKET2: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M6.63595 11.0857L3.10042 14.6213C2.70989 15.0118 2.70989 15.645 3.10042 16.0355L4.54999 17.4851C5.52068 16.8419 6.84138 16.948 7.69661 17.8032C8.55184 18.6585 8.65791 19.9792 8.0148 20.9499L9.46438 22.3994C9.8549 22.79 10.4881 22.79 10.8786 22.3994L14.4141 18.8639L12.9999 17.4497C12.6094 17.0592 12.6094 16.426 12.9999 16.0355C13.3904 15.645 14.0236 15.645 14.4141 16.0355L15.8283 17.4497L22.8994 10.3786C23.2899 9.9881 23.2899 9.35493 22.8994 8.96441L21.4498 7.51483C20.4791 8.15794 19.1584 8.05187 18.3032 7.19664C17.448 6.34141 17.3419 5.02071 17.985 4.05002L16.5354 2.60045C16.1449 2.20992 15.5118 2.20992 15.1212 2.60045L8.05016 9.67151L9.46438 11.0857C9.8549 11.4763 9.8549 12.1094 9.46438 12.4999C9.07385 12.8905 8.44069 12.8905 8.05016 12.4999L6.63595 11.0857ZM10.1715 14.6213C9.78096 14.2307 9.78096 13.5976 10.1715 13.207C10.562 12.8165 11.1952 12.8165 11.5857 13.207L12.2928 13.9142C12.6833 14.3047 12.6833 14.9378 12.2928 15.3284C11.9023 15.7189 11.2691 15.7189 10.8786 15.3284L10.1715 14.6213Z',
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.MOVE_OUT: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M2.56068 10.682C1.97489 11.2678 1.97489 12.2175 2.56068 12.8033L4.682 14.9246C5.26778 15.5104 6.21753 15.5104 6.80332 14.9246L8.92464 12.8033C9.51043 12.2175 9.51043 11.2678 8.92464 10.682L6.80332 8.56065C6.21753 7.97486 5.26778 7.97486 4.682 8.56065L2.56068 10.682ZM14.5607 10.682C13.9749 11.2678 13.9749 12.2175 14.5607 12.8033L16.682 14.9246C17.2678 15.5104 18.2175 15.5104 18.8033 14.9246L20.9246 12.8033C21.5104 12.2175 21.5104 11.2678 20.9246 10.682L18.8033 8.56065C18.2175 7.97486 17.2678 7.97486 16.682 8.56065L14.5607 10.682Z',
          fill: color || EColorPalette.cGray4,
          opacity: 0.3,
        })}
        ${this.getPath({
          d: 'M8.56068 4.68197C7.97489 5.26775 7.97489 6.2175 8.56068 6.80329L10.682 8.92461C11.2678 9.51039 12.2175 9.51039 12.8033 8.92461L14.9246 6.80329C15.5104 6.2175 15.5104 5.26775 14.9246 4.68197L12.8033 2.56065C12.2175 1.97486 11.2678 1.97486 10.682 2.56065L8.56068 4.68197ZM8.56068 16.682C7.97489 17.2678 7.97489 18.2175 8.56068 18.8033L10.682 20.9246C11.2678 21.5104 12.2175 21.5104 12.8033 20.9246L14.9246 18.8033C15.5104 18.2175 15.5104 17.2678 14.9246 16.682L12.8033 14.5606C12.2175 13.9749 11.2678 13.9749 10.682 14.5606L8.56068 16.682Z',
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.HIDDEN: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M11.0955 17.9608C11.3879 17.9865 11.6893 18 12 18C16.909 18 21 12 21 12C21 12 20.3303 11.0179 19.2078 9.84839L11.0955 17.9608Z',
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getPath({
          d: 'M14.5051 6.49485C13.7076 6.18695 12.8665 6 12 6C5.45455 6 3 12 3 12C3 12 3.75006 13.8335 5.52661 15.4734L9 12C9 10.3431 10.3431 9 12 9L14.5051 6.49485Z',
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getRectangle({
          opacity: 0.3,
          x: 5.1001,
          y: 18.4351,
          width: 19,
          height: 2,
          rx: 1,
          fill: color || EColorPalette.cGray4,
          transform: 'rotate(-45 5.1001 18.4351)',
        })},
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.EXPAND_ARROWS: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M5.46452 17.1214C5.074 17.5119 5.074 18.1451 5.46452 18.5356C5.85505 18.9261 6.48821 18.9261 6.87874 18.5356L12.0001 13.4143L18.1924 19.6067C18.583 19.9972 19.2161 19.9972 19.6067 19.6067C19.9972 19.2162 19.9972 18.583 19.6067 18.1925L13.4143 12.0001L19.6067 5.80769C19.9972 5.41717 19.9972 4.784 19.6067 4.39348C19.2161 4.00295 18.583 4.00295 18.1924 4.39348L12.0001 10.5859L6.87874 5.46455C6.48821 5.07402 5.85505 5.07402 5.46452 5.46455C5.074 5.85507 5.074 6.48824 5.46452 6.87876L10.5858 12.0001L5.46452 17.1214Z',
          fill: color || EColorPalette.cGray4,
          opacity: 0.3,
        })}
        ${this.getPath({
          d: 'M18 6H15C15 6 14 5.82843 14 5C14 4.17157 15 4 15 4H20V9C20 9 20 10 19 10C18 10 18 9 18 9V6ZM9 18H6V15C6 15 6 14 5 14C4 14 4 15 4 15V20H9C9 20 10 19.8284 10 19C10 18.1716 9 18 9 18ZM18 15V18H15C15 18 14 18 14 19C14 20 15 20 15 20H20V15C20 15 19.8284 14 19 14C18.1716 14 18 15 18 15ZM6 9V6H9C9 6 10 6 10 5C10 4 9 4 9 4H4V9C4 9 4.17157 10 5 10C5.82843 10 6 9 6 9Z',
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }
      case EIcon.TRASH: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M7.39346 20.115L6.5 8.5H18.5L17.6065 20.115C17.5464 20.8965 16.8948 21.5 16.111 21.5H8.88905C8.10524 21.5 7.45358 20.8965 7.39346 20.115ZM8.95438 14.5894L8.5 10.5H16.5L16.0518 14.5339L8.95438 14.5894Z',
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getPath({
          d: 'M14.5 5V4C14.5 3.72386 14.2761 3.5 14 3.5H11C10.7239 3.5 10.5 3.72386 10.5 4V5H6C5.72386 5 5.5 5.22386 5.5 5.5V6C5.5 6.27614 5.72386 6.5 6 6.5H19C19.2761 6.5 19.5 6.27614 19.5 6V5.5C19.5 5.22386 19.2761 5 19 5H14.5Z',
          fill: color || EColorPalette.cGray4,
          opacity: 0.3,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.CHART_LINE: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M5.5 19H20.5C21.0523 19 21.5 19.4477 21.5 20C21.5 20.5523 21.0523 21 20.5 21H4.5C3.94772 21 3.5 20.5523 3.5 20V4C3.5 3.44772 3.94772 3 4.5 3C5.05228 3 5.5 3.44772 5.5 4V19Z',
          fill: color || EColorPalette.cGray4,
          opacity: 0.3,
        })}
        ${this.getPath({
          d: 'M9.22954 14.684C8.85181 15.0869 8.21897 15.1073 7.81606 14.7296C7.41315 14.3518 7.39274 13.719 7.77047 13.3161L11.5205 9.3161C11.8858 8.92644 12.4928 8.89264 12.8991 9.23935L15.8589 11.765L19.7151 6.8804C20.0573 6.44692 20.6862 6.37294 21.1196 6.71516C21.5531 7.05738 21.6271 7.68621 21.2849 8.11969L16.7849 13.8197C16.4334 14.2649 15.7824 14.3289 15.3509 13.9607L12.3268 11.3802L9.22954 14.684Z',
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.NOTIFICATIONS: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M11.5 3C9.01472 3 7 5.01472 7 7.5V12H5.5C4.67157 12 4 12.6716 4 13.5C4 14.3284 4.67157 15 5.5 15H18.5C19.3284 15 20 14.3284 20 13.5C20 12.6716 19.3284 12 18.5 12H17V7.5C17 5.01472 14.9853 3 12.5 3H11.5Z',
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getRectangle({
          opacity: 0.3,
          x: 10,
          y: 16,
          width: 4,
          height: 4,
          rx: 2,
          fill: color || EColorPalette.cGray4,
        })},
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.SETTINGS: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M18.6225 9.75H18.75C19.9926 9.75 21 10.7574 21 12C21 13.2426 19.9926 14.25 18.75 14.25L18.6855 14.25C18.4912 14.2508 18.3159 14.3669 18.2394 14.5454C18.1557 14.7351 18.1943 14.9481 18.3278 15.0847L18.3725 15.1294C18.795 15.5514 19.0324 16.1241 19.0324 16.7212C19.0324 17.3184 18.795 17.8911 18.3731 18.3125C17.9511 18.735 17.3784 18.9724 16.7812 18.9724C16.1841 18.9724 15.6114 18.735 15.1897 18.3128L15.1506 18.2736C15.0081 18.1343 14.7951 18.0957 14.6054 18.1794C14.4269 18.2559 14.3108 18.4312 14.31 18.6225V18.75C14.31 19.9926 13.3026 21 12.06 21C10.8174 21 9.81 19.9926 9.81 18.75C9.80552 18.4999 9.67899 18.323 9.44718 18.2361C9.26485 18.1557 9.05191 18.1943 8.91533 18.3278L8.87063 18.3725C8.4486 18.795 7.87592 19.0324 7.27875 19.0324C6.68158 19.0324 6.1089 18.795 5.68746 18.3731C5.26497 17.9511 5.02757 17.3784 5.02757 16.7812C5.02757 16.1841 5.26497 15.6114 5.68717 15.1897L5.72635 15.1506C5.86571 15.0081 5.90432 14.7951 5.82065 14.6054C5.7441 14.4269 5.56881 14.3108 5.3775 14.31H5.25C4.00736 14.31 3 13.3026 3 12.06C3 10.8174 4.00736 9.81 5.25 9.81C5.50008 9.80552 5.677 9.67899 5.76385 9.44718C5.84432 9.26485 5.80571 9.05191 5.67217 8.91533L5.62747 8.87063C5.20497 8.4486 4.96757 7.87592 4.96757 7.27875C4.96757 6.68158 5.20497 6.1089 5.62687 5.68746C6.0489 5.26497 6.62158 5.02757 7.21875 5.02757C7.81592 5.02757 8.3886 5.26497 8.81033 5.68717L8.84945 5.72635C8.99191 5.86571 9.20485 5.90432 9.38718 5.82385L9.49485 5.80115C9.65041 5.71689 9.74929 5.55401 9.75 5.3775V5.25C9.75 4.00736 10.7574 3 12 3C13.2426 3 14.25 4.00736 14.25 5.25L14.25 5.31451C14.2508 5.50881 14.3669 5.6841 14.5528 5.76385C14.7351 5.84432 14.9481 5.80571 15.0847 5.67217L15.1294 5.62747C15.5514 5.20497 16.1241 4.96757 16.7212 4.96757C17.3184 4.96757 17.8911 5.20497 18.3125 5.62687C18.735 6.0489 18.9724 6.62158 18.9724 7.21875C18.9724 7.81592 18.735 8.3886 18.3128 8.81033L18.2736 8.84945C18.1343 8.99191 18.0957 9.20485 18.1761 9.38718L18.1989 9.49485C18.2831 9.65041 18.446 9.74929 18.6225 9.75Z',
          fill: color || EColorPalette.cGray4,
          opacity: 0.3,
        })}
        ${this.getPath({
          d: 'M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z',
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.SETTINGS2: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M1.68628 12.0001L4.99999 8.68637V5.00008H8.68628L11.5858 2.10059L14.4853 5.00008H19V9.5148L21.4853 12.0001L19 14.4854V19.0001H14.4853L11.5858 21.8996L8.68628 19.0001H4.99999V15.3138L1.68628 12.0001ZM15 12.0001C15 13.6569 13.6568 15.0001 12 15.0001C10.3431 15.0001 8.99999 13.6569 8.99999 12.0001C8.99999 10.3432 10.3431 9.00008 12 9.00008C13.6568 9.00008 15 10.3432 15 12.0001Z',
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.SETTINGS3: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 20 20')}
        ${this.getPath({
          d: 'M7.70833 18.3307L7.375 15.6641C7.19444 15.5946 7.02431 15.5113 6.86458 15.4141C6.70486 15.3168 6.54861 15.2127 6.39583 15.1016L3.91667 16.1432L1.625 12.1849L3.77083 10.5599C3.75694 10.4627 3.75 10.3689 3.75 10.2786V9.71615C3.75 9.62587 3.75694 9.53212 3.77083 9.4349L1.625 7.8099L3.91667 3.85156L6.39583 4.89323C6.54861 4.78212 6.70833 4.67795 6.875 4.58073C7.04167 4.48351 7.20833 4.40017 7.375 4.33073L7.70833 1.66406H12.2917L12.625 4.33073C12.8056 4.40017 12.9757 4.48351 13.1354 4.58073C13.2951 4.67795 13.4514 4.78212 13.6042 4.89323L16.0833 3.85156L18.375 7.8099L16.2292 9.4349C16.2431 9.53212 16.25 9.62587 16.25 9.71615V10.2786C16.25 10.3689 16.2361 10.4627 16.2083 10.5599L18.3542 12.1849L16.0625 16.1432L13.6042 15.1016C13.4514 15.2127 13.2917 15.3168 13.125 15.4141C12.9583 15.5113 12.7917 15.5946 12.625 15.6641L12.2917 18.3307H7.70833Z',
          fill: color || EColorPalette.cGray2,
        })}
        ${this.getPath({
          d: 'M10.0417 12.9141C10.8472 12.9141 11.5347 12.6293 12.1042 12.0599C12.6736 11.4905 12.9583 10.803 12.9583 9.9974C12.9583 9.19184 12.6736 8.50434 12.1042 7.9349C11.5347 7.36545 10.8472 7.08073 10.0417 7.08073C9.22222 7.08073 8.53125 7.36545 7.96875 7.9349C7.40625 8.50434 7.125 9.19184 7.125 9.9974C7.125 10.803 7.40625 11.4905 7.96875 12.0599C8.53125 12.6293 9.22222 12.9141 10.0417 12.9141Z',
          fill: secondColor || EColorPalette.cWhite,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.EXPAND: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M11 6H18V13C18 13 18 14 19 14C20 14 20 13 20 13V4H11C11 4 10 4.17157 10 5C10 5.82843 11 6 11 6ZM13 18H6V11C6 11 6 10 5 10C4 10 4 11 4 11V20H13C13 20 14 19.8284 14 19C14 18.1716 13 18 13 18Z',
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getRectangle({
          opacity: 0.3,
          x: 7.75732,
          y: 14.8284,
          width: 10,
          height: 2,
          rx: 1,
          transform: 'rotate(-45 7.75732 14.8284)',
          fill: color || EColorPalette.cGray4,
        })},
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.COLLAPSE: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M20 9H15V4C15 4 15 3 14 3C13 3 13 4 13 4V11H20C20 11 21 10.8284 21 10C21 9.17157 20 9 20 9ZM5 14H10V19C10 19 10 20 11 20C12 20 12 19 12 19V12H5C5 12 4 12.1716 4 13C4 13.8284 5 14 5 14Z',
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getPath({
          d: 'M16.8786 5.70701C16.4881 6.09754 16.4881 6.7307 16.8786 7.12123C17.2691 7.51175 17.9023 7.51175 18.2928 7.12123L21.1212 4.2928C21.5118 3.90228 21.5118 3.26911 21.1212 2.87859C20.7307 2.48806 20.0975 2.48806 19.707 2.87859L16.8786 5.70701ZM3.87859 18.707C3.48806 19.0975 3.48806 19.7307 3.87859 20.1212C4.26911 20.5118 4.90228 20.5118 5.2928 20.1212L8.12123 17.2928C8.51175 16.9023 8.51175 16.2691 8.12123 15.8786C7.7307 15.4881 7.09754 15.4881 6.70701 15.8786L3.87859 18.707Z',
          fill: color || EColorPalette.cGray4,
          opacity: 0.3,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.BLOCKS: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 25 24')}
        ${this.getRectangle({
          x: 4.5,
          y: 4,
          width: 7,
          height: 7,
          rx: 1.5,
          fill: color || EColorPalette.cGray4,
        })},
        ${this.getPath({
          d: 'M13.5 5.5C13.5 4.67157 14.1716 4 15 4H19C19.8284 4 20.5 4.67157 20.5 5.5V9.5C20.5 10.3284 19.8284 11 19 11H15C14.1716 11 13.5 10.3284 13.5 9.5V5.5ZM4.5 14.5C4.5 13.6716 5.17157 13 6 13H10C10.8284 13 11.5 13.6716 11.5 14.5V18.5C11.5 19.3284 10.8284 20 10 20H6C5.17157 20 4.5 19.3284 4.5 18.5V14.5ZM15 13C14.1716 13 13.5 13.6716 13.5 14.5V18.5C13.5 19.3284 14.1716 20 15 20H19C19.8284 20 20.5 19.3284 20.5 18.5V14.5C20.5 13.6716 19.8284 13 19 13H15Z',
          fill: color || EColorPalette.cGray4,
          opacity: 0.3,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.MENU: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 25 24')}
        ${this.getRectangle({
          x: 4.5,
          y: 5,
          width: 16,
          height: 3,
          rx: 1.5,
          fill: color || EColorPalette.cGray4,
        })},
        ${this.getPath({
          d: 'M6 10C5.17157 10 4.5 10.6716 4.5 11.5C4.5 12.3284 5.17157 13 6 13H19C19.8284 13 20.5 12.3284 20.5 11.5C20.5 10.6716 19.8284 10 19 10H6ZM6 15C5.17157 15 4.5 15.6716 4.5 16.5C4.5 17.3284 5.17157 18 6 18H19C19.8284 18 20.5 17.3284 20.5 16.5C20.5 15.6716 19.8284 15 19 15H6Z',
          fill: color || EColorPalette.cGray4,
          opacity: 0.3,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.BULB: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 25 24')}
        ${this.getPath({
          d: 'M12 17C16.4183 17 20 13.4183 20 9C20 4.58172 16.4183 1 12 1C7.58172 1 4 4.58172 4 9C4 13.4183 7.58172 17 12 17Z',
          fill: color || EColorPalette.cGray4,
          opacity: 0.3,
        })}
        ${this.getPath({
          d: 'M6.53809 9L10.5679 19.3624L11.9759 18.8149L13.3744 19.3674L17.4702 9H6.53809ZM9.46178 11H14.5297L11.9758 17.4645L9.46178 11Z',
          fill: EColorPalette.cGray4,
          opacity: 0.3,
        })}
        ${this.getPath({
          d: 'M10 22H14C14 23.1046 13.1046 24 12 24C10.8954 24 10 23.1046 10 22Z',
          fill: EColorPalette.cGray4,
          opacity: 0.3,
        })}
        ${this.getPath({
          d: 'M8 17C8 16.4477 8.44772 16 9 16H15C15.5523 16 16 16.4477 16 17C16 17.5523 15.5523 18 15 18C15.5523 18 16 18.4477 16 19C16 19.5523 15.5523 20 15 20C15.5523 20 16 20.4477 16 21C16 21.5523 15.5523 22 15 22H9C8.44772 22 8 21.5523 8 21C8 20.4477 8.44772 20 9 20C8.44772 20 8 19.5523 8 19C8 18.4477 8.44772 18 9 18C8.44772 18 8 17.5523 8 17Z',
          fill: EColorPalette.cGray4,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.BUG: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M1 15.8285C0.447715 15.8285 -1.10011e-07 15.3808 -1.22081e-07 14.8285C-1.34152e-07 14.2762 0.447715 13.8285 1 13.8285L3 13.8285C3.55228 13.8285 4 14.2762 4 14.8285C4 15.3808 3.55228 15.8285 3 15.8285L1 15.8285ZM1.70711 10.7072C1.31658 10.3167 1.31658 9.68351 1.70711 9.29299C2.09763 8.90246 2.7308 8.90246 3.12132 9.29299L4.53553 10.7072C4.92606 11.0977 4.92606 11.7309 4.53553 12.1214C4.14501 12.5119 3.51184 12.5119 3.12132 12.1214L1.70711 10.7072ZM1.70711 20.1214C1.31658 19.7309 1.31658 19.0977 1.70711 18.7072L3.12132 17.293C3.51184 16.9025 4.14501 16.9025 4.53553 17.293C4.92606 17.6835 4.92606 18.3167 4.53553 18.7072L3.12132 20.1214C2.7308 20.5119 2.09763 20.5119 1.70711 20.1214Z',
          fill: color || EColorPalette.cGray4,
          opacity: 0.3,
        })}
        ${this.getRectangle({
          width: 2,
          height: 4,
          rx: 1,
          fill: color || EColorPalette.cGray4,
          transform: 'matrix(0.917288 -0.398225 -0.398225 -0.917288 8.59302 5.46558)',
          opacity: 0.3,
        })},
        ${this.getRectangle({
          opacity: 0.3,
          x: 14.8345,
          y: 5.46558,
          width: 2,
          height: 4,
          rx: 1,
          transform: 'rotate(-156.533 14.8345 5.46558)',
          fill: color || EColorPalette.cGray4,
        })},

        ${this.getPath({
          d: 'M22.1213 10.7071C22.5118 10.3166 22.5118 9.68342 22.1213 9.29289C21.7308 8.90237 21.0976 8.90237 20.7071 9.29289L19.2929 10.7071C18.9024 11.0976 18.9024 11.7308 19.2929 12.1213C19.6834 12.5118 20.3166 12.5118 20.7071 12.1213L22.1213 10.7071ZM22.8284 15.8286C23.3807 15.8286 23.8284 15.3809 23.8284 14.8286C23.8284 14.2763 23.3807 13.8286 22.8284 13.8286H20.8284C20.2761 13.8286 19.8284 14.2763 19.8284 14.8286C19.8284 15.3809 20.2761 15.8286 20.8284 15.8286H22.8284ZM22.1213 20.1214C22.5118 19.7309 22.5118 19.0977 22.1213 18.7072L20.7071 17.293C20.3166 16.9025 19.6834 16.9025 19.2929 17.293C18.9024 17.6835 18.9024 18.3167 19.2929 18.7072L20.7071 20.1214C21.0976 20.5119 21.7308 20.5119 22.1213 20.1214Z',
          fill: EColorPalette.cGray4,
          opacity: 0.3,
        })}
        ${this.getPath({
          d: 'M6 12H18V17C18 20.3137 15.3137 23 12 23C8.68629 23 6 20.3137 6 17V12Z',
          fill: EColorPalette.cGray4,
          opacity: 0.3,
        })}
        ${this.getPath({
          d: 'M6 10C6 6.68629 8.68629 4 12 4C15.3137 4 18 6.68629 18 10V11H6V10Z',
          fill: EColorPalette.cGray4,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.DOLLAR_CIRCLE: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 706 548')}
        <g transform="translate(0.000000,548.000000) scale(0.100000,-0.100000)" fill="${color}" stroke="none">
        <path d="M3305 5450 c-3 -5 -34 -10 -68 -10 -62 0 -214 -23 -273 -41 -17 -5 -39 -9 -49 -9 -10 0 -32 -4 -49 -10 -17 -5 -89 -28 -161 -50 -71 -22 -139 -44 -150 -49 -11 -5 -51 -21 -90 -37 -65 -26 -193 -89 -290 -143 -40 -23 -266 -171 -275 -181 -3 -3 -39 -32 -80 -64 -97 -76 -320 -299 -396 -396 -32 -41 -61 -77 -64 -80 -10 -9 -158 -235 -181 -275 -54 -97 -117 -225 -143 -290 -16 -38 -32 -79 -37 -90 -5 -11 -27 -78 -49 -150 -22 -71 -45 -144 -50 -161 -6 -17 -10 -39 -10 -49 0 -10 -4 -32 -9 -49 -18 -59 -41 -211 -41 -273 0 -34 -4 -65 -10 -68 -14 -8 -14 -422 0 -430 6 -3 10 -34 10 -68 0 -62 23 -214 41 -273 5 -17 9 -39 9 -49 0 -10 4 -32 10 -49 5 -17 28 -89 50 -161 22 -71 44 -139 49 -150 5 -11 21 -51 37 -90 26 -65 89 -193 143 -290 23 -40 171 -266 181 -275 3 -3 32 -39 64 -80 76 -97 299 -320 396 -396 41 -32 77 -61 80 -64 9 -10 235 -158 275 -181 97 -54 225 -117 290 -143 39 -16 79 -32 90 -37 11 -5 79 -27 150 -49 72 -22 144 -45 161 -50 17 -6 39 -10 49 -10 10 0 32 -4 49 -9 59 -18 211 -41 273 -41 34 0 65 -4 68 -10 8 -14 422 -14 430 0 3 6 34 10 68 10 62 0 214 23 273 41 17 5 39 9 49 9 10 0 32 4 49 10 17 5 90 28 161 50 72 22 139 44 150 49 11 5 52 21 90 37 65 26 193 89 290 143 40 23 266 171 275 181 3 3 39 32 80 64 97 76 320 299 396 396 32 41 61 77 64 80 10 9 158 235 181 275 54 97 117 225 143 290 16 39 32 79 37 90 5 11 27 79 49 150 22 72 45 144 50 161 6 17 10 39 10 49 0 10 4 32 9 49 18 59 41 211 41 273 0 34 5 65 10 68 6 4 10 87 10 215 0 128 -4 211 -10 215 -5 3 -10 34 -10 68 0 62 -23 214 -41 273 -5 17 -9 39 -9 49 0 10 -4 32 -10 49 -5 17 -28 90 -50 161 -22 72 -44 139 -49 150 -5 11 -21 52 -37 90 -26 65 -89 193 -143 290 -23 40 -171 266 -181 275 -3 3 -32 39 -64 80 -76 97 -299 320 -396 396 -41 32 -77 61 -80 64 -9 10 -235 158 -275 181 -97 54 -225 117 -290 143 -38 16 -79 32 -90 37 -11 5 -78 27 -150 49 -71 22 -144 45 -161 50 -17 6 -39 10 -49 10 -10 0 -32 4 -49 9 -59 18 -211 41 -273 41 -34 0 -65 5 -68 10 -4 6 -87 10 -215 10 -128 0 -211 -4 -215 -10z m420 -330 c190 -16 283 -33 480 -90 86 -25 194 -64 270 -99 165 -77 219 -106 302 -162 23 -16 44 -29 48 -29 10 0 201 -145 255 -194 58 -52 174 -168 226 -226 49 -54 194 -245 194 -255 0 -4 13 -25 29 -48 39 -57 83 -133 108 -187 11 -25 32 -70 46 -100 59 -126 89 -214 142 -415 32 -124 43 -197 57 -398 16 -240 -13 -588 -61 -737 -5 -14 -14 -45 -21 -70 -28 -102 -72 -224 -109 -305 -68 -146 -89 -185 -184 -340 -20 -34 -157 -215 -190 -254 -41 -47 -201 -207 -248 -248 -39 -33 -220 -170 -254 -190 -107 -66 -188 -112 -225 -130 -134 -62 -160 -74 -210 -93 -63 -25 -145 -52 -210 -70 -25 -7 -56 -16 -70 -21 -149 -48 -498 -77 -738 -60 -202 14 -340 33 -422 60 -14 5 -45 14 -70 21 -112 32 -225 73 -305 109 -146 68 -185 89 -340 184 -34 20 -215 157 -254 190 -47 41 -207 201 -248 248 -33 39 -170 220 -190 254 -66 107 -112 188 -130 225 -62 134 -74 160 -93 210 -25 63 -52 145 -70 210 -7 25 -16 56 -21 70 -48 149 -77 498 -60 737 14 203 33 341 60 423 5 14 14 45 21 70 32 112 73 225 109 305 77 165 106 219 162 302 16 23 29 44 29 48 0 10 145 201 194 255 52 58 168 174 226 226 54 49 245 194 255 194 4 0 25 13 48 29 83 56 137 85 302 162 113 52 208 84 400 134 214 56 499 76 760 55z"/> <path d="M3415 4888 c-3 -7 -6 -76 -7 -153 l-3 -140 -125 -16 c-129 -16 -153 -21 -230 -45 -254 -80 -465 -285 -572 -554 -38 -97 -49 -153 -55 -280 -15 -349 96 -586 350 -753 124 -81 224 -125 422 -186 17 -5 64 -17 105 -26 41 -9 82 -20 90 -24 13 -7 15 -91 18 -645 l2 -636 -30 0 c-76 0 -252 58 -336 112 -149 94 -221 224 -263 479 -6 37 -11 85 -11 107 0 76 11 72 -211 72 -177 0 -200 -2 -206 -17 -7 -17 10 -217 26 -313 54 -320 220 -539 515 -678 98 -46 304 -102 376 -102 21 0 62 -5 89 -11 l51 -11 2 -221 3 -222 108 -3 107 -3 0 221 0 221 35 15 c19 7 54 14 78 14 24 0 57 4 73 9 16 5 63 17 104 26 413 92 678 353 740 730 44 263 37 443 -22 611 -45 127 -184 297 -288 353 -179 97 -348 165 -575 230 -16 4 -56 17 -87 27 l-58 18 0 568 0 568 51 0 c114 0 254 -59 342 -143 52 -51 67 -71 105 -139 31 -56 67 -172 74 -238 l3 -25 203 -3 202 -2 0 42 c0 95 -34 232 -88 358 -22 52 -105 168 -158 223 -110 114 -293 213 -454 248 -33 7 -244 44 -277 49 -2 0 -3 68 -3 150 l0 150 -105 0 c-75 0 -107 -4 -110 -12z m-7 -1200 c1 -345 -1 -548 -7 -548 -15 0 -110 26 -165 45 -155 53 -301 167 -348 271 -14 33 -31 77 -38 99 -13 47 -9 220 8 280 16 58 77 174 120 227 37 47 148 128 174 128 8 0 18 4 24 8 5 5 34 14 64 20 30 7 64 14 75 16 11 2 36 3 55 3 l35 -2 3 -547z m274 -1047 c13 -5 50 -17 83 -27 33 -10 64 -22 70 -26 5 -4 15 -8 22 -8 18 0 120 -49 178 -86 227 -146 301 -464 173 -742 -29 -62 -99 -153 -150 -195 -47 -39 -155 -93 -216 -109 -57 -15 -182 -30 -198 -24 -12 5 -14 99 -14 609 0 332 3 607 7 610 9 9 18 9 45 -2z"/>
        ${this.getEndUrlClip()}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.LOGO: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 1004 788')}
        ${this.getPath({
          d: 'M226.508 517.964C245.214 517.964 255.95 526.977 255.95 542.726C255.95 559.095 245.214 568.625 226.508 568.625H209.398V590.482H196.853V517.964H226.508ZM209.398 557.642H225.976C237.455 557.642 243.938 552.876 243.938 543.034C243.938 533.503 237.457 528.948 225.976 528.948H209.395L209.398 557.642Z',
          fill: color || EColorPalette.cBlack,
        })}
        ${this.getPath({
          d: 'M334.812 568.519C333.749 568.622 332.581 568.622 331.411 568.622H313.768V590.482H301.226V517.964H331.411C350.437 517.964 361.384 526.977 361.384 542.726C361.384 554.33 355.858 562.514 345.973 566.243L362.66 590.482H348.417L334.812 568.519ZM331.411 557.642C342.89 557.642 349.481 552.875 349.481 543.034C349.481 533.503 342.89 528.947 331.411 528.947H313.768V557.644L331.411 557.642Z',
          fill: color || EColorPalette.cBlack,
        })}
        ${this.getPath({
          d: 'M483.614 554.223C483.614 574.942 466.395 591 444.18 591C421.966 591 404.748 574.942 404.748 554.223C404.748 533.399 421.967 517.652 444.18 517.652C466.393 517.652 483.614 533.503 483.614 554.223ZM417.502 554.223C417.502 568.622 429.725 580.019 444.286 580.019C451.265 580.027 457.961 577.338 462.909 572.542C467.856 567.745 470.65 561.232 470.679 554.43C470.707 547.629 467.967 541.094 463.06 536.258C458.153 531.422 451.479 528.68 444.501 528.633H444.286C429.619 528.633 417.502 539.82 417.502 554.223Z',
          fill: color || EColorPalette.cBlack,
        })}
        ${this.getPath({
          d: 'M559.501 517.964C578.208 517.964 588.943 526.977 588.943 542.726C588.943 559.095 578.208 568.625 559.501 568.625H542.392V590.482H529.847V517.964H559.501ZM542.392 557.642H558.972C570.451 557.642 576.935 552.876 576.935 543.034C576.935 533.503 570.454 528.948 558.972 528.948H542.389L542.392 557.642Z',
          fill: color || EColorPalette.cBlack,
        })}
        ${this.getPath({
          d: 'M696.504 560.645C696.504 579.501 684.812 590.896 665.361 590.896C645.805 590.896 634.006 579.501 634.006 560.645V517.964H642.51V560.646C642.51 574.733 651.225 582.92 665.361 582.92C679.497 582.92 688.001 574.736 688.001 560.646V517.964H696.504V560.645Z',
          fill: color || EColorPalette.cBlack,
        })}
        ${this.getPath({
          d: 'M778.13 517.964C796.518 517.964 807.147 526.871 807.147 542.309C807.147 558.367 796.518 567.588 778.13 567.588H758.682V590.482H750.179V517.964H778.13ZM758.682 559.609H777.814C791.206 559.609 798.965 553.808 798.965 542.516C798.965 531.534 791.206 525.94 777.814 525.94H758.682V559.609Z',
          fill: color || EColorPalette.cBlack,
        })}
        ${this.getPath({
          d: 'M604.941 309.075V328.658H635.576L579.802 374.02V197H431.221V297.938L383.159 311.987V424.991L365.875 439.135C360.602 436.458 354.542 435.642 348.721 436.823C342.901 438.004 337.679 441.11 333.94 445.615C330.2 450.12 328.174 455.746 328.204 461.541C328.234 467.335 330.319 472.941 334.105 477.409C337.891 481.877 343.145 484.931 348.977 486.055C354.809 487.179 360.861 486.302 366.106 483.574C371.351 480.846 375.467 476.435 377.756 471.087C380.046 465.739 380.368 459.785 378.668 454.232L441.273 403.008L508.26 457.719L649.926 342.485V372.579H670.017V309.075H604.941ZM353.936 472.684C351.653 472.683 349.421 472.022 347.523 470.786C345.625 469.549 344.146 467.791 343.273 465.735C342.399 463.679 342.171 461.416 342.617 459.234C343.062 457.051 344.162 455.046 345.776 453.472C347.391 451.899 349.448 450.827 351.687 450.393C353.927 449.959 356.248 450.181 358.357 451.033C360.467 451.884 362.27 453.326 363.539 455.176C364.808 457.026 365.485 459.202 365.486 461.427C365.486 462.905 365.188 464.369 364.608 465.735C364.027 467.101 363.177 468.342 362.104 469.388C361.031 470.433 359.758 471.262 358.357 471.828C356.955 472.393 355.453 472.684 353.936 472.684ZM498.311 424.043L441.26 377.45L403.249 408.548V326.554L498.311 298.766V424.043ZM559.711 390.361L518.402 423.961V272.45L451.312 292.064V216.583H559.711V390.361Z',
          fill: color || EColorPalette.cBlack,
        })}

        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.EXCHANGE: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 17 16')}
        ${this.getRectangle({
          x: 5.04688,
          y: 4.66663,
          width: 1.33333,
          height: 8,
          rx: 0.666667,
          transform: 'rotate(-90 5.04688 4.66663)',
          fill: color || EColorPalette.cGray4,
        })},
        ${this.getPath({
          d: 'M6.85161 5.52864C7.11196 5.78899 7.11196 6.2111 6.85161 6.47144C6.59126 6.73179 6.16915 6.73179 5.9088 6.47144L3.9088 4.47144C3.64845 4.2111 3.64845 3.78899 3.9088 3.52864L5.9088 1.52864C6.16915 1.26829 6.59126 1.26829 6.85161 1.52864C7.11196 1.78899 7.11196 2.2111 6.85161 2.47145L5.32302 4.00004L6.85161 5.52864Z',
          fill: color || EColorPalette.cBlack,
        })}
        ${this.getRectangle({
          width: 1.33333,
          height: 8,
          rx: 0.666667,
          transform: 'matrix(0 -1 -1 0 11.7129 12.6666)',
          fill: color || EColorPalette.cGray4,
        })},
        ${this.getPath({
          d: 'M9.90815 13.5286C9.6478 13.789 9.6478 14.2111 9.90815 14.4714C10.1685 14.7318 10.5906 14.7318 10.851 14.4714L12.851 12.4714C13.1113 12.2111 13.1113 11.789 12.851 11.5286L10.851 9.52864C10.5906 9.26829 10.1685 9.26829 9.90815 9.52864C9.6478 9.78899 9.6478 10.2111 9.90815 10.4714L11.4367 12L9.90815 13.5286Z',
          fill: color || EColorPalette.cBlack,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }
      case EIcon.SENDING: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 25 24')}
        ${this.getPath({
          d: 'M8.5 5.77181C8.5 5.40898 8.87431 5.16696 9.20518 5.31585L23.0456 11.544C23.4386 11.7209 23.4386 12.279 23.0456 12.4559L9.20518 18.6841C8.87431 18.833 8.5 18.591 8.5 18.2281V13.1669L20.947 12L8.5 10.8331V5.77181Z',
          fill: color || EColorPalette.cBlack,
        })}
        ${this.getPath({
          d: 'M4.5 6C3.94772 6 3.5 6.44772 3.5 7C3.5 7.55228 3.94772 8 4.5 8H5.5C6.05228 8 6.5 7.55228 6.5 7C6.5 6.44772 6.05228 6 5.5 6H4.5ZM0.5 12C0.5 11.4477 0.947715 11 1.5 11H5.5C6.05228 11 6.5 11.4477 6.5 12C6.5 12.5523 6.05228 13 5.5 13H1.5C0.947715 13 0.5 12.5523 0.5 12ZM3.5 17C3.5 16.4477 3.94772 16 4.5 16H5.5C6.05228 16 6.5 16.4477 6.5 17C6.5 17.5523 6.05228 18 5.5 18H4.5C3.94772 18 3.5 17.5523 3.5 17Z',
          fill: color || EColorPalette.cBlack,
          opacity: 0.3,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.REST: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 25 24')}
        ${this.getPath({
          d: 'M19.9607 2.91959C19.6133 2.8466 19.2593 2.81021 18.9044 2.81097V2.79359C18.3317 2.7931 17.7631 2.8901 17.223 3.08035C15.3893 0.963928 12.187 0.734737 10.0706 2.56842C9.65627 2.92741 9.30293 3.3512 9.02426 3.82332C6.24547 3.42262 3.668 5.35036 3.2673 8.12916C2.8666 10.908 4.79435 13.4854 7.57314 13.8861C7.82351 13.9222 8.07616 13.9396 8.32909 13.9381H8.51591C9.85092 16.4024 12.9309 17.3179 15.3953 15.9829C16.6096 15.3251 17.5034 14.2007 17.8703 12.8693C20.6179 13.4465 23.3131 11.6871 23.8904 8.93958C24.4676 6.19212 22.7083 3.49686 19.9607 2.91959ZM11.0098 10.1929C10.9179 10.2629 10.8036 10.2969 10.6883 10.2885H8.29867C8.16176 10.299 8.02582 10.2588 7.91654 10.1757C7.8341 10.0983 7.78935 9.98886 7.79467 9.87573C7.79712 9.79144 7.82775 9.71035 7.88157 9.64545C7.93952 9.56724 8.06117 9.4224 8.24653 9.21097L8.78095 8.62007L9.21543 8.12476L9.55867 7.73372C9.64992 7.6251 9.72378 7.53386 9.77592 7.46H8.45943C8.31296 7.4682 8.16615 7.45207 8.02495 7.41221C7.93224 7.38033 7.88591 7.2949 7.88591 7.15586C7.88206 7.06098 7.92214 6.96963 7.99453 6.90821C8.08078 6.84053 8.1894 6.80789 8.29867 6.81696H10.3538C10.5031 6.80898 10.6522 6.83728 10.7883 6.89952C10.8929 6.96143 10.9522 7.07831 10.9403 7.19931C10.9389 7.24993 10.9286 7.29995 10.9099 7.34703C10.894 7.39179 10.8721 7.43415 10.8447 7.47303L10.7448 7.59469L10.5927 7.76414L8.9504 9.58897H10.6883C10.8043 9.58006 10.9193 9.6158 11.0098 9.6889C11.0816 9.75451 11.1212 9.84808 11.1185 9.94524C11.1217 10.04 11.0817 10.131 11.0098 10.1929ZM14.7116 12.1741C14.638 12.2291 14.5469 12.2553 14.4553 12.248H12.5219C12.4157 12.2632 12.3077 12.2386 12.2185 12.1789C12.0963 12.0656 12.0839 11.8762 12.1916 11.7483C12.238 11.6875 12.335 11.573 12.4827 11.4051L12.9172 10.9315L13.2735 10.5361L13.5472 10.2233C13.6211 10.1364 13.6776 10.0625 13.721 10.0017H12.6479C12.5364 10.0058 12.425 9.9926 12.3176 9.96257C12.2438 9.9365 12.2047 9.86698 12.2047 9.75402C12.2019 9.67891 12.234 9.60678 12.2916 9.5585C12.361 9.50517 12.4475 9.47888 12.5349 9.48464H14.1642C14.2836 9.4779 14.4029 9.50028 14.5118 9.54981C14.5957 9.59874 14.6433 9.69216 14.6334 9.78878C14.6334 9.82886 14.626 9.86861 14.6117 9.90609C14.5987 9.9415 14.5812 9.97512 14.5596 10.006L14.4814 10.1016L14.3597 10.2363L13.0563 11.6962H14.4553C14.5476 11.6888 14.6392 11.7167 14.7116 11.7744C14.7686 11.8268 14.8003 11.9012 14.7985 11.9786C14.8001 12.0535 14.7682 12.1251 14.7116 12.1741ZM19.0608 9.96257C18.9689 10.0326 18.8546 10.0666 18.7393 10.0582H16.3105C16.1736 10.0686 16.0375 10.0284 15.9282 9.94519C15.843 9.86763 15.7968 9.75614 15.8022 9.64105C15.8046 9.54916 15.8402 9.46123 15.9021 9.3934C15.9601 9.31519 16.0817 9.17034 16.2671 8.95891L16.8015 8.36367L17.236 7.86836L17.5792 7.47732C17.669 7.37017 17.7415 7.27893 17.7965 7.2036H16.4713C16.3248 7.21158 16.178 7.19545 16.0368 7.15581C15.9412 7.12105 15.8978 7.0385 15.8978 6.89512C15.8939 6.80024 15.934 6.70889 16.0064 6.64746C16.0927 6.57979 16.2013 6.54715 16.3105 6.55622H18.3787C18.5281 6.54802 18.6772 6.57637 18.8132 6.63877C18.9178 6.70068 18.9771 6.81756 18.9652 6.93856C18.9638 6.98918 18.9535 7.0392 18.9348 7.08629C18.9188 7.13099 18.8969 7.17335 18.8697 7.21229L18.7697 7.3296L18.6177 7.49905L16.9797 9.32822H18.7176C18.8336 9.31932 18.9486 9.35505 19.0391 9.42815C19.1108 9.49376 19.1505 9.58734 19.1477 9.6845C19.1648 9.78443 19.1325 9.88648 19.0608 9.95822V9.96257Z',
          fill: color || EColorPalette.cBlack,
        })}
        ${this.getPath({
          d: 'M6.26093 19.8285C7.6191 19.8285 8.72011 18.7275 8.72011 17.3693C8.72011 16.0112 7.6191 14.9102 6.26093 14.9102C4.90277 14.9102 3.80176 16.0112 3.80176 17.3693C3.80176 18.7275 4.90277 19.8285 6.26093 19.8285Z',
          fill: color || EColorPalette.cBlack,
        })}
        ${this.getPath({
          d: 'M1.85524 22.6714C2.87987 22.6714 3.71049 21.8408 3.71049 20.8162C3.71049 19.7916 2.87987 18.9609 1.85524 18.9609C0.830621 18.9609 0 19.7916 0 20.8162C0 21.8408 0.830621 22.6714 1.85524 22.6714Z',
          fill: color || EColorPalette.cBlack,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.HAMMER: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 25 24')}
        ${this.getPath({
          d: 'M18.9246 12.6464L21.7531 9.81798C21.9483 9.62272 22.2649 9.62272 22.4602 9.81798L23.1673 10.5251C23.3625 10.7204 23.3625 11.0369 23.1673 11.2322L20.3389 14.0606C20.1436 14.2559 19.827 14.2559 19.6318 14.0606L18.9246 13.3535C18.7294 13.1583 18.7294 12.8417 18.9246 12.6464ZM3.72185 17.9497L13.6213 8.05022C14.0119 7.65969 14.645 7.65969 15.0356 8.05022L16.4498 9.46443C16.8403 9.85496 16.8403 10.4881 16.4498 10.8786L6.55028 20.7781C6.15975 21.1687 5.52659 21.1687 5.13606 20.7781L3.72185 19.3639C3.33132 18.9734 3.33132 18.3402 3.72185 17.9497Z',
          fill: color || EColorPalette.cGray4,
          opacity: 0.3,
        })}
        ${this.getPath({
          d: 'M10.0857 4.51477C12.0383 2.56215 15.2041 2.56215 17.1568 4.51477L20.6603 8.01831C20.678 8.03598 20.678 8.06463 20.6603 8.0823L17.5103 11.2323C17.3151 11.4275 16.9985 11.4275 16.8032 11.2323L10.0857 4.51477Z',
          fill: color || EColorPalette.cGray4,
        })}

        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.SLEEP: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 25 24')}
        ${this.getPath({
          d: 'M19.4251 19.4253C18.3065 20.5438 16.9497 21.3951 15.4559 21.9156C13.9622 22.4362 12.3701 22.6125 10.7986 22.4314C9.22717 22.2503 7.71694 21.7164 6.38074 20.8698C5.04454 20.0231 3.91691 18.8855 3.08207 17.5418C2.24724 16.1982 1.72677 14.6833 1.55956 13.1103C1.39235 11.5373 1.58271 9.94688 2.11642 8.45777C2.65013 6.96867 3.5134 5.6194 4.64174 4.51074C5.77009 3.40208 7.13435 2.56269 8.63261 2.05528C8.79219 2.00724 8.96321 2.01371 9.11869 2.07368C9.27418 2.13364 9.40526 2.24368 9.49125 2.38643C9.57724 2.52917 9.61324 2.69649 9.59357 2.86197C9.5739 3.02745 9.49967 3.18167 9.38261 3.30027C8.12509 4.55937 7.26904 6.16299 6.92267 7.90848C6.5763 9.65396 6.75514 11.463 7.43661 13.1068C8.11808 14.7507 9.27157 16.1556 10.7513 17.1441C12.231 18.1326 13.9706 18.6602 15.7501 18.6603C16.7328 18.6638 17.709 18.5015 18.6376 18.1803C18.798 18.1261 18.9719 18.1277 19.1313 18.1849C19.2906 18.242 19.4259 18.3514 19.5151 18.4953C19.6075 18.6376 19.649 18.8071 19.6326 18.976C19.6163 19.1449 19.5431 19.3033 19.4251 19.4253Z',
          fill: color || EColorPalette.cBlack,
        })}
        ${this.getPath({
          d: 'M17.0303 15.2197C17.171 15.3603 17.25 15.5511 17.25 15.75C17.25 15.9489 17.171 16.1397 17.0303 16.2803C16.8897 16.421 16.6989 16.5 16.5 16.5H13.5C13.3578 16.4989 13.2189 16.4575 13.0994 16.3804C12.9799 16.3034 12.8847 16.194 12.825 16.065C12.7649 15.9345 12.7436 15.7895 12.7635 15.6472C12.7835 15.505 12.8439 15.3714 12.9375 15.2625L14.4375 13.5H13.5C13.3011 13.5 13.1103 13.421 12.9697 13.2803C12.829 13.1397 12.75 12.9489 12.75 12.75C12.75 12.5511 12.829 12.3603 12.9697 12.2197C13.1103 12.079 13.3011 12 13.5 12H16.0725C16.2146 12.0028 16.353 12.0459 16.4715 12.1244C16.59 12.2028 16.6838 12.3133 16.742 12.4429C16.8002 12.5726 16.8203 12.7162 16.8 12.8568C16.7797 12.9975 16.7199 13.1295 16.6275 13.2375L15.1275 15H16.5C16.6989 15 16.8897 15.079 17.0303 15.2197Z',
          fill: color || EColorPalette.cBlack,
          opacity: 0.6,
        })}
        ${this.getPath({
          d: 'M22.2803 10.7197C22.421 10.8603 22.5 11.0511 22.5 11.25C22.5 11.4489 22.421 11.6397 22.2803 11.7803C22.1397 11.921 21.9489 12 21.75 12H18.75C18.6078 11.9989 18.4689 11.9575 18.3494 11.8804C18.2299 11.8034 18.1347 11.694 18.075 11.565C18.0149 11.4345 17.9936 11.2895 18.0135 11.1472C18.0335 11.005 18.0939 10.8714 18.1875 10.7625L19.6875 9H18.75C18.5511 9 18.3603 8.92098 18.2197 8.78033C18.079 8.63968 18 8.44891 18 8.25C18 8.05109 18.079 7.86032 18.2197 7.71967C18.3603 7.57902 18.5511 7.5 18.75 7.5H21.3225C21.4646 7.50281 21.603 7.54594 21.7215 7.62436C21.84 7.70278 21.9338 7.81326 21.992 7.94294C22.0502 8.07262 22.0703 8.21616 22.05 8.35683C22.0297 8.4975 21.9699 8.62952 21.8775 8.7375L20.3775 10.5H21.75C21.9489 10.5 22.1397 10.579 22.2803 10.7197Z',
          fill: color || EColorPalette.cBlack,
          opacity: 0.8,
        })}
        ${this.getPath({
          d: 'M16.7816 6.71967C16.9222 6.86032 17.0012 7.05109 17.0012 7.25C17.0012 7.44891 16.9222 7.63968 16.7816 7.78033C16.6409 7.92098 16.4501 8 16.2512 8H11.7512C11.6078 8.00035 11.4672 7.95956 11.3463 7.88246C11.2253 7.80536 11.129 7.69518 11.0687 7.565C11.0087 7.43451 10.9873 7.28951 11.0073 7.14725C11.0272 7.00499 11.0876 6.87144 11.1812 6.7625L14.0012 3.5H11.7512C11.5523 3.5 11.3615 3.42098 11.2209 3.28033C11.0802 3.13968 11.0012 2.94891 11.0012 2.75C11.0012 2.55109 11.0802 2.36032 11.2209 2.21967C11.3615 2.07902 11.5523 2 11.7512 2H15.6062C15.7496 1.99999 15.89 2.04108 16.0107 2.11839C16.1315 2.1957 16.2275 2.30601 16.2875 2.43623C16.3475 2.56645 16.3689 2.71114 16.3491 2.85315C16.3294 2.99516 16.2694 3.12855 16.1762 3.2375L13.3787 6.5H16.2512C16.4501 6.5 16.6409 6.57902 16.7816 6.71967Z',
          fill: color || EColorPalette.cBlack,
          opacity: 0.4,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.CLIPBOARD: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M8.72732 4.63647H7.45459C6.35002 4.63647 5.45459 5.5319 5.45459 6.63647V19.0001C5.45459 20.1047 6.35002 21.0001 7.45459 21.0001H16.5455C17.6501 21.0001 18.5455 20.1047 18.5455 19.0001V6.63647C18.5455 5.53191 17.6501 4.63647 16.5455 4.63647H15.2728V5.04557C15.2728 5.72337 14.7233 6.27284 14.0455 6.27284H9.95459C9.27679 6.27284 8.72732 5.72337 8.72732 5.04557V4.63647Z',
          fill: color || EColorPalette.cGray4,
          opacity: 0.3,
        })}
        ${this.getPath({
          d: 'M11.1818 3.81818H10.0454C9.76927 3.81818 9.54541 4.04204 9.54541 4.31818V4.95455C9.54541 5.23069 9.76927 5.45455 10.0454 5.45455H13.9545C14.2306 5.45455 14.4545 5.23069 14.4545 4.95455V4.31818C14.4545 4.04204 14.2306 3.81818 13.9545 3.81818H12.8181C12.8181 3.36631 12.4518 3 12 3C11.5481 3 11.1818 3.36631 11.1818 3.81818Z',
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getRectangle({
          opacity: 0.3,
          x: 8,
          y: 10,
          width: 4,
          height: 2,
          rx: 1,
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getRectangle({
          opacity: 0.3,
          x: 8,
          y: 14,
          width: 8,
          height: 2,
          rx: 1,
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.BULLET_LIST: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 25 24')}
        ${this.getPath({
          d: 'M9.5 6.5C9.5 5.67157 10.1716 5 11 5H20C20.8284 5 21.5 5.67157 21.5 6.5C21.5 7.32843 20.8284 8 20 8H11C10.1716 8 9.5 7.32843 9.5 6.5ZM9.5 11.5C9.5 10.6716 10.1716 10 11 10H20C20.8284 10 21.5 10.6716 21.5 11.5C21.5 12.3284 20.8284 13 20 13H11C10.1716 13 9.5 12.3284 9.5 11.5ZM11 15C10.1716 15 9.5 15.6716 9.5 16.5C9.5 17.3284 10.1716 18 11 18H20C20.8284 18 21.5 17.3284 21.5 16.5C21.5 15.6716 20.8284 15 20 15H11Z',
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getPath({
          d: 'M6 8C6.82843 8 7.5 7.32843 7.5 6.5C7.5 5.67157 6.82843 5 6 5C5.17157 5 4.5 5.67157 4.5 6.5C4.5 7.32843 5.17157 8 6 8ZM6 13C6.82843 13 7.5 12.3284 7.5 11.5C7.5 10.6716 6.82843 10 6 10C5.17157 10 4.5 10.6716 4.5 11.5C4.5 12.3284 5.17157 13 6 13ZM7.5 16.5C7.5 17.3284 6.82843 18 6 18C5.17157 18 4.5 17.3284 4.5 16.5C4.5 15.6716 5.17157 15 6 15C6.82843 15 7.5 15.6716 7.5 16.5Z',
          fill: color || EColorPalette.cGray4,
          opacity: 0.3,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.PIE_CHART_CIRCLE: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 18 18', 'none')}
        <g clip-path="url(#clip0_3853_33367)">
        ${this.getPath({
          d: 'M7.875 0.0703125V10.1257H17.9304C17.3767 14.5653 13.5896 18.0007 9 18.0007C4.02943 18.0007 0 13.9713 0 9.00069C0 4.41111 3.4354 0.623923 7.875 0.0703125Z',
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getPath({
          d: 'M10.125 0.0703125V7.87569H17.9304C17.4228 3.80463 14.196 0.577966 10.125 0.0703125Z',
          fill: color || EColorPalette.cGray4,
        })}
        </g>
        ${this.getDefsPart()}
        ${this.getClipPath()}
        <rect width="18" height="18" fill="white"/>
        ${this.endClipPath()}
        ${this.endDefsPart()}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.LIST_WITH_THREE_STRIPES: {
        return `
          ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 18 14', 'none')}
          ${this.getPath({
            d: 'M0.541992 13.1667V10.3333H17.4587V13.1667H0.541992ZM0.541992 8.41667V5.58333H17.4587V8.41667H0.541992ZM0.541992 3.64583V0.8125H17.4587V3.64583H0.541992Z',
            fill: color || EColorPalette.cGray,
          })}
          ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.CARDS_BOARDS: {
        return `
          ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 16 16', 'none')}
          ${this.getPath({
            d: 'M2.16667 6.75C1.70833 6.75 1.31597 6.58681 0.989583 6.26042C0.663194 5.93403 0.5 5.54167 0.5 5.08333V2.16667C0.5 1.70833 0.663194 1.31597 0.989583 0.989583C1.31597 0.663194 1.70833 0.5 2.16667 0.5H5.08333C5.54167 0.5 5.93403 0.663194 6.26042 0.989583C6.58681 1.31597 6.75 1.70833 6.75 2.16667V5.08333C6.75 5.54167 6.58681 5.93403 6.26042 6.26042C5.93403 6.58681 5.54167 6.75 5.08333 6.75H2.16667ZM2.16667 15.5C1.70833 15.5 1.31597 15.3368 0.989583 15.0104C0.663194 14.684 0.5 14.2917 0.5 13.8333V10.9167C0.5 10.4583 0.663194 10.066 0.989583 9.73958C1.31597 9.41319 1.70833 9.25 2.16667 9.25H5.08333C5.54167 9.25 5.93403 9.41319 6.26042 9.73958C6.58681 10.066 6.75 10.4583 6.75 10.9167V13.8333C6.75 14.2917 6.58681 14.684 6.26042 15.0104C5.93403 15.3368 5.54167 15.5 5.08333 15.5H2.16667ZM10.9167 6.75C10.4583 6.75 10.066 6.58681 9.73958 6.26042C9.41319 5.93403 9.25 5.54167 9.25 5.08333V2.16667C9.25 1.70833 9.41319 1.31597 9.73958 0.989583C10.066 0.663194 10.4583 0.5 10.9167 0.5H13.8333C14.2917 0.5 14.684 0.663194 15.0104 0.989583C15.3368 1.31597 15.5 1.70833 15.5 2.16667V5.08333C15.5 5.54167 15.3368 5.93403 15.0104 6.26042C14.684 6.58681 14.2917 6.75 13.8333 6.75H10.9167ZM10.9167 15.5C10.4583 15.5 10.066 15.3368 9.73958 15.0104C9.41319 14.684 9.25 14.2917 9.25 13.8333V10.9167C9.25 10.4583 9.41319 10.066 9.73958 9.73958C10.066 9.41319 10.4583 9.25 10.9167 9.25H13.8333C14.2917 9.25 14.684 9.41319 15.0104 9.73958C15.3368 10.066 15.5 10.4583 15.5 10.9167V13.8333C15.5 14.2917 15.3368 14.684 15.0104 15.0104C14.684 15.3368 14.2917 15.5 13.8333 15.5H10.9167Z',
            fill: color || EColorPalette.cGray,
          })}
          ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.BELL: {
        return `
          ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 12 16', 'none')}
          ${this.getPath({
            d: 'M0 13.25V11.75H1.5V6.5C1.5 5.4625 1.8125 4.54063 2.4375 3.73438C3.0625 2.92813 3.875 2.4 4.875 2.15V1.625C4.875 1.3125 4.98438 1.04688 5.20312 0.828125C5.42188 0.609375 5.6875 0.5 6 0.5C6.3125 0.5 6.57813 0.609375 6.79688 0.828125C7.01563 1.04688 7.125 1.3125 7.125 1.625V2.15C8.125 2.4 8.9375 2.92813 9.5625 3.73438C10.1875 4.54063 10.5 5.4625 10.5 6.5V11.75H12V13.25H0ZM6 15.5C5.5875 15.5 5.23438 15.3531 4.94063 15.0594C4.64688 14.7656 4.5 14.4125 4.5 14H7.5C7.5 14.4125 7.35313 14.7656 7.05938 15.0594C6.76563 15.3531 6.4125 15.5 6 15.5Z',
            fill: color || EColorPalette.cGray,
          })}
          ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.BELL_CROSSED: {
        return `
          ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 20 20', 'none')}
          ${this.getPath({
            d: 'M3.33268 15.8307V14.1641H4.99935V8.33073C4.99935 7.8724 5.05838 7.42101 5.17643 6.97656C5.29449 6.53212 5.47157 6.10851 5.70768 5.70573L8.33268 8.33073H5.99935L1.16602 3.4974L2.33268 2.33073L17.666 17.6641L16.4993 18.8307L13.4577 15.8307H3.33268ZM14.9993 12.6224L6.83268 4.45573C7.11046 4.23351 7.40907 4.03906 7.72852 3.8724C8.04796 3.70573 8.38824 3.58073 8.74935 3.4974V2.91406C8.74935 2.56684 8.87088 2.2717 9.11393 2.02865C9.35699 1.78559 9.65213 1.66406 9.99935 1.66406C10.3466 1.66406 10.6417 1.78559 10.8848 2.02865C11.1278 2.2717 11.2493 2.56684 11.2493 2.91406V3.4974C12.3605 3.77517 13.2632 4.36198 13.9577 5.25781C14.6521 6.15365 14.9993 7.17795 14.9993 8.33073V12.6224ZM9.99935 18.3307C9.54102 18.3307 9.14865 18.1675 8.82227 17.8411C8.49588 17.5148 8.33268 17.1224 8.33268 16.6641H11.666C11.666 17.1224 11.5028 17.5148 11.1764 17.8411C10.85 18.1675 10.4577 18.3307 9.99935 18.3307Z',
            fill: color || EColorPalette.cGray,
          })}
          ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.NOTIFICATIONS_BIT: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M14.1618 5.87061C14.0566 6.22877 14.0001 6.60779 14.0001 7C14.0001 8.74164 15.1132 10.2233 16.6668 10.7724V11.8587C16.6668 13.178 17.2448 14.4233 18.2528 15.2753C18.5182 15.5027 18.6668 15.8253 18.6668 16.1667C18.6668 16.81 18.1435 17.3333 17.5002 17.3333H6.50016C5.85683 17.3333 5.3335 16.81 5.3335 16.1667C5.3335 15.8253 5.48216 15.5027 5.7415 15.2807C6.7555 14.4233 7.3335 13.178 7.3335 11.8587V10C7.3335 7.654 9.0755 5.712 11.3335 5.38667V4.66667C11.3335 4.298 11.6322 4 12.0002 4C12.3682 4 12.6668 4.298 12.6668 4.66667V5.38667C13.1991 5.46338 13.7028 5.63 14.1618 5.87061Z',
          fill: color || EColorPalette.cGray,
        })}
        ${this.getOpenUrlClip(0.3)}
        ${this.getPath({
          d: 'M12.0001 20C13.2074 20 14.2174 19.1393 14.4494 18H9.55078C9.78278 19.1393 10.7928 20 12.0001 20Z',
          fill: color || EColorPalette.cGray,
        })}
        ${this.getEndUrlClip()}
        ${this.getSvgEndTemplate()}
        `;
      }
      case EIcon.SNEAKERS: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getRectangle({
          opacity: 0.3,
          x: 2.29688,
          y: 4.05859,
          width: 6.73002,
          height: 13.46,
          rx: 1,
          fill: color || EColorPalette.cGray4,
        })}
        ${this.getPath({
          d:
            'M 1.33545 13.325 ' +
            'C 1.33545 13.016 ' +
            '1.57177 12.7596 ' +
            '1.87853 12.7222' +
            'C6.35443 12.1768 ' +
            '8.53329 10.46623 ' +
            '9.42237 9.51687 ' +
            'C 9.70192 9.21836 ' +
            '10.2192 9.1756 ' +
            '10.5009 9.47202 ' +
            'C 10.7876 9.7736 ' +
            '11.1616 10.15966 ' +
            '11.5713 10.56384' +
            'C 11.5436 10.59924 ' +
            '11.518 10.63702 ' +
            '11.4949 10.67709 ' +
            'L 10.0527 13.175 ' +
            'C 9.78724 13.6348 ' +
            '9.9448 14.2228 ' +
            '10.4046 14.4883 ' +
            'C 10.8645 14.7538 ' +
            '11.4525 14.5962 ' +
            '11.718 14.1364 ' +
            'L 13.0109 11.897 ' +
            'C 13.4644 12.2825 ' +
            '13.8931 12.604 ' +
            '14.2347 12.7814 ' +
            'C 14.3514 12.8421 ' +
            '14.4664 12.9005 ' +
            '14.5796 12.9566 ' +
            'L 13.8985 14.1364 ' +
            'C 13.633 14.5962 ' +
            '13.7905 15.1842 ' +
            '14.2504 15.4497 ' +
            'C 14.7102 15.7152 ' +
            '15.2982 15.5577 ' +
            '15.5637 15.0978 ' +
            'L 16.3565 13.7247 ' +
            'C 17.8284 13.2618 ' +
            '18.9067 14.3968 ' +
            '19.6913 14.4951 ' +
            'C 20.0361 14.5383 ' +
            '20.3242 14.5744 ' +
            '20.5641 14.6344 ' +
            'C 21.4747 14.862 ' +
            '22.1696 15.6293 ' +
            '22.649 16.3741 ' +
            'C 23.2069 17.2409 ' +
            '22.717 18.2995 ' +
            '21.7186 18.5561 ' +
            'C 20.5812 18.8485 ' +
            '18.8943 19.1738 ' +
            '16.6916 19.3075 ' +
            'C 12.887 19.5385 ' +
            '4.23132 19.3713 ' +
            '1.91473 19.3207 ' +
            'C 1.58916 19.3136 ' +
            '1.33545 19.048 ' +
            '1.33545 18.7223 ' +
            'L 1.33545 13.325Z',

          fill: color || EColorPalette.cGray4,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.DOWNLOAD: {
        return `
          ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
          ${this.getPath({
            d: 'M2 13C2 12.5 2.5 12 3 12C3.5 12 4 12.5 4 13C4 13.5 4 18 4 18C4 19.1046 4.89543 20 6 20H18C19.1046 20 20 19.1046 20 18V13C20 12.4477 20.4477 12 21 12C21.5523 12 22 12.4477 22 13V18C22 20.2091 20.2091 22 18 22H6C3.79086 22 2 20.2091 2 18C2 18 2 13.5 2 13Z',
            fill: color || EColorPalette.cGreen3,
            opacity: 0.3,
          })}
          ${this.getRectangle({
            opacity: 0.3,
            x: 13,
            y: 15,
            width: 2,
            height: 14,
            rx: 1,
            transform: 'rotate(-180 13 15)',
            fill: color || EColorPalette.cGreen3,
          })}
          ${this.getPath({
            d: 'M16.2929 9.29289C16.6834 8.90237 17.3166 8.90237 17.7071 9.29289C18.0976 9.68342 18.0976 10.3166 17.7071 10.7071L12.7071 15.7071C12.331 16.0832 11.7264 16.0991 11.331 15.7433L6.33104 11.2433C5.92053 10.8738 5.88725 10.2415 6.25671 9.83103C6.62617 9.42052 7.25845 9.38724 7.66896 9.7567L11.9638 13.622L16.2929 9.29289Z',
            fill: color || EColorPalette.cGreen3,
          })}
          ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.HELP: {
        return `
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7.96 12.8C8.24 12.8 8.47667 12.7033 8.67 12.51C8.86333 12.3167 8.96 12.08 8.96 11.8C8.96 11.52 8.86333 11.2833 8.67 11.09C8.47667 10.8967 8.24 10.8 7.96 10.8C7.68 10.8 7.44333 10.8967 7.25 11.09C7.05667 11.2833 6.96 11.52 6.96 11.8C6.96 12.08 7.05667 12.3167 7.25 12.51C7.44333 12.7033 7.68 12.8 7.96 12.8ZM8 16C6.89333 16 5.85333 15.79 4.88 15.37C3.90667 14.95 3.06 14.38 2.34 13.66C1.62 12.94 1.05 12.0933 0.63 11.12C0.21 10.1467 0 9.10667 0 8C0 6.89333 0.21 5.85333 0.63 4.88C1.05 3.90667 1.62 3.06 2.34 2.34C3.06 1.62 3.90667 1.05 4.88 0.63C5.85333 0.21 6.89333 0 8 0C9.10667 0 10.1467 0.21 11.12 0.63C12.0933 1.05 12.94 1.62 13.66 2.34C14.38 3.06 14.95 3.90667 15.37 4.88C15.79 5.85333 16 6.89333 16 8C16 9.10667 15.79 10.1467 15.37 11.12C14.95 12.0933 14.38 12.94 13.66 13.66C12.94 14.38 12.0933 14.95 11.12 15.37C10.1467 15.79 9.10667 16 8 16ZM8.08 4.56C8.41333 4.56 8.70333 4.66667 8.95 4.88C9.19667 5.09333 9.32 5.36 9.32 5.68C9.32 5.97333 9.23 6.23333 9.05 6.46C8.87 6.68667 8.66667 6.9 8.44 7.1C8.13333 7.36667 7.86333 7.66 7.63 7.98C7.39667 8.3 7.28 8.66 7.28 9.06C7.28 9.24667 7.35 9.40333 7.49 9.53C7.63 9.65667 7.79333 9.72 7.98 9.72C8.18 9.72 8.35 9.65333 8.49 9.52C8.63 9.38667 8.72 9.22 8.76 9.02C8.81333 8.74 8.93333 8.49 9.12 8.27C9.30667 8.05 9.50667 7.84 9.72 7.64C10.0267 7.34667 10.29 7.02667 10.51 6.68C10.73 6.33333 10.84 5.94667 10.84 5.52C10.84 4.84 10.5633 4.28333 10.01 3.85C9.45667 3.41667 8.81333 3.2 8.08 3.2C7.57333 3.2 7.09 3.30667 6.63 3.52C6.17 3.73333 5.82 4.06 5.58 4.5C5.48667 4.66 5.45667 4.83 5.49 5.01C5.52333 5.19 5.61333 5.32667 5.76 5.42C5.94667 5.52667 6.14 5.56 6.34 5.52C6.54 5.48 6.70667 5.36667 6.84 5.18C6.98667 4.98 7.17 4.82667 7.39 4.72C7.61 4.61333 7.84 4.56 8.08 4.56Z" fill="#B9B8B8"/>
          </svg>
        `;
      }

      case EIcon.CLOSE_SMALL: {
        return `
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4.99844 6.40078L2.08159 9.32578C1.89719 9.50912 1.6625 9.60078 1.37751 9.60078C1.09253 9.60078 0.857837 9.50912 0.673438 9.32578C0.490104 9.14245 0.398438 8.90911 0.398438 8.62578C0.398438 8.34245 0.490104 8.10911 0.673438 7.92578L3.59844 5.00078L0.673438 2.10898C0.490104 1.92456 0.398438 1.68986 0.398438 1.40488C0.398438 1.11988 0.490104 0.885181 0.673438 0.700781C0.856771 0.517448 1.0901 0.425781 1.37344 0.425781C1.65677 0.425781 1.8901 0.517448 2.07344 0.700781L4.99844 3.62578L7.89024 0.700781C8.07465 0.517448 8.30935 0.425781 8.59434 0.425781C8.87934 0.425781 9.11404 0.517448 9.29844 0.700781C9.49844 0.900781 9.59844 1.13828 9.59844 1.41328C9.59844 1.68828 9.49844 1.91745 9.29844 2.10078L6.37344 5.00078L9.29844 7.91763C9.48177 8.10203 9.57344 8.33672 9.57344 8.62171C9.57344 8.90669 9.48177 9.14138 9.29844 9.32578C9.09844 9.52578 8.86094 9.62578 8.58594 9.62578C8.31094 9.62578 8.08177 9.52578 7.89844 9.32578L4.99844 6.40078Z" fill="#1C1B1F"/>
          </svg>
        `;
      }

      case EIcon.THREE_COLUMNS: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, 12, 12, '0 0 12 12')}
        ${this.getPath({
          d: 'M5.37143 12C5.11238 12 4.89524 11.9179 4.72 11.7536C4.54476 11.5893 4.45714 11.3857 4.45714 11.1429V0.857143C4.45714 0.614286 4.54476 0.410714 4.72 0.246429C4.89524 0.0821429 5.11238 0 5.37143 0H6.62857C6.88762 0 7.10476 0.0821429 7.28 0.246429C7.45524 0.410714 7.54286 0.614286 7.54286 0.857143V11.1429C7.54286 11.3857 7.45524 11.5893 7.28 11.7536C7.10476 11.9179 6.88762 12 6.62857 12H5.37143ZM0.914286 12C0.655238 12 0.438095 11.9179 0.262857 11.7536C0.087619 11.5893 0 11.3857 0 11.1429V0.857143C0 0.614286 0.087619 0.410714 0.262857 0.246429C0.438095 0.0821429 0.655238 0 0.914286 0H2.17143C2.43048 0 2.64762 0.0821429 2.82286 0.246429C2.9981 0.410714 3.08571 0.614286 3.08571 0.857143V11.1429C3.08571 11.3857 2.9981 11.5893 2.82286 11.7536C2.64762 11.9179 2.43048 12 2.17143 12H0.914286ZM9.82857 12C9.56952 12 9.35238 11.9179 9.17714 11.7536C9.0019 11.5893 8.91429 11.3857 8.91429 11.1429V0.857143C8.91429 0.614286 9.0019 0.410714 9.17714 0.246429C9.35238 0.0821429 9.56952 0 9.82857 0H11.0857C11.3448 0 11.5619 0.0821429 11.7371 0.246429C11.9124 0.410714 12 0.614286 12 0.857143V11.1429C12 11.3857 11.9124 11.5893 11.7371 11.7536C11.5619 11.9179 11.3448 12 11.0857 12H9.82857Z',
          fill: color || EColorPalette.cGreen3,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.TWO_ROWS: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, 12, 12, '0 0 12 12')}
        ${this.getPath({
          d: 'M12 4C12 4.36667 11.8694 4.68056 11.6083 4.94167C11.3472 5.20278 11.0333 5.33333 10.6667 5.33333L1.33333 5.33333C0.966667 5.33333 0.652778 5.20278 0.391667 4.94167C0.130556 4.68056 0 4.36667 0 4L0 1.33333C0 0.966666 0.130556 0.652777 0.391667 0.391666C0.652778 0.130555 0.966667 -9.53674e-07 1.33333 -9.53674e-07H10.6667C11.0333 -9.53674e-07 11.3472 0.130555 11.6083 0.391666C11.8694 0.652777 12 0.966666 12 1.33333V4ZM12 10.6667C12 11.0333 11.8694 11.3472 11.6083 11.6083C11.3472 11.8694 11.0333 12 10.6667 12H1.33333C0.966667 12 0.652778 11.8694 0.391667 11.6083C0.130556 11.3472 0 11.0333 0 10.6667L0 8C0 7.63333 0.130556 7.31944 0.391667 7.05833C0.652778 6.79722 0.966667 6.66667 1.33333 6.66667L10.6667 6.66667C11.0333 6.66667 11.3472 6.79722 11.6083 7.05833C11.8694 7.31944 12 7.63333 12 8V10.6667Z',
          fill: color || EColorPalette.cGreen3,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.SQUARE_GRID: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, 18, 18, '0 0 18 18')}
        ${this.getPath({
          d: 'M3 3H6V6H3V3Z',
          fill: color || EColorPalette.cGreen3,
        })}
        ${this.getSvgBeginTemplate(this.uniqueId, 18, 18, '0 0 18 18')}
        ${this.getPath({
          d: 'M8.5 3C7.94772 3 7.5 3.44772 7.5 4V5C7.5 5.55228 7.94772 6 8.5 6H9.5C10.0523 6 10.5 5.55228 10.5 5V4C10.5 3.44772 10.0523 3 9.5 3H8.5ZM4 7.5C3.44772 7.5 3 7.94772 3 8.5V9.5C3 10.0523 3.44772 10.5 4 10.5H5C5.55228 10.5 6 10.0523 6 9.5V8.5C6 7.94772 5.55228 7.5 5 7.5H4ZM7.5 8.5C7.5 7.94772 7.94772 7.5 8.5 7.5H9.5C10.0523 7.5 10.5 7.94772 10.5 8.5V9.5C10.5 10.0523 10.0523 10.5 9.5 10.5H8.5C7.94772 10.5 7.5 10.0523 7.5 9.5V8.5ZM13 3C12.4477 3 12 3.44772 12 4V5C12 5.55228 12.4477 6 13 6H14C14.5523 6 15 5.55228 15 5V4C15 3.44772 14.5523 3 14 3H13ZM12 8.5C12 7.94772 12.4477 7.5 13 7.5H14C14.5523 7.5 15 7.94772 15 8.5V9.5C15 10.0523 14.5523 10.5 14 10.5H13C12.4477 10.5 12 10.0523 12 9.5V8.5ZM4 12C3.44772 12 3 12.4477 3 13V14C3 14.5523 3.44772 15 4 15H5C5.55228 15 6 14.5523 6 14V13C6 12.4477 5.55228 12 5 12H4ZM7.5 13C7.5 12.4477 7.94772 12 8.5 12H9.5C10.0523 12 10.5 12.4477 10.5 13V14C10.5 14.5523 10.0523 15 9.5 15H8.5C7.94772 15 7.5 14.5523 7.5 14V13ZM13 12C12.4477 12 12 12.4477 12 13V14C12 14.5523 12.4477 15 13 15H14C14.5523 15 15 14.5523 15 14V13C15 12.4477 14.5523 12 14 12H13Z',
          fill: color || EColorPalette.cGreen3,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.VENDOR_CALENDAR: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        <path d="M18 2V4M6 2V4" stroke="${
          color || EColorPalette.cGreen
        }" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M2.5 12.2432C2.5 7.88594 2.5 5.70728 3.75212 4.35364C5.00424 3 7.01949 3 11.05 3H12.95C16.9805 3 18.9958 3 20.2479 4.35364C21.5 5.70728 21.5 7.88594 21.5 12.2432V12.7568C21.5 17.1141 21.5 19.2927 20.2479 20.6464C18.9958 22 16.9805 22 12.95 22H11.05C7.01949 22 5.00424 22 3.75212 20.6464C2.5 19.2927 2.5 17.1141 2.5 12.7568V12.2432Z" stroke="${
          color || EColorPalette.cGreen
        }" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M3 8H21" stroke="${
          color || EColorPalette.cGreen
        }" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.VENDOR_CREATED: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        <path d="M18 2V4M6 2V4" stroke="${
          color || EColorPalette.cGreen
        }" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M2.5 12.2432C2.5 7.88594 2.5 5.70728 3.75212 4.35364C5.00424 3 7.01949 3 11.05 3H12.95C16.9805 3 18.9958 3 20.2479 4.35364C21.5 5.70728 21.5 7.88594 21.5 12.2432V12.7568C21.5 17.1141 21.5 19.2927 20.2479 20.6464C18.9958 22 16.9805 22 12.95 22H11.05C7.01949 22 5.00424 22 3.75212 20.6464C2.5 19.2927 2.5 17.1141 2.5 12.7568V12.2432Z" stroke="${
          color || EColorPalette.cGreen
        }" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M3 8H21" stroke="${
          color || EColorPalette.cGreen
        }" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M9 15H15M12 12V18" stroke="${
          color || EColorPalette.cGreen
        }" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.VENDOR_CHECK: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        <path d="M2.5 12C2.5 7.52166 2.5 5.28249 3.89124 3.89124C5.28249 2.5 7.52166 2.5 12 2.5C16.4783 2.5 18.7175 2.5 20.1088 3.89124C21.5 5.28249 21.5 7.52166 21.5 12C21.5 16.4783 21.5 18.7175 20.1088 20.1088C18.7175 21.5 16.4783 21.5 12 21.5C7.52166 21.5 5.28249 21.5 3.89124 20.1088C2.5 18.7175 2.5 16.4783 2.5 12Z" stroke="${
          color || EColorPalette.cGreen
        }" stroke-width="1.5"/>
        <path d="M8 12.5L10.5 15L16 9" stroke="${
          color || EColorPalette.cGreen
        }" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.VENDOR_ENTRY_CALL_REQ: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        <g clip-path="url(#clip0_${this.uniqueId})">
          ${this.getPath({
            d: 'M12.832 2.83466C12.832 2.61365 12.9198 2.40169 13.0761 2.24541C13.2324 2.08913 13.4443 2.00133 13.6653 2.00133C15.8747 2.00376 17.9929 2.88251 19.5552 4.44479C21.1175 6.00707 21.9962 8.12527 21.9987 10.3347C21.9987 10.5557 21.9109 10.7676 21.7546 10.9239C21.5983 11.0802 21.3863 11.168 21.1653 11.168C20.9443 11.168 20.7324 11.0802 20.5761 10.9239C20.4198 10.7676 20.332 10.5557 20.332 10.3347C20.33 8.56716 19.627 6.87262 18.3772 5.62281C17.1274 4.373 15.4328 3.66998 13.6653 3.668C13.4443 3.668 13.2324 3.5802 13.0761 3.42392C12.9198 3.26764 12.832 3.05568 12.832 2.83466Z',
            fill: color || EColorPalette.cGreen,
          })}
          ${this.getPath({
            d: 'M13.6653 7.00133C14.5494 7.00133 15.3972 7.35252 16.0224 7.97764C16.6475 8.60276 16.9987 9.45061 16.9987 10.3347C16.9987 10.5557 17.0865 10.7676 17.2427 10.9239C17.399 11.0802 17.611 11.168 17.832 11.168C18.053 11.168 18.265 11.0802 18.4213 10.9239C18.5775 10.7676 18.6653 10.5557 18.6653 10.3347C18.664 9.00899 18.1368 7.73799 17.1994 6.80059C16.262 5.8632 14.991 5.33599 13.6653 5.33466C13.4443 5.33466 13.2324 5.42246 13.0761 5.57874C12.9198 5.73502 12.832 5.94698 12.832 6.168C12.832 6.38901 12.9198 6.60097 13.0761 6.75725C13.2324 6.91353 13.4443 7.00133 13.6653 7.00133Z',
            fill: color || EColorPalette.cGreen,
          })}
          ${this.getPath({
            d: 'M21.2428 15.9505C21.7258 16.4348 21.9969 17.0908 21.9969 17.7747C21.9969 18.4586 21.7258 19.1146 21.2428 19.5988L20.4845 20.473C13.6595 27.0072 -2.94881 10.403 3.48452 3.55633L4.44285 2.723C4.92766 2.25356 5.57779 1.99387 6.2526 2.00011C6.92741 2.00635 7.57263 2.27801 8.04868 2.75633C8.07451 2.78216 9.61868 4.788 9.61868 4.788C10.0769 5.26935 10.3319 5.90877 10.3308 6.57333C10.3298 7.2379 10.0726 7.87648 9.61284 8.35633L8.64785 9.56966C9.18188 10.8673 9.96706 12.0465 10.9583 13.0397C11.9495 14.033 13.1272 14.8205 14.4237 15.3572L15.6445 14.3863C16.1244 13.9269 16.7629 13.6701 17.4272 13.6691C18.0916 13.6682 18.7308 13.9233 19.212 14.3813C19.212 14.3813 21.217 15.9247 21.2428 15.9505Z',
            fill: color || EColorPalette.cGreen,
          })}
          ${this.getPath({
            d: 'M20.0962 17.1622C20.0962 17.1622 18.102 15.628 18.0762 15.6022C17.9045 15.4319 17.6725 15.3364 17.4308 15.3364C17.189 15.3364 16.957 15.4319 16.7853 15.6022C16.7628 15.6255 15.082 16.9647 15.082 16.9647C14.9687 17.0548 14.8339 17.1139 14.6909 17.1361C14.5478 17.1583 14.4014 17.1429 14.2662 17.0913C12.5866 16.466 11.061 15.4869 9.79271 14.2206C8.52446 12.9542 7.54318 11.43 6.91535 9.75133C6.85968 9.61421 6.84153 9.46474 6.86277 9.31828C6.884 9.17182 6.94386 9.03366 7.03618 8.918C7.03618 8.918 8.37535 7.23633 8.39785 7.21466C8.56807 7.04299 8.66358 6.81101 8.66358 6.56925C8.66358 6.32748 8.56807 6.09551 8.39785 5.92383C8.37201 5.89883 6.83785 3.903 6.83785 3.903C6.6636 3.74675 6.43618 3.66308 6.20222 3.66912C5.96826 3.67516 5.74547 3.77047 5.57952 3.9355L4.62118 4.76883C-0.0804784 10.4222 14.312 24.0163 19.2662 19.3347L20.0253 18.4597C20.2032 18.2949 20.3101 18.0674 20.3233 17.8252C20.3365 17.5831 20.2551 17.3453 20.0962 17.1622Z',
            fill: EColorPalette.cWhite,
          })}
        </g>
        <defs>
          <clipPath id="clip0_${this.uniqueId}">
            <rect width="20" height="20" fill="white" transform="translate(2 2)"/>
          </clipPath>
        </defs>
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.VENDOR_VACANT: {
        return `
          ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
          <g clip-path="url(#clip0_${this.uniqueId})">
            <path d="M7.50693 24C5.69668 23.9989 3.94794 23.343 2.58338 22.1535C1.21883 20.9639 0.330567 19.321 0.0824936 17.5278C-0.16558 15.7346 0.243282 13.9122 1.23363 12.3969C2.22398 10.8816 3.72899 9.7756 5.47093 9.28302C6.73706 8.92045 8.07722 8.90387 9.35193 9.23502L17.2599 1.32902C17.6797 0.906302 18.1793 0.571153 18.7297 0.343015C19.28 0.114877 19.8702 -0.0017098 20.4659 1.89472e-05C21.4035 0.000813379 22.3024 0.37361 22.9654 1.03657C23.6283 1.69952 24.0011 2.59846 24.0019 3.53602C24.0035 4.13181 23.8869 4.722 23.659 5.27246C23.431 5.82292 23.0962 6.32274 22.6739 6.74302L22.0019 7.41502C21.6259 7.78846 21.1179 7.99865 20.5879 8.00002H19.0019V9.00002C19.0019 9.53045 18.7912 10.0392 18.4161 10.4142C18.0411 10.7893 17.5324 11 17.0019 11H16.0019V12.586C16.0026 12.8488 15.9512 13.109 15.8506 13.3517C15.75 13.5945 15.6023 13.8148 15.4159 14L14.7659 14.65C15.0989 15.9241 15.0827 17.2644 14.7189 18.53C14.3096 19.972 13.4792 21.259 12.3341 22.2262C11.1889 23.1934 9.78109 23.7968 8.29093 23.959C8.03047 23.9861 7.76879 23.9998 7.50693 24ZM7.50693 11C6.48308 10.999 5.47928 11.2838 4.60852 11.8224C3.73776 12.361 3.03461 13.1319 2.57823 14.0484C2.12185 14.9649 1.93036 15.9906 2.0253 17.0101C2.12025 18.0295 2.49787 19.0022 3.11565 19.8187C3.73343 20.6351 4.56685 21.2629 5.52209 21.6314C6.47733 21.9999 7.51646 22.0945 8.52252 21.9044C9.52858 21.7144 10.4616 21.2473 11.2166 20.5557C11.9716 19.8642 12.5186 18.9756 12.7959 17.99C13.1079 16.9066 13.073 15.7526 12.6959 14.69C12.6344 14.5126 12.6241 14.3214 12.6662 14.1383C12.7083 13.9553 12.8011 13.7878 12.9339 13.655L14.0019 12.586V11C14.0019 10.4696 14.2126 9.96088 14.5877 9.58581C14.9628 9.21073 15.4715 9.00002 16.0019 9.00002H17.0019V8.00002C17.0019 7.46959 17.2126 6.96088 17.5877 6.58581C17.9628 6.21073 18.4715 6.00002 19.0019 6.00002H20.5879L21.2599 5.32802C21.4959 5.09317 21.683 4.81388 21.8103 4.5063C21.9377 4.19871 22.0028 3.86893 22.0019 3.53602C22.0017 3.1289 21.8399 2.73852 21.5521 2.45055C21.2643 2.16258 20.874 2.00055 20.4669 2.00002C20.1338 1.9992 19.8038 2.06445 19.496 2.19198C19.1883 2.31951 18.9088 2.5068 18.6739 2.74302L10.3429 11.073C10.21 11.2059 10.0423 11.2986 9.85902 11.3405C9.67578 11.3825 9.48444 11.3719 9.30693 11.31C8.727 11.1058 8.11676 11.001 7.50193 11H7.50693ZM5.00193 18C5.00193 18.1978 5.06058 18.3911 5.17046 18.5556C5.28034 18.72 5.43652 18.8482 5.61925 18.9239C5.80197 18.9996 6.00304 19.0194 6.19702 18.9808C6.391 18.9422 6.56918 18.847 6.70904 18.7071C6.84889 18.5673 6.94413 18.3891 6.98272 18.1951C7.0213 18.0011 7.0015 17.8001 6.92581 17.6173C6.85012 17.4346 6.72195 17.2784 6.5575 17.1685C6.39305 17.0587 6.19971 17 6.00193 17C5.73671 17 5.48236 17.1054 5.29482 17.2929C5.10729 17.4804 5.00193 17.7348 5.00193 18Z" fill="${
              color || EColorPalette.cGreen
            }"/>
          </g>
          <defs>
            <clipPath id="clip0_${this.uniqueId}">
              <rect width="24" height="24" fill="white"/>
            </clipPath>
          </defs>
          ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.VENDOR_PETS: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        <path d="M14.9992 11C14.9992 15 19.9994 14 19.9997 18C20 20 18 22 15.9997 22C13.9994 22 13.9329 20.5 11.9992 20.5C10.2738 20.5 9.99691 22 7.99846 22C6 22 4 20 4 18C4 14 8.99917 15 8.99917 11C8.99917 9.5 10.5 8.5 11.9992 8.5C13.4983 8.5 14.9992 9.5 14.9992 11Z" stroke="${
          color || EColorPalette.cGreen
        }" stroke-width="1.5"/>
        <circle cx="4" cy="9.5" r="2" stroke="${color || EColorPalette.cGreen}" stroke-width="1.5"/>
        <circle cx="8.5" cy="4" r="2" stroke="${color || EColorPalette.cGreen}" stroke-width="1.5"/>
        <circle cx="15.5" cy="4" r="2" stroke="${color || EColorPalette.cGreen}" stroke-width="1.5"/>
        <circle cx="20" cy="9.5" r="2" stroke="${color || EColorPalette.cGreen}" stroke-width="1.5"/>
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.VENDOR_BUILDING: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        <path d="M12 2H6C3.518 2 3 2.518 3 5V22H15V5C15 2.518 14.482 2 12 2Z" stroke="${
          color || EColorPalette.cGreen
        }" stroke-width="1.5" stroke-linejoin="round"/>
        <path d="M18 8H15V22H21V11C21 8.518 20.482 8 18 8Z" stroke="${
          color || EColorPalette.cGreen
        }" stroke-width="1.5" stroke-linejoin="round"/>
        <path d="M8 6H10M8 9H10M8 12H10" stroke="${
          color || EColorPalette.cGreen
        }" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M11.5 22V18C11.5 17.0572 11.5 16.5858 11.2071 16.2929C10.9142 16 10.4428 16 9.5 16H8.5C7.55719 16 7.08579 16 6.79289 16.2929C6.5 16.5858 6.5 17.0572 6.5 18V22" stroke="${
          color || EColorPalette.cGreen
        }" stroke-width="1.5" stroke-linejoin="round"/>
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.VENDOR_PATCH: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        <path d="M17.5 10.5L19.5 12.5M14 14L16 16M10.5 17.5L12.5 19.5" stroke="${
          color || EColorPalette.cGreen
        }" stroke-width="1.5" stroke-linecap="round"/>
        <path d="M10.5355 4.67767C11.9002 3.31296 12.5826 2.6306 13.3438 2.3153C14.3587 1.8949 15.4991 1.8949 16.5141 2.3153C17.2753 2.6306 17.9576 3.31296 19.3223 4.67767C20.687 6.04238 21.3694 6.72474 21.6847 7.48594C22.1051 8.50088 22.1051 9.64126 21.6847 10.6562C21.3694 11.4174 20.687 12.0998 19.3223 13.4645L13.4645 19.3223C12.0998 20.687 11.4174 21.3694 10.6562 21.6847C9.64126 22.1051 8.50088 22.1051 7.48594 21.6847C6.72474 21.3694 6.04238 20.687 4.67767 19.3223C3.31296 17.9576 2.6306 17.2753 2.3153 16.5141C1.8949 15.4991 1.8949 14.3587 2.3153 13.3438C2.6306 12.5826 3.31296 11.9002 4.67767 10.5355L10.5355 4.67767Z" stroke="${
          color || EColorPalette.cGreen
        }" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.VENDOR_CALL: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 25 24')}
        <path d="M4.10965 11.9424C3.16163 10.2893 2.70388 8.93948 2.42787 7.57121C2.01965 5.54758 2.95384 3.57081 4.50141 2.30947C5.15548 1.77638 5.90526 1.95852 6.29203 2.6524L7.16521 4.21891C7.85732 5.46057 8.20337 6.08139 8.13473 6.73959C8.0661 7.39779 7.5994 7.93386 6.666 9.00601L4.10965 11.9424ZM4.10965 11.9424C6.02854 15.2883 9.03987 18.3013 12.3896 20.2224M12.3896 20.2224C14.0427 21.1704 15.3925 21.6282 16.7608 21.9042C18.7844 22.3124 20.7612 21.3782 22.0225 19.8306C22.5556 19.1766 22.3735 18.4268 21.6796 18.04L20.1131 17.1668C18.8714 16.4747 18.2506 16.1287 17.5924 16.1973C16.9342 16.2659 16.3981 16.7326 15.326 17.666L12.3896 20.2224Z" stroke="${
          color || EColorPalette.cWhite
        }" stroke-width="1.5" stroke-linejoin="round"/>
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.VENDOR_MESSAGE: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        <path d="M8.5 14.5H15.5M8.5 9.5H12" stroke="${
          color || EColorPalette.cWhite
        }" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M14.1706 20.8905C18.3536 20.6125 21.6856 17.2332 21.9598 12.9909C22.0134 12.1607 22.0134 11.3009 21.9598 10.4707C21.6856 6.22838 18.3536 2.84913 14.1706 2.57107C12.7435 2.47621 11.2536 2.47641 9.8294 2.57107C5.64639 2.84913 2.31441 6.22838 2.04024 10.4707C1.98659 11.3009 1.98659 12.1607 2.04024 12.9909C2.1401 14.536 2.82343 15.9666 3.62791 17.1746C4.09501 18.0203 3.78674 19.0758 3.30021 19.9978C2.94941 20.6626 2.77401 20.995 2.91484 21.2351C3.05568 21.4752 3.37026 21.4829 3.99943 21.4982C5.24367 21.5285 6.08268 21.1757 6.74868 20.6846C7.1264 20.4061 7.31527 20.2668 7.44544 20.2508C7.5756 20.2348 7.83177 20.3403 8.34401 20.5513C8.8044 20.7409 9.33896 20.8579 9.8294 20.8905C11.2536 20.9852 12.7435 20.9854 14.1706 20.8905Z" stroke="${
          color || EColorPalette.cWhite
        }" stroke-width="1.5" stroke-linejoin="round"/>
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.VENDOR_MAIL: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 25 24')}
        <path d="M2.66406 6L9.57708 9.91697C12.1257 11.361 13.2025 11.361 15.7511 9.91697L22.6641 6" stroke="${
          color || EColorPalette.cWhite
        }" stroke-width="1.5" stroke-linejoin="round"/>
        <path d="M2.67983 13.4756C2.7452 16.5412 2.77789 18.0739 3.90902 19.2094C5.04014 20.3448 6.61439 20.3843 9.76289 20.4634C11.7034 20.5122 13.6248 20.5122 15.5653 20.4634C18.7138 20.3843 20.288 20.3448 21.4192 19.2094C22.5503 18.0739 22.583 16.5412 22.6483 13.4756C22.6694 12.4899 22.6694 11.5101 22.6483 10.5244C22.583 7.45886 22.5503 5.92609 21.4192 4.79066C20.288 3.65523 18.7138 3.61568 15.5653 3.53657C13.6248 3.48781 11.7034 3.48781 9.76288 3.53656C6.61439 3.61566 5.04014 3.65521 3.90901 4.79065C2.77788 5.92608 2.7452 7.45885 2.67982 10.5244C2.6588 11.5101 2.65881 12.4899 2.67983 13.4756Z" stroke="${
          color || EColorPalette.cWhite
        }" stroke-width="1.5" stroke-linejoin="round"/>
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.CALENDAR: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
        ${this.getPath({
          d: 'M5 22C4.45 22 3.97917 21.8042 3.5875 21.4125C3.19583 21.0208 3 20.55 3 20V6C3 5.45 3.19583 4.97917 3.5875 4.5875C3.97917 4.19583 4.45 4 5 4H6V3C6 2.71667 6.09583 2.47917 6.2875 2.2875C6.47917 2.09583 6.71667 2 7 2C7.28333 2 7.52083 2.09583 7.7125 2.2875C7.90417 2.47917 8 2.71667 8 3V4H16V3C16 2.71667 16.0958 2.47917 16.2875 2.2875C16.4792 2.09583 16.7167 2 17 2C17.2833 2 17.5208 2.09583 17.7125 2.2875C17.9042 2.47917 18 2.71667 18 3V4H19C19.55 4 20.0208 4.19583 20.4125 4.5875C20.8042 4.97917 21 5.45 21 6V20C21 20.55 20.8042 21.0208 20.4125 21.4125C20.0208 21.8042 19.55 22 19 22H5ZM5 20H19V10H5V20ZM5 8H19V6H5V8Z',
          opacity: 0.3,
          fill: color || EColorPalette.cGreen3,
        })}
        ${this.getPath({
          d: 'M11.2875 13.7125C11.4792 13.9042 11.7167 14 12 14C12.2833 14 12.5208 13.9042 12.7125 13.7125C12.9042 13.5208 13 13.2833 13 13C13 12.7167 12.9042 12.4792 12.7125 12.2875C12.5208 12.0958 12.2833 12 12 12C11.7167 12 11.4792 12.0958 11.2875 12.2875C11.0958 12.4792 11 12.7167 11 13C11 13.2833 11.0958 13.5208 11.2875 13.7125Z',
          fill: color || EColorPalette.cGreen3,
        })}
        ${this.getPath({
          d: 'M7.2875 13.7125C7.47917 13.9042 7.71667 14 8 14C8.28333 14 8.52083 13.9042 8.7125 13.7125C8.90417 13.5208 9 13.2833 9 13C9 12.7167 8.90417 12.4792 8.7125 12.2875C8.52083 12.0958 8.28333 12 8 12C7.71667 12 7.47917 12.0958 7.2875 12.2875C7.09583 12.4792 7 12.7167 7 13C7 13.2833 7.09583 13.5208 7.2875 13.7125Z',
          fill: color || EColorPalette.cGreen3,
        })}
        ${this.getPath({
          d: 'M15.2875 13.7125C15.4792 13.9042 15.7167 14 16 14C16.2833 14 16.5208 13.9042 16.7125 13.7125C16.9042 13.5208 17 13.2833 17 13C17 12.7167 16.9042 12.4792 16.7125 12.2875C16.5208 12.0958 16.2833 12 16 12C15.7167 12 15.4792 12.0958 15.2875 12.2875C15.0958 12.4792 15 12.7167 15 13C15 13.2833 15.0958 13.5208 15.2875 13.7125Z',
          fill: color || EColorPalette.cGreen3,
        })}
        ${this.getPath({
          d: 'M11.2875 17.7125C11.4792 17.9042 11.7167 18 12 18C12.2833 18 12.5208 17.9042 12.7125 17.7125C12.9042 17.5208 13 17.2833 13 17C13 16.7167 12.9042 16.4792 12.7125 16.2875C12.5208 16.0958 12.2833 16 12 16C11.7167 16 11.4792 16.0958 11.2875 16.2875C11.0958 16.4792 11 16.7167 11 17C11 17.2833 11.0958 17.5208 11.2875 17.7125Z',
          fill: color || EColorPalette.cGreen3,
        })}
        ${this.getPath({
          d: 'M7.2875 17.7125C7.47917 17.9042 7.71667 18 8 18C8.28333 18 8.52083 17.9042 8.7125 17.7125C8.90417 17.5208 9 17.2833 9 17C9 16.7167 8.90417 16.4792 8.7125 16.2875C8.52083 16.0958 8.28333 16 8 16C7.71667 16 7.47917 16.0958 7.2875 16.2875C7.09583 16.4792 7 16.7167 7 17C7 17.2833 7.09583 17.5208 7.2875 17.7125Z',
          fill: color || EColorPalette.cGreen3,
        })}
        ${this.getPath({
          d: 'M15.2875 17.7125C15.4792 17.9042 15.7167 18 16 18C16.2833 18 16.5208 17.9042 16.7125 17.7125C16.9042 17.5208 17 17.2833 17 17C17 16.7167 16.9042 16.4792 16.7125 16.2875C16.5208 16.0958 16.2833 16 16 16C15.7167 16 15.4792 16.0958 15.2875 16.2875C15.0958 16.4792 15 16.7167 15 17C15 17.2833 15.0958 17.5208 15.2875 17.7125Z',
          fill: color || EColorPalette.cGreen3,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.DOLLAR_THIN: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 25 25')}
        ${this.getPath({
          d: 'M16.4598 9.46875L14.4598 9.46875C14.4598 8.38875 13.0898 7.46875 11.4598 7.46875C9.82984 7.46875 8.45984 8.38875 8.45984 9.46875C8.45984 10.5687 9.49984 10.9687 11.6998 11.4987C13.8198 12.0287 16.4598 12.6888 16.4598 15.4687C16.4598 17.2587 14.9598 18.9687 12.4598 19.2887L12.4598 21.4687L10.4598 21.4687L10.4598 19.2887C7.96997 19.2887 6.45984 17.2587 6.45984 15.4687L8.45984 15.4687C8.45984 16.5487 9.82984 17.4687 11.4598 17.4687C13.0898 17.4687 14.4598 16.5487 14.4598 15.4687C14.4598 14.3687 13.4198 13.9687 11.2198 13.4387C9.09984 12.9087 6.45984 12.2487 6.45984 9.46875C6.45984 7.67875 8.68011 5.64875 10.4598 5.64875L10.4598 3.46875L12.4598 3.46875L12.4598 5.64875C14.8848 5.96875 16.4598 7.67875 16.4598 9.46875Z',
          fill: color || EColorPalette.cGreen3,
        })}
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.SYNC: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 20 20')} 
        ${this.getPath({
          d: 'M3.3335 16.6693V15.0026H5.62516L5.29183 14.7109C4.56961 14.072 4.06266 13.3429 3.771 12.5234C3.47933 11.704 3.3335 10.8776 3.3335 10.0443C3.3335 8.5026 3.7953 7.13108 4.71891 5.92969C5.64252 4.7283 6.84738 3.93316 8.3335 3.54427V5.29427C7.3335 5.65538 6.52794 6.26997 5.91683 7.13802C5.30572 8.00608 5.00016 8.97483 5.00016 10.0443C5.00016 10.6693 5.11822 11.2769 5.35433 11.8672C5.59044 12.4575 5.9585 13.0026 6.4585 13.5026L6.66683 13.7109V11.6693H8.3335V16.6693H3.3335ZM11.6668 16.4609V14.7109C12.6668 14.3498 13.4724 13.7352 14.0835 12.8672C14.6946 11.9991 15.0002 11.0304 15.0002 9.96094C15.0002 9.33594 14.8821 8.7283 14.646 8.13802C14.4099 7.54774 14.0418 7.0026 13.5418 6.5026L13.3335 6.29427V8.33594H11.6668V3.33594H16.6668V5.0026H14.3752L14.7085 5.29427C15.3891 5.97483 15.8856 6.71441 16.1981 7.51302C16.5106 8.31163 16.6668 9.1276 16.6668 9.96094C16.6668 11.5026 16.205 12.8741 15.2814 14.0755C14.3578 15.2769 13.1529 16.072 11.6668 16.4609Z',
          fill: color || EColorPalette.cGray5,
        })}                              
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.BULK_MODE: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')} 
        ${this.getPath({
          d: 'M6 20C5.45 20 4.97917 19.8042 4.5875 19.4125C4.19583 19.0208 4 18.55 4 18C4 17.45 4.19583 16.9792 4.5875 16.5875C4.97917 16.1958 5.45 16 6 16C6.55 16 7.02083 16.1958 7.4125 16.5875C7.80417 16.9792 8 17.45 8 18C8 18.55 7.80417 19.0208 7.4125 19.4125C7.02083 19.8042 6.55 20 6 20ZM6 14C5.45 14 4.97917 13.8042 4.5875 13.4125C4.19583 13.0208 4 12.55 4 12C4 11.45 4.19583 10.9792 4.5875 10.5875C4.97917 10.1958 5.45 10 6 10C6.55 10 7.02083 10.1958 7.4125 10.5875C7.80417 10.9792 8 11.45 8 12C8 12.55 7.80417 13.0208 7.4125 13.4125C7.02083 13.8042 6.55 14 6 14ZM6 8C5.45 8 4.97917 7.80417 4.5875 7.4125C4.19583 7.02083 4 6.55 4 6C4 5.45 4.19583 4.97917 4.5875 4.5875C4.97917 4.19583 5.45 4 6 4C6.55 4 7.02083 4.19583 7.4125 4.5875C7.80417 4.97917 8 5.45 8 6C8 6.55 7.80417 7.02083 7.4125 7.4125C7.02083 7.80417 6.55 8 6 8ZM12 8C11.45 8 10.9792 7.80417 10.5875 7.4125C10.1958 7.02083 10 6.55 10 6C10 5.45 10.1958 4.97917 10.5875 4.5875C10.9792 4.19583 11.45 4 12 4C12.55 4 13.0208 4.19583 13.4125 4.5875C13.8042 4.97917 14 5.45 14 6C14 6.55 13.8042 7.02083 13.4125 7.4125C13.0208 7.80417 12.55 8 12 8ZM18 8C17.45 8 16.9792 7.80417 16.5875 7.4125C16.1958 7.02083 16 6.55 16 6C16 5.45 16.1958 4.97917 16.5875 4.5875C16.9792 4.19583 17.45 4 18 4C18.55 4 19.0208 4.19583 19.4125 4.5875C19.8042 4.97917 20 5.45 20 6C20 6.55 19.8042 7.02083 19.4125 7.4125C19.0208 7.80417 18.55 8 18 8ZM12 14C11.45 14 10.9792 13.8042 10.5875 13.4125C10.1958 13.0208 10 12.55 10 12C10 11.45 10.1958 10.9792 10.5875 10.5875C10.9792 10.1958 11.45 10 12 10C12.55 10 13.0208 10.1958 13.4125 10.5875C13.8042 10.9792 14 11.45 14 12C14 12.55 13.8042 13.0208 13.4125 13.4125C13.0208 13.8042 12.55 14 12 14ZM13 20V16.925L18.525 11.425C18.675 11.275 18.8417 11.1667 19.025 11.1C19.2083 11.0333 19.3917 11 19.575 11C19.775 11 19.9667 11.0375 20.15 11.1125C20.3333 11.1875 20.5 11.3 20.65 11.45L21.575 12.375C21.7083 12.525 21.8125 12.6917 21.8875 12.875C21.9625 13.0583 22 13.2417 22 13.425C22 13.6083 21.9667 13.7958 21.9 13.9875C21.8333 14.1792 21.725 14.35 21.575 14.5L16.075 20H13ZM19.575 14.4L20.5 13.425L19.575 12.5L18.625 13.45L19.575 14.4Z',
          fill: color || EColorPalette.cGray7,
        })}                              
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.DOUBLE_CHECK: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 12 12')} 
        ${this.getPath({
          d: 'M3.35039 9.00078L0.525391 6.17578L1.23789 5.47578L3.36289 7.60078L4.06289 8.30078L3.35039 9.00078ZM6.17539 9.00078L3.35039 6.17578L4.05039 5.46328L6.17539 7.58828L10.7754 2.98828L11.4754 3.70078L6.17539 9.00078ZM6.17539 6.17578L5.46289 5.47578L7.93789 3.00078L8.65039 3.70078L6.17539 6.17578Z',
          fill: color || EColorPalette.cGray7,
        })}                              
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.CARET: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 18 18')} 
        ${this.getPath({
          d: 'M6.75 13.1906L6.75 4.80564C6.75062 4.65731 6.79521 4.5125 6.87813 4.38951C6.96106 4.26653 7.07859 4.17089 7.21586 4.11469C7.35313 4.0585 7.50399 4.04427 7.64935 4.07381C7.79471 4.10335 7.92804 4.17533 8.0325 4.28064L12.2175 8.46564C12.2878 8.53537 12.3436 8.61832 12.3817 8.70971C12.4197 8.80111 12.4393 8.89914 12.4393 8.99814C12.4393 9.09715 12.4197 9.19518 12.3817 9.28658C12.3436 9.37797 12.2878 9.46092 12.2175 9.53064L8.0325 13.7156C7.92804 13.821 7.79471 13.8929 7.64935 13.9225C7.50399 13.952 7.35313 13.9378 7.21586 13.8816C7.07859 13.8254 6.96106 13.7298 6.87813 13.6068C6.79521 13.4838 6.75062 13.339 6.75 13.1906Z',
          fill: color || EColorPalette.cGray7,
        })}                              
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.CAMPAIGN: {
        return `
        ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')} 
        ${this.getPath({
          d: 'M8.46124 19V14.6923H7.38432C6.79201 14.6923 6.28496 14.4814 5.86316 14.0596C5.44137 13.6378 5.23047 13.1308 5.23047 12.5385V10.3846C5.23047 9.79231 5.44137 9.28526 5.86316 8.86346C6.28496 8.44167 6.79201 8.23077 7.38432 8.23077H11.692L17.0766 5V17.9231L11.692 14.6923H10.6151V19H8.46124ZM18.1535 15.0692V7.85385C18.6382 8.28462 19.0285 8.80962 19.3247 9.42885C19.6209 10.0481 19.7689 10.7256 19.7689 11.4615C19.7689 12.1974 19.6209 12.875 19.3247 13.4942C19.0285 14.1135 18.6382 14.6385 18.1535 15.0692Z',
          fill: color || EColorPalette.cGray7,
        })}                              
        ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.HISTORY: {
        return `
          ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 18 19')}
          ${this.getPath({
            d: 'M1.58325 2.16406V7.83158V7.805V16.8307V2.16406ZM3.64575 11.1016H6.82934C6.96225 10.8441 7.11152 10.6008 7.27713 10.3717C7.44289 10.1425 7.62508 9.92746 7.82369 9.72656H3.64575V11.1016ZM3.64575 14.7682H6.1824C6.14833 14.5391 6.13129 14.3113 6.13129 14.0851C6.13129 13.859 6.14543 13.6284 6.17369 13.3932H3.64575V14.7682ZM1.86536 18.2057C1.40229 18.2057 1.01034 18.0453 0.689502 17.7245C0.368669 17.4036 0.208252 17.0117 0.208252 16.5486V2.44617C0.208252 1.9831 0.368669 1.59115 0.689502 1.27031C1.01034 0.949479 1.40229 0.789062 1.86536 0.789062H9.14575L13.9583 5.60156V8.05365C13.7326 7.98551 13.5066 7.93295 13.2804 7.89598C13.0541 7.85885 12.8217 7.83739 12.5833 7.83158V6.28906H8.45825V2.16406H1.86536C1.79477 2.16406 1.73015 2.19347 1.67148 2.25229C1.61266 2.31096 1.58325 2.37558 1.58325 2.44617V16.5486C1.58325 16.6192 1.61266 16.6838 1.67148 16.7425C1.73015 16.8013 1.79477 16.8307 1.86536 16.8307H6.86463C6.99739 17.094 7.14666 17.3402 7.31242 17.5693C7.47818 17.7985 7.66327 18.0106 7.86769 18.2057H1.86536Z',
            fill: color || EColorPalette.cGray5,
          })}
          ${this.getPath({
            d: 'M14.0352 16.2578L14.6768 15.6161L12.9581 13.8974V11.3307H12.0414V14.2641L14.0352 16.2578ZM12.4998 18.6641C11.2317 18.6641 10.1508 18.2172 9.25705 17.3234C8.3633 16.4297 7.91642 15.3488 7.91642 14.0807C7.91642 12.8127 8.3633 11.7318 9.25705 10.838C10.1508 9.94427 11.2317 9.4974 12.4998 9.4974C13.7678 9.4974 14.8487 9.94427 15.7425 10.838C16.6362 11.7318 17.0831 12.8127 17.0831 14.0807C17.0831 15.3488 16.6362 16.4297 15.7425 17.3234C14.8487 18.2172 13.7678 18.6641 12.4998 18.6641Z',
            fill: color || EColorPalette.cGray5,
          })}
          ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.DOT_ZERO: {
        return `
          ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 10 10')}
          ${this.getPath({
            d: 'M0 10V7.27273H2.72727V10H0ZM6.81818 10C5.93939 10 5.18939 9.68939 4.56818 9.06818C3.94697 8.44697 3.63636 7.69697 3.63636 6.81818V3.18182C3.63636 2.30303 3.94697 1.55303 4.56818 0.931818C5.18939 0.310606 5.93939 0 6.81818 0C7.69697 0 8.44697 0.310606 9.06818 0.931818C9.68939 1.55303 10 2.30303 10 3.18182V6.81818C10 7.69697 9.68939 8.44697 9.06818 9.06818C8.44697 9.68939 7.69697 10 6.81818 10ZM6.81818 8.18182C7.19697 8.18182 7.51894 8.04924 7.78409 7.78409C8.04924 7.51894 8.18182 7.19697 8.18182 6.81818V3.18182C8.18182 2.80303 8.04924 2.48106 7.78409 2.21591C7.51894 1.95076 7.19697 1.81818 6.81818 1.81818C6.43939 1.81818 6.11742 1.95076 5.85227 2.21591C5.58712 2.48106 5.45455 2.80303 5.45455 3.18182V6.81818C5.45455 7.19697 5.58712 7.51894 5.85227 7.78409C6.11742 8.04924 6.43939 8.18182 6.81818 8.18182Z',
            fill: color || EColorPalette.cGreen,
          })}
          ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.INFO_USER: {
        return `
          ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
          ${this.getCircle({
            cx: 12,
            cy: 12,
            r: 10,
            fill: color || EColorPalette.cGreen,
          })}
          ${this.getPath({
            d: 'M14.0825 16.7296H13.7589L13.7618 11.2737C13.7519 10.9013 13.3457 10.6016 12.8452 10.6016H9.91752C9.41076 10.6016 9 10.9087 9 11.2875C9 11.6664 9.41076 11.9735 9.91752 11.9735H10.4201V16.7296H10.0965C9.58974 16.7296 9.17898 17.0367 9.17898 17.4155C9.17898 17.7945 9.58974 18.1016 10.0965 18.1016H14.0825C14.5892 18.1016 15 17.7945 15 17.4155C15 17.0367 14.5892 16.7296 14.0825 16.7296Z',
            fill: secondColor || EColorPalette.cWhite,
          })}
          ${this.getPath({
            d: 'M11.9 9.19844C12.9493 9.19844 13.8 8.34778 13.8 7.29844C13.8 6.2491 12.9493 5.39844 11.9 5.39844C10.8507 5.39844 10 6.2491 10 7.29844C10 8.34778 10.8507 9.19844 11.9 9.19844Z',
            fill: secondColor || EColorPalette.cWhite,
          })}
          ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.CARET_DOWN: {
        return `
          ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 12 13')}
          ${this.getPath({
            d: 'M3.20493 5H8.79494C8.89382 5.00041 8.99036 5.03014 9.07236 5.08542C9.15435 5.1407 9.21811 5.21906 9.25557 5.31057C9.29303 5.40209 9.30252 5.50266 9.28282 5.59957C9.26313 5.69647 9.21515 5.78536 9.14494 5.855L6.35494 8.64499C6.30845 8.69186 6.25315 8.72905 6.19222 8.75444C6.13129 8.77982 6.06594 8.79289 5.99994 8.79289C5.93393 8.79289 5.86858 8.77982 5.80765 8.75444C5.74672 8.72905 5.69142 8.69186 5.64494 8.64499L2.85494 5.855C2.78472 5.78536 2.73674 5.69647 2.71705 5.59957C2.69735 5.50266 2.70684 5.40209 2.7443 5.31057C2.78176 5.21906 2.84552 5.1407 2.92751 5.08542C3.00951 5.03014 3.10605 5.00041 3.20493 5Z',
            fill: color || EColorPalette.cGray,
          })}
          ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.CARET_UP: {
        return `
          ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 12 13')}
          ${this.getPath({
            d: 'M8.79507 8L3.20506 8C3.10618 7.99959 3.00964 7.96986 2.92764 7.91458C2.84565 7.8593 2.78189 7.78094 2.74443 7.68943C2.70697 7.59791 2.69748 7.49734 2.71718 7.40043C2.73687 7.30353 2.78485 7.21464 2.85506 7.145L5.64506 4.35501C5.69155 4.30814 5.74685 4.27095 5.80778 4.24556C5.86871 4.22018 5.93406 4.20711 6.00006 4.20711C6.06607 4.20711 6.13142 4.22018 6.19235 4.24556C6.25328 4.27095 6.30858 4.30814 6.35506 4.35501L9.14506 7.145C9.21528 7.21464 9.26326 7.30353 9.28295 7.40043C9.30265 7.49734 9.29316 7.59791 9.2557 7.68943C9.21824 7.78094 9.15448 7.8593 9.07249 7.91458C8.99049 7.96986 8.89395 7.99959 8.79507 8Z',
            fill: color || EColorPalette.cGray,
          })}
          ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.REQUIRE_PHOTO: {
        return `
          ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 20 20')}
          <mask id="mask0_${
            this.uniqueId
          }" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="20" height="20">
            <rect width="20" height="20" fill="#D9D9D9"/>
          </mask>
          <g mask="url(#mask0_${this.uniqueId})">
            ${this.getPath({
              d: 'M10 14.5C11 14.5 11.8333 14.1597 12.5 13.4792C13.1667 12.7986 13.5 11.9722 13.5 11C13.5 10.0417 13.1667 9.21875 12.5 8.53125C11.8333 7.84375 11 7.5 10 7.5C9 7.5 8.16667 7.84375 7.5 8.53125C6.83333 9.21875 6.5 10.0417 6.5 11C6.5 11.9722 6.83333 12.7986 7.5 13.4792C8.16667 14.1597 9 14.5 10 14.5ZM10 13C9.41667 13 8.9375 12.8125 8.5625 12.4375C8.1875 12.0625 8 11.5903 8 11.0208C8 10.4653 8.1875 9.98958 8.5625 9.59375C8.9375 9.19792 9.41667 9 10 9C10.5833 9 11.0625 9.19792 11.4375 9.59375C11.8125 9.98958 12 10.4653 12 11.0208C12 11.5903 11.8125 12.0625 11.4375 12.4375C11.0625 12.8125 10.5833 13 10 13ZM3.5 17C3.09722 17 2.74653 16.8507 2.44792 16.5521C2.14931 16.2535 2 15.9028 2 15.5V6.5C2 6.09722 2.14931 5.74653 2.44792 5.44792C2.74653 5.14931 3.09722 5 3.5 5H6L7.5 3H12.5L14 5H16.5C16.9167 5 17.2708 5.14931 17.5625 5.44792C17.8542 5.74653 18 6.09722 18 6.5V15.5C18 15.9028 17.8542 16.2535 17.5625 16.5521C17.2708 16.8507 16.9167 17 16.5 17H3.5Z',
              fill: color || EColorPalette.cGray5,
            })}
          </g>
          ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.REQUIRE_COMMENT: {
        return `
          ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 20 20')}
          <mask id="mask1_${
            this.uniqueId
          }" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="20" height="20">
            <rect width="20" height="20" fill="#D9D9D9"/>
          </mask>
          <g mask="url(#mask1_${this.uniqueId})">
            ${this.getPath({
              d: 'M15.8111 14.8111C17.1679 13.5567 18 11.8634 18 10C18 6.13401 14.4183 3 10 3C5.58172 3 2 6.13401 2 10C2 13.866 5.58172 17 10 17H18L15.8111 14.8111ZM6 11H12V12H6V11ZM14 8H6V9H14V8Z',
              fill: color || EColorPalette.cGray5,
            })}
          </g>
          ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.ADD_RATING_GROUP: {
        return `
          ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 20 20')}
          <mask id="mask2_${
            this.uniqueId
          }" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="20" height="20">
            <rect width="20" height="20" fill="#D9D9D9"/>
          </mask>
          <g mask="url(#mask2_${this.uniqueId})">
            ${this.getPath({
              d: 'M8.83333 2.6205L6.54167 5.76731L3.04167 6.98615C2.71339 7.10378 2.45771 7.30308 2.27463 7.58407C2.09154 7.86505 2 8.17524 2 8.51464C2 8.67755 2.02083 8.83287 2.0625 8.98061C2.10417 9.12835 2.17361 9.27608 2.27083 9.42382L4.52083 12.7701L4.4375 16.3601C4.42361 16.8181 4.56765 17.2059 4.8696 17.5235C5.17156 17.8412 5.52385 18 5.92648 18C6.05208 17.9963 6.17708 17.9815 6.31138 17.9344L10 16.8476L13.6875 17.9335C13.9236 17.9778 14.0543 18 14.0797 18C14.4849 18 14.8333 17.8412 15.125 17.5235C15.4167 17.2059 15.5625 16.8181 15.5625 16.3601L15.4792 12.7701L17.7292 9.42382C17.8255 9.2786 17.8947 9.12677 17.9368 8.96835C17.9789 8.80992 18 8.65149 18 8.49307C18 8.15905 17.9097 7.85395 17.7292 7.57775C17.5486 7.30154 17.2986 7.10434 16.9792 6.98615L13.4792 5.76731L11.1875 2.6205C11.0347 2.41367 10.8576 2.25854 10.6562 2.15512C10.4549 2.05171 10.2415 2 10.0162 2C9.7909 2 9.57498 2.05004 9.36844 2.15012C9.16191 2.25019 8.98354 2.40699 8.83333 2.6205ZM11.6667 12.9375C11.1806 13.3125 10.6256 13.5 10.0018 13.5C9.37804 13.5 8.82249 13.3125 8.33517 12.9375C7.84783 12.5625 7.48611 12.0833 7.25 11.5H12.75C12.5139 12.0833 12.1528 12.5625 11.6667 12.9375ZM8.70833 10.2083C8.51389 10.4028 8.27778 10.5 8 10.5C7.72222 10.5 7.48611 10.4028 7.29167 10.2083C7.09722 10.0139 7 9.77778 7 9.5C7 9.22222 7.09722 8.98611 7.29167 8.79167C7.48611 8.59722 7.72222 8.5 8 8.5C8.27778 8.5 8.51389 8.59722 8.70833 8.79167C8.90278 8.98611 9 9.22222 9 9.5C9 9.77778 8.90278 10.0139 8.70833 10.2083ZM12.7083 10.2083C12.5139 10.4028 12.2778 10.5 12 10.5C11.7222 10.5 11.4861 10.4028 11.2917 10.2083C11.0972 10.0139 11 9.77778 11 9.5C11 9.22222 11.0972 8.98611 11.2917 8.79167C11.4861 8.59722 11.7222 8.5 12 8.5C12.2778 8.5 12.5139 8.59722 12.7083 8.79167C12.9028 8.98611 13 9.22222 13 9.5C13 9.77778 12.9028 10.0139 12.7083 10.2083Z',
              fill: color || EColorPalette.cGray5,
            })}
          </g>
          ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.ADD_QUESTION: {
        return `
          ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 20 20')}
          <mask id="mask3_${
            this.uniqueId
          }" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="20" height="20">
            <rect width="20" height="20" fill="#D9D9D9"/>
          </mask>
          <g mask="url(#mask3_${this.uniqueId})">
            ${this.getPath({
              d: 'M18 10C18 11.8634 17.1679 13.5567 15.8111 14.8111L18 17H10C5.58172 17 2 13.866 2 10C2 6.13401 5.58172 3 10 3C14.4183 3 18 6.13401 18 10ZM9.39584 11.4583H10.6042C10.6042 11.0972 10.632 10.8229 10.6875 10.6354C10.7431 10.4479 10.9028 10.2222 11.1667 9.95833C11.4167 9.72222 11.6667 9.47222 11.9167 9.20833C12.1667 8.94444 12.2917 8.56944 12.2917 8.08333C12.2917 7.41667 12.0695 6.90278 11.625 6.54167C11.1806 6.18056 10.6389 6 10 6C9.41827 6 8.92909 6.16667 8.53247 6.5C8.13583 6.83333 7.86112 7.22222 7.70834 7.66667L8.79851 8.125C8.8634 7.88889 8.98953 7.66667 9.17691 7.45833C9.36428 7.25 9.63865 7.14583 10 7.14583C10.375 7.14583 10.6493 7.25 10.8229 7.45833C10.9965 7.66667 11.0833 7.88889 11.0833 8.125C11.0833 8.41667 10.9826 8.66319 10.7813 8.86458C10.5799 9.06597 10.3611 9.27778 10.125 9.5C9.7639 9.83333 9.55209 10.1215 9.48959 10.3646C9.42709 10.6076 9.39584 10.9722 9.39584 11.4583ZM10 14C10.2304 14 10.4268 13.9187 10.5894 13.7561C10.752 13.5935 10.8333 13.397 10.8333 13.1667C10.8333 12.9363 10.752 12.7398 10.5894 12.5772C10.4268 12.4146 10.2304 12.3333 10 12.3333C9.76966 12.3333 9.57318 12.4146 9.41057 12.5772C9.24798 12.7398 9.16668 12.9363 9.16668 13.1667C9.16668 13.397 9.24798 13.5935 9.41057 13.7561C9.57318 13.9187 9.76966 14 10 14Z',
              fill: color || EColorPalette.cGray5,
            })}
          </g>
          ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.RADIO_BUTTON: {
        return `
          ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 -960 960 960')}
          ${this.getPath({
            d: 'M480-280q83 0 141.5-58.5T680-480q0-83-58.5-141.5T480-680q-83 0-141.5 58.5T280-480q0 83 58.5 141.5T480-280Zm0 200q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Z',
            fill: color || EColorPalette.cGray,
          })}
          ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.MAT_DELETE_FOREVER: {
        return `
          ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 24 24')}
            ${this.getPath({
              d: 'M9.4 16.5L12 13.9L14.6 16.5L16 15.1L13.4 12.5L16 9.9L14.6 8.5L12 11.1L9.4 8.5L8 9.9L10.6 12.5L8 15.1L9.4 16.5ZM7 21C6.45 21 5.97917 20.8042 5.5875 20.4125C5.19583 20.0208 5 19.55 5 19V6H4V4H9V3H15V4H20V6H19V19C19 19.55 18.8042 20.0208 18.4125 20.4125C18.0208 20.8042 17.55 21 17 21H7Z',
              fill: color || EColorPalette.cGray5,
            })}
          ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.RESTORE_FROM_TRASH: {
        return `
          ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 20 20')}
            ${this.getPath({
              d: 'M9.16732 13.3333H10.834V9.875L12.1673 11.1667L13.334 10L10.0007 6.66667L6.66732 10L7.83398 11.1667L9.16732 9.875V13.3333ZM5.83398 17.5C5.37565 17.5 4.98329 17.3368 4.6569 17.0104C4.33051 16.684 4.16732 16.2917 4.16732 15.8333V5H3.33398V3.33333H7.50065V2.5H12.5007V3.33333H16.6673V5H15.834V15.8333C15.834 16.2917 15.6708 16.684 15.3444 17.0104C15.018 17.3368 14.6257 17.5 14.1673 17.5H5.83398Z',
              fill: color || EColorPalette.cGray5,
            })}
          ${this.getSvgEndTemplate()}
        `;
      }

      case EIcon.CLOUD_UPLOAD: {
        return `
          ${this.getSvgBeginTemplate(this.uniqueId, width, height, '0 0 40 40')}
          ${this.getPath({
            d: 'M18.3327 33.3332H10.8327C8.3049 33.3332 6.14518 32.4582 4.35352 30.7082C2.56185 28.9582 1.66602 26.8193 1.66602 24.2915C1.66602 22.1248 2.31879 20.1943 3.62435 18.4998C4.9299 16.8054 6.63824 15.7221 8.74935 15.2498C9.44379 12.6943 10.8327 10.6248 12.916 9.0415C14.9993 7.45817 17.3605 6.6665 19.9993 6.6665C23.2493 6.6665 26.0063 7.79845 28.2702 10.0623C30.5341 12.3262 31.666 15.0832 31.666 18.3332C33.5827 18.5554 35.173 19.3818 36.4369 20.8123C37.7007 22.2429 38.3327 23.9165 38.3327 25.8332C38.3327 27.9165 37.6035 29.6873 36.1452 31.1457C34.6869 32.604 32.916 33.3332 30.8327 33.3332H21.666V21.4165L24.3327 23.9998L26.666 21.6665L19.9993 14.9998L13.3327 21.6665L15.666 23.9998L18.3327 21.4165V33.3332Z',
            fill: color || EColorPalette.cOrange,
          })}
          ${this.getSvgEndTemplate()}
        `;
      }
    }
    return undefined;
  }

  private getSvgBeginTemplate(
    uniqueId: string,
    width: number,
    height: number,
    viewBox: string,
    color = 'none'
  ): string {
    return `<svg class="cls-${uniqueId}" width="${width}" height="${height}" viewBox="${viewBox}" fill="${color}" xmlns="http://www.w3.org/2000/svg">`;
  }

  private getSvgEndTemplate(): string {
    return `</svg>`;
  }

  private getPath(params: ISvgPath): string {
    return `<path class="svg-content" fill-rule="evenodd" clip-rule="evenodd" d="${params.d}"
    ${this.getColorFill(params.fill)} opacity="${params?.opacity || 1}"
    ${this.getColorStroke(params.stroke)}
    ${this.getStrokeWidth(params.strokeWidth)}/>`;
  }

  private getRectangle(params: ISvgRectangle): string {
    return `<rect class="svg-content" ${this.getX(params.x)} ${this.getY(params.y)} width="${params.width}" height="${
      params.height
    }" transform="${params?.transform || ''}" fill="${params.fill}" rx="${params?.rx || 0}" ${this.getColorStroke(
      params?.stroke
    )} opacity="${params.opacity || 1}"/>`;
  }

  private getCircle(params: ISvgCircle): string {
    return `<circle cx="${params.cx}" cy="${params.cy}" r="${params.r}" ${this.getColorStroke(
      params.stroke
    )} ${this.getStrokeWidth(params.strokeWidth)} ${this.getColorFill(params.fill)} opacity="${
      params?.opacity || 1
    }"/>`;
  }

  private getX(x: number): string {
    return isNil(x) ? '' : `x="${x}"`;
  }

  private getY(y: number): string {
    return isNil(y) ? '' : `y="${y}"`;
  }

  private getColorFill(color: string): string {
    return color ? `fill="${color}"` : '';
  }

  private getColorStroke(color: string): string {
    return color ? `stroke="${color}"` : '';
  }

  private getStrokeWidth(value: number): string {
    return value ? `stroke-width="${value}"` : '';
  }

  private getUrlClip() {
    return `<g clip-path="url(#clip0)">`;
  }

  private getOpenUrlClip(opacity) {
    return `<g opacity="${opacity}">`;
  }

  private getEndUrlClip() {
    return `</g>`;
  }

  private getDefsPart() {
    return `<defs>`;
  }

  private endDefsPart() {
    return `</defs>`;
  }

  private getClipPath() {
    return `<clipPath id="clip0">`;
  }

  private endClipPath() {
    return `</clipPath>`;
  }

  private getClipPathRect() {
    return `<rect width="27.75" height="27" fill="white" transform="translate(4.625 4.5)"/>`;
  }
}
